import axios, { type AxiosInstance } from 'axios';
import { PublicClientApplication, type AccountInfo } from '@azure/msal-browser';
import { apiScopes } from './msalConfig';
import { ApiError, SESSION_EXPIRED_MESSAGE, isNotFoundError, toApiError } from './httpErrors';
import { fileNameFromDisposition } from './contentDisposition';

let msalInstance: PublicClientApplication | null = null;
let activeAccount: AccountInfo | null = null;

export function setMsalInstance(instance: PublicClientApplication) {
  msalInstance = instance;
}

export function setActiveAccount(account: AccountInfo | null) {
  activeAccount = account;
}

const api: AxiosInstance = axios.create({
  baseURL: (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api/v1',
  headers: { 'Content-Type': 'application/json' },
});

/**
 * A refused download's error, with the server's own sentence.
 *
 * ⚠️ THE ERROR IS READ OUT OF THE BLOB. With `responseType: 'blob'` a refusal's JSON body arrives as a Blob too, and
 * `toApiError` then finds no `message` and says "Request failed with status code 409", losing the one sentence that
 * says WHY. `fallback` is used when the body is not JSON (a gateway's HTML page, say). Shared by every blob download
 * that reads its own refusals (#645 made it the fourth).
 */
async function blobRefusal(r: { status: number; data: unknown }, fallback: string): Promise<ApiError> {
  let message = fallback;
  try {
    const body = JSON.parse(await (r.data as Blob).text());
    const m = body?.message ?? body?.error;
    if (m) message = Array.isArray(m) ? m.join(', ') : String(m);
  } catch { /* not JSON: keep the fallback */ }
  return new ApiError(message, r.status);
}

// ─── Admin "View as" impersonation ──────────────────────────────────────────
// The impersonated user id is kept in localStorage so it survives the reload we
// do on enter/exit (which clears all cached queries cleanly). Only meaningful
// for Admins — the API ignores the header for everyone else.
const IMPERSONATE_KEY = 'pmt.impersonateUserId';
const IMPERSONATE_NAME_KEY = 'pmt.impersonateName';
export const impersonation = {
  activeId: () => localStorage.getItem(IMPERSONATE_KEY),
  activeName: () => localStorage.getItem(IMPERSONATE_NAME_KEY),
  start: (userId: string, name: string | null) => {
    localStorage.setItem(IMPERSONATE_KEY, userId);
    localStorage.setItem(IMPERSONATE_NAME_KEY, name ?? 'user');
    window.location.assign('/'); // full reload → fresh me() + no stale admin-context cache
  },
  stop: () => {
    localStorage.removeItem(IMPERSONATE_KEY);
    localStorage.removeItem(IMPERSONATE_NAME_KEY);
    window.location.assign('/');
  },
};

// Intercept every request and attach the Bearer token
api.interceptors.request.use(async (config) => {
  if (!msalInstance || !activeAccount) return config;

  try {
    const result = await msalInstance.acquireTokenSilent({
      scopes: apiScopes,
      account: activeAccount,
    });
    config.headers.Authorization = `Bearer ${result.accessToken}`;
    // Admin "View as" — attach the impersonated user id. The API honors it ONLY
    // if this token belongs to an Admin (a non-admin gets their own view), and
    // forces the request read-only.
    const impersonateId = localStorage.getItem(IMPERSONATE_KEY);
    if (impersonateId) config.headers['X-Impersonate-User'] = impersonateId;
  } catch {
    // Token acquisition failed for a signed-in account. Do NOT send the
    // request unauthenticated — that's a guaranteed 401 with a message
    // ("Unauthorized") that tells the user nothing. Fail it here with
    // actionable guidance instead. (Root cause of the "notes intermittently
    // don't save" report: expired sessions were silently firing token-less
    // writes that could never succeed.)
    console.warn('apiClient: silent token acquisition failed');
    throw new Error(SESSION_EXPIRED_MESSAGE);
  }

  return config;
});

// Surface error messages from the API clearly.
//
// ⚠️ This REPLACES the Axios error, so `err.response` does not survive to any
// caller — which silently killed every `err.response.status` check downstream.
// `toApiError` carries the status onto the replacement so status-aware handling
// works; use the predicates in httpErrors rather than reaching for `.response`.
api.interceptors.response.use(
  (res) => res,
  (err) => Promise.reject(toApiError(err)),
);

export default api;

// ─── Typed resource helpers ────────────────────────────────────────────────────

// One row in a Projects CSV import. All fields are optional (except jobNumber,
// which is the lookup key). String fields are sent raw — the API resolves
// statuses, divisions, user emails, and checklist values server-side.
export interface ImportProjectRow {
  jobNumber: string;
  title?: string;
  division?: string;
  client?: string;
  projectStatus?: string;
  mobilizationStatus?: string;
  primaryPmEmail?: string;
  secondaryPmEmail?: string;
  estimatorEmail?: string;
  safetyManagerEmail?: string;
  originalStartDate?: string;
  currentStartDate?: string;
  estimatedCompletion?: string;
  estimatorHandoffDate?: string;
  fieldLeaderHandoffDate?: string;
  originalDuration?: string;
  currentDuration?: string;
  bidPeakCrew?: string;
  bidWeeklyHours?: string;
  isArchived?: string;
  travelJob?: string;
  perDiem?: string;
  perDiemDays?: string;
  jobAddress?: string;
  /** 'unverified' | 'verified' | 'unresolvable' — see job-address.ts on the API. */
  jobAddressStatus?: JobAddressStatus;
  jobCity?: string | null;
  jobState?: string | null;
  jobPostalCode?: string | null;
  jobLatitude?: number | null;
  jobLongitude?: number | null;
  siteContactName?: string;
  siteContactPhone?: string;
  siteContactEmail?: string;
  clinicName?: string;
  clinicAddress?: string;
  clinicPhone?: string;
  delayNotes?: string;
  estimatorNotes?: string;
  pmNotes?: string;
  /**
   * ⚠️ THE CSV IMPORT HAS NO `travelBand`, ON PURPOSE — do not add one here.
   *
   * Advertising it on this interface would be a lie in three places at once: the
   * server's `ImportProjectRowDto` has no such property and
   * `forbidNonWhitelisted` rejects the request, `ProjectsImportModal.COLUMN_MAP`
   * has no header for it, and neither import write path stores it. The import is
   * a retired path, so it keeps writing this legacy column and nothing more.
   *
   * (Codex, #504 — I had added `travelBand` to this interface by mistake. It
   * belongs on the project shape, which lives in `OverviewEdit`, not on an
   * import row.)
   */
  travelDistanceNote?: string;
  jobFolderUrl?: string;
  /** Map of checklist_item.code → 'YES' | 'NO' | 'N/A' | 'BLOCKED'. */
  checklist?: Record<string, string>;
}

export interface ImportProjectsResult {
  updated:  number;
  created:  number;
  skipped:  number;
  warnings: string[];
}

// Per-tab open-item counts for the project workspace tab badges.
// Each is the number of open / in-process / active items for the job.
export interface ProjectCounts {
  rfis:         number;  // RFIs with status 'open'
  submittals:   number;  // submittals in-process (queued / in-review / resubmit)
  equipment:    number;  // equipment currently on the job (status code 'on_site')
  receiving:    number;  // open receiving orders (Active phase)
  shipping:     number;  // open shipping requests (Active phase)
  materials:    number;  // material lines not yet installed
  qaqc:         number;  // pour cards awaiting office confirmation
  changes:      number;  // open changes + approved not yet entered in Intacct
  mobilization: number;  // mobilizations not yet finished (no end_date)
  punch:        number;  // punch items not yet closed
}

// Suggested same-day weather for a project's site (free NWS forecast). All
// fields optional; available:false when there's no site address or NWS is down.
export interface WeatherSuggestion {
  available: boolean;
  reason?: string;
  source?: string;
  asOf?: string;
  cond?: string | null;
  hi?: number | null;
  lo?: number | null;
  precip?: string | null;
  windSpeed?: string | null;
}

// Actual daily manpower for a project, read from Paycor hours.
export interface ManpowerActuals {
  available: boolean;
  jobNumber: string;
  /**
   * The actual start Paycor supports for this job, or null (#380).
   *
   * ⚠️ NOT derivable from `daily` below. That array filters on job number alone —
   * no `is_field = 1`, no exclusion list — because it answers "hours booked to
   * this job", not "did field work begin". This field is computed server-side by
   * the same definition the nightly derivation uses, and is what the Schedule &
   * Dates card falls back to for a job with no stored phase row.
   */
  derivedActualStart: string | null;
  summary: {
    totalHours: number; regHours: number; otHours: number; dtHours: number;
    workers: number; daysWorked: number; lastWorkDate: string | null;
    peakCrew: number; avgRecentCrew: number;
  } | null;
  daily: { workDate: string; crew: number; hours: number; regHours: number; otHours: number; dtHours: number }[];
  // Job-to-date actual hours split by phase via the cost_code → phase map.
  // 'unmapped' collects hours whose cost_code isn't mapped yet (or all hours
  // when phase_cost_codes is empty). Absent on older responses.
  byPhase?: { phase: string; hours: number }[];
}

// One per-phase schedule row (GET /projects/:id carries these as `phases`;
// PATCH /projects/:id/phases returns the refreshed list). Dates are ISO.
export interface ProjectPhaseDto {
  phase: 'fabrication' | 'installation' | 'project';
  originalStart: string | null;
  /** The start currently being worked to — not the baseline, not an actual (#380). */
  plannedStart: string | null;
  originalEnd: string | null;
  actualStart: string | null;
  /** 'paycor' when the nightly derivation owns `actualStart`; null when a human does. */
  actualStartSource: 'paycor' | 'manual' | null;
  actualEnd: string | null;
  isProjectedEnd: boolean;
  completionPct: number | null;
  budgetLaborHours: number | null;
  updatedAt?: string | null;
  updatedById?: string | null;
}

// Projected (estimated) hours for a project, shown on the Manpower tab.
// Effective = override when the estimator pinned one, else the Intacct baseline
// (billable + non-billable). null effective = no estimate synced yet.
/**
 * A job's estimated labor hours. `effective` is ONLY the estimator's typed
 * figure — the old autocalculated Intacct number is `reference`, shown as an
 * unverified starting point and never used as the value (it ran low, which is
 * why the weekly sync was removed on 2026-08-04).
 */
export interface EstimatedHours {
  /** The planning figure: the typed override, else the derived fallback. */
  effective:         number | null;
  source:            'estimator' | 'derived' | null;
  /** Labor budget ÷ the company $/MH rate — the pre-loaded fallback. Null when
   *  the job has no positive synced labor budget, and null for field-leader
   *  viewers (server-side redaction). `pendingChangeHours` = the slice from
   *  not-yet-approved change scope, disclosed when > 0. `estimateEnabled` false
   *  = the figure reflects the LAST sync (`estimateSyncedAt`), not the current
   *  estimate. Detail payloads only. */
  derived:           {
    hours: number;
    rate: number;
    rateSource: 'config' | 'default';
    pendingChangeHours: number;
    estimateEnabled: boolean;
    estimateSyncedAt: string | null;
  } | null;
  reference:         number | null;
  billable:          number | null;
  nonBillable:       number | null;
  override:          number | null;
  referenceSyncedAt: string | null;
  enteredAt:         string | null;
  enteredById:       string | null;
  enteredByName:     string | null;
  /** Whether this job should be flagged for missing hours. */
  state:             'set' | 'missing' | 'exempt';
}

export type JobAddressStatus = 'unverified' | 'suggested' | 'verified' | 'unresolvable';

/** One candidate from the address picker's search. */
export interface AddressCandidate {
  formatted: string;
  line1: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  latitude: number;
  longitude: number;
  /** 'Address' is street level; anything else is coarser and marked as such. */
  type: string;
  /** Azure's match codes. Forwarded on save so the server applies the same rule. */
  matchCodes: string[];
  /**
   * Whether this may be saved as a verified job site — decided by the API.
   *
   * ⚠️ NOT the same as `type === 'Address'`. Azure also returns `type: 'Address'`
   * with `matchCodes: ['UpHierarchy']` and no street line, which is a postcode or
   * locality centroid wearing the right label. Branch on this, not on the type.
   * (Codex, #479.)
   */
  selectable: boolean;
}

/**
 * What the PATCH actually stores.
 *
 * ⚠️ NOT `Omit<AddressCandidate, …>`. A candidate's fields are nullable
 * because that is what the geocoder may return; the payload's are required
 * because the API validates them as such, and `postalCode` is OPTIONAL rather
 * than nullable — not every US address has one on file. Deriving this type
 * from the candidate made `postalCode: string | null` and refused the
 * `undefined` that "no ZIP" actually means.
 */
export interface SetAddressPayload {
  line1: string;
  city: string;
  /** USPS two-letter code. */
  state: string;
  postalCode?: string;
  latitude: number;
  longitude: number;
  /** Azure's result type. The API refuses anything but a street-level 'Address'. */
  type: string;
  /**
   * Azure's match codes for the chosen candidate. REQUIRED: 'Address' alone is
   * not street level — Azure labels a postcode centroid 'Address' too, flagged
   * only here as 'UpHierarchy'. The API fails closed without it. (Codex, #479.)
   */
  matchCodes: string[];
  /**
   * Sent ONLY by the admin worklist: the row state it displayed. The API refuses
   * with 409 if the job has since been changed or verified by somebody else.
   *
   * ⚠️ The project-page picker must NOT send it — its user is looking at current
   * state, so last-write-wins is right there.
   */
  expect?: { storedAddress: string | null };
}

/**
 * One job still needing a verified address (Admin → Job Addresses).
 *
 * ⚠️ There is no suggestion to accept. The list says WHICH jobs need attention;
 * a person sets each address through the same picker used on the project, which
 * is the only thing that writes a verified location.
 */
export interface AddressReviewRow {
  projectId: number;
  jobNumber: string | null;
  title: string | null;
  travelJob: boolean;
  /**
   * Whatever the row holds today — not narrowed to the two statuses now written.
   * The database still permits `suggested` and `unresolvable` from the removed
   * sweep, and dev holds rows in both.
   */
  status: JobAddressStatus;
  /** The free text somebody typed, which is what needs checking. */
  storedAddress: string | null;
}

/** An open bid placeholder (SYSTAC- or ClosedWon-) a job created by hand can be linked to. */
export interface BidPlaceholder {
  id: number;
  jobNumber: string;
  title: string;
  client: string | null;
  /** The Bid Tracker's id for the bid. */
  bidId: number;
  /** `won` = ClosedWon-, waiting for a Sage number; `chance` = SYSTAC-, not won yet. */
  stage: 'chance' | 'won';
  createdAt: string;
}

export const projectsApi = {
  /** Addresses the sweep has proposed or given up on — Admin only. */
  addressReview: () =>
    api.get('/projects/address/review').then((r) => r.data as AddressReviewRow[]),

  /**
   * Address candidates for the picker.
   *
   * ⚠️ Every call is a billable Azure Maps transaction. Callers MUST debounce —
   * the component does, and the server ignores queries under 4 characters, but
   * neither of those helps a new caller that fires on every keystroke.
   */
  searchAddress: (q: string) =>
    api.get('/projects/address/search', { params: { q } })
      .then((r) => r.data as { candidates: AddressCandidate[]; reason?: string }),

  /** Empty the job site address and its verification. */
  clearAddress: (id: number) =>
    api.delete(`/projects/${id}/address`).then((r) => r.data as { jobAddress: null; status: JobAddressStatus }),

  /** Store an address the user picked, marking it verified. */
  setAddress: (id: number, picked: SetAddressPayload) =>
    api.patch(`/projects/${id}/address`, picked)
      .then((r) => r.data as { jobAddress: string; status: JobAddressStatus }),

  /**
   * Open bid placeholders. By default every word of `q` must appear in the title, client or
   * job number, newest first (the picker). `'any'` is the duplicate check: any word of three
   * or more characters, best match first.
   */
  bidPlaceholders: (q?: string, match?: 'any') =>
    api.get('/projects/bid-placeholders', { params: { ...(q ? { q } : {}), ...(match ? { match } : {}) } })
      .then((r) => r.data as BidPlaceholder[]),

  /** Move a bid's link onto this job and archive the placeholder it replaces. */
  linkBid: (id: number, placeholderProjectId: number) =>
    api.post(`/projects/${id}/link-bid`, { placeholderProjectId }).then((r) => r.data),

  list: (params?: Record<string, unknown>) =>
    api.get('/projects', { params }).then((r) => r.data),
  get: (id: number) => api.get(`/projects/${id}`).then((r) => r.data),
  safety: (id: number) => api.get(`/projects/${id}/safety`).then((r) => r.data),
  idByJob: (jobNumber: string) =>
    api.get(`/projects/by-job/${encodeURIComponent(jobNumber)}`).then((r) => r.data as { id: number }),
  counts: (id: number) => api.get(`/projects/${id}/counts`).then((r) => r.data as ProjectCounts),
  weather: (id: number) => api.get(`/projects/${id}/weather`).then((r) => r.data as WeatherSuggestion),
  manpower: (id: number) => api.get(`/projects/${id}/manpower`).then((r) => r.data as ManpowerActuals),
  /**
   * The job's cross-domain "who did what" feed, newest first, paged.
   * Cost-bearing events are filtered out server-side for users without
   * financials access, so the page never has to decide what to hide.
   */
  activity: (id: number, params?: { limit?: number; offset?: number }) =>
    api.get(`/projects/${id}/activity`, { params }).then((r) => r.data as ProjectActivityPage),
  estimatedHours: (id: number) =>
    api.get(`/projects/${id}/estimated-hours`).then((r) => r.data as EstimatedHours),
  // override: a number records the estimator's hours; null takes them back off.
  setEstimatedHours: (id: number, override: number | null) =>
    api.patch(`/projects/${id}/estimated-hours`, { override }).then((r) => r.data as EstimatedHours),
  create: (data: unknown) => api.post('/projects', data).then((r) => r.data),
  update: (id: number, data: unknown) =>
    api.patch(`/projects/${id}`, data).then((r) => r.data),
  updateChecklist: (id: number, items: unknown) =>
    api.patch(`/projects/${id}/checklist`, { items }).then((r) => r.data),
  // Upsert per-phase schedule (fabrication/installation, or a single project
  // phase). Returns the refreshed phase list.
  updatePhases: (id: number, phases: unknown) =>
    api.patch(`/projects/${id}/phases`, { phases }).then((r) => r.data as ProjectPhaseDto[]),
  delete: (id: number) => api.delete(`/projects/${id}`),
  bulkImport: (rows: ImportProjectRow[]) =>
    api.post('/projects/import', { rows }).then((r) => r.data as ImportProjectsResult),
  // Notes (return the project's full updated note list)
  addNote: (id: number, category: string, body: string) =>
    api.post(`/projects/${id}/notes`, { category, body }).then((r) => r.data),
  updateNote: (id: number, noteId: number, body: string) =>
    api.patch(`/projects/${id}/notes/${noteId}`, { body }).then((r) => r.data),
  deleteNote: (id: number, noteId: number) =>
    api.delete(`/projects/${id}/notes/${noteId}`).then((r) => r.data),
  // Field leaders (each call returns the project's full updated list)
  fieldLeaders: (id: number) =>
    api.get(`/projects/${id}/field-leaders`).then((r) => r.data as FieldLeader[]),
  assignFieldLeader: (id: number, person: { entraObjectId: string; displayName?: string; email?: string }) =>
    api.post(`/projects/${id}/field-leaders`, person).then((r) => r.data as FieldLeader[]),
  unassignFieldLeader: (id: number, userId: string) =>
    api.delete(`/projects/${id}/field-leaders/${encodeURIComponent(userId)}`).then((r) => r.data as FieldLeader[]),
  setPrimaryFieldLeader: (id: number, userId: string) =>
    api.patch(`/projects/${id}/field-leaders/${encodeURIComponent(userId)}/primary`).then((r) => r.data as FieldLeader[]),
};

// A field leader assigned to a project (drives their access to that job).
export interface FieldLeader {
  id: string;
  displayName: string | null;
  email: string | null;
  assignedAt: string;
  /** Starred as the project's primary (the portfolio "Field:" display). */
  isPrimary?: boolean;
}

// Lightweight project record for the equipment job-number auto-fill and project
// picker. Carries the project's field leader / job address / site contact so the
// request and call-off forms can prefill them.
export interface EquipmentProjectOption {
  id: number;
  jobNumber: string;
  title: string;
  fieldLeader: string | null;
  jobAddress: string | null;
  siteContactName: string | null;
  siteContactPhone: string | null;
}

export const equipmentApi = {
  list: (params?: Record<string, unknown>) =>
    api.get('/equipment', { params }).then((r) => r.data),
  get: (id: number) => api.get(`/equipment/${id}`).then((r) => r.data),
  create: (data: unknown) => api.post('/equipment', data).then((r) => r.data),
  bulkImport: (items: unknown[]) =>
    api.post('/equipment/import', items).then((r) => r.data),
  update: (id: number, data: unknown) =>
    api.patch(`/equipment/${id}`, data).then((r) => r.data),
  delete: (id: number) => api.delete(`/equipment/${id}`),
  callOff: (id: number, data: unknown) =>
    api.post(`/equipment/${id}/call-off`, data).then((r) => r.data),
  extend: (id: number, data: unknown) =>
    api.post(`/equipment/${id}/extend`, data).then((r) => r.data),
  // Unscoped project list for job-number auto-fill and the project picker.
  // The regular projectsApi.list is visibility-scoped to the caller's
  // assignments, which breaks cross-project job-number matching.
  projectOptions: () =>
    api.get('/equipment/project-options')
      .then((r) => r.data as EquipmentProjectOption[]),
  // Active owned-equipment units for the "Owned Equipment #" picker (Admin catalog).
  ownedUnits: () =>
    api.get('/equipment/owned-units').then((r) => r.data as OwnedEquipmentUnit[]),

  // Files on a request — the rental agreement, mainly (#571). Stored in the
  // tracker itself, and only for equipment managers: everyone else gets a 403.
  listFiles: (id: number) =>
    api.get(`/equipment/${id}/files`).then((r) => r.data as EquipmentFile[]),
  uploadFile: (id: number, file: File) => {
    const fd = new FormData();
    fd.append('file', file, file.name);
    // Content-Type undefined → the browser sets multipart/form-data with the boundary.
    return api.post(`/equipment/${id}/files`, fd, { headers: { 'Content-Type': undefined } as any })
      .then((r) => r.data as EquipmentFile);
  },
  downloadFile: (id: number, fileId: number) =>
    api.get(`/equipment/${id}/files/${fileId}`, { responseType: 'blob' }).then((r) => r.data as Blob),
  removeFile: (id: number, fileId: number) =>
    api.delete(`/equipment/${id}/files/${fileId}`).then((r) => r.data),
};

export interface OwnedEquipmentUnit {
  unitNumber: string;
  description: string | null;
  equipmentClass: string | null;
}

/** One file on an equipment request, without its bytes (#571). */
export interface EquipmentFile {
  id: number;
  fileName: string;
  contentType: string | null;
  sizeBytes: number | null;
  uploadedAt: string;
  uploadedBy: { id: string; displayName: string | null; email: string | null } | null;
}

export interface EquipmentRate {
  id: number;
  category: string;
  model: string;
  equipmentType: string;
  capacityRange: string | null;
  unit: string | null;
  dayRate: number | null;
  weekRate: number | null;
  monthRate: number | null;
  rateYear: number;
  isActive: boolean;
}

/** One row of an uploaded rate sheet, as POSTed to /equipment-rates/import. */
export interface ImportRateRow {
  category: string;
  model: string;
  equipmentType: string;
  capacityRange: string | null;
  unit: string | null;
  dayRate: number | null;
  weekRate: number | null;
  monthRate: number | null;
}

export interface ImportRatesRequest {
  rateYear: number;
  /**
   * 'all-active' supersedes every active row whatever year it carries — what
   * publishing a new annual sheet means, since the picker filters on is_active
   * with no year predicate. 'same-year' (the default) touches only rows sharing
   * rateYear, for correcting a sheet already published.
   */
  supersede?: 'same-year' | 'all-active';
  /** Categories exempt from deactivation, so curated rows survive a full replace. */
  preserveCategories?: string[];
  rows: ImportRateRow[];
}

export interface ImportRatesResult {
  inserted: number;
  deactivated: number;
}

export const equipmentRatesApi = {
  list: (params?: { search?: string; rateYear?: number; activeOnly?: boolean }) =>
    api.get('/equipment-rates', { params }).then((r) => r.data as EquipmentRate[]),
  bulkImport: (body: ImportRatesRequest) =>
    api.post('/equipment-rates/import', body).then((r) => r.data as ImportRatesResult),
};

export const receivingApi = {
  list: (params?: Record<string, unknown>) =>
    api.get('/receiving', { params }).then((r) => r.data),
  get: (id: number) => api.get(`/receiving/${id}`).then((r) => r.data),
  create: (data: unknown) => api.post('/receiving', data).then((r) => r.data),
  bulkImport: (items: unknown[]) =>
    api.post('/receiving/import', items).then((r) => r.data),
  update: (id: number, data: unknown) =>
    api.patch(`/receiving/${id}`, data).then((r) => r.data),
  delete: (id: number) => api.delete(`/receiving/${id}`),
  // Unscoped project list for the project picker (GET /projects is
  // visibility-scoped to the caller's assignments).
  projectOptions: () =>
    api.get('/receiving/project-options')
      .then((r) => r.data as { id: number; jobNumber: string; title: string }[]),
};

// ─── Shipping requests: ask Logistics to pick something up or deliver it ─────
// (#717, #718, #719). A request may belong to no job (excess material, a vendor pickup).

export type ShippingKind = 'pickup' | 'delivery';

/** `code` is what the app branches on; `label` is display text that can be reworded. */
export interface ShippingStatusOption {
  id: number;
  code: string;
  label: string;
  phase: 'Active' | 'Closed';
  sortOrder: number;
}

export interface ShippingTruckTypeOption {
  id: number;
  code: string;
  label: string;
}

export interface ShippingRequest {
  id: number;
  kind: ShippingKind;
  projectId: number | null;
  jobNameRaw: string | null;
  materialId: number | null;
  items: string;
  fromLocation: string | null;
  toLocation: string | null;
  /** A DATE column, serialized as a timestamp at UTC midnight: read it with toDateInputValue / fmtDate. */
  neededByDate: string | null;
  expedited: boolean;
  dimensions: string | null;
  weight: string | null;
  truckTypeId: number | null;
  /** The jobsite can receive it. null = not answered. */
  siteReady: boolean | null;
  contactName: string | null;
  contactPhone: string | null;
  statusId: number;
  notes: string | null;
  requestorId: string | null;
  requestorName: string | null;
  /**
   * Whether THIS caller may change or remove the request: the Logistics desk any, a PM only those on their own jobs
   * (or with no job, ones they raised). The server decides it and refuses the write otherwise; the drawer opens a
   * request read-only when this is false, so nobody types into a form that cannot be saved.
   */
  canEdit: boolean;
  createdAt: string;
  updatedAt: string;
  status: ShippingStatusOption | null;
  truckType: ShippingTruckTypeOption | null;
  project: { id: number; jobNumber: string; title: string } | null;
  material: { id: number; item: string | null } | null;
}

/**
 * POST /shipping. A blank optional field is left out rather than sent as null.
 * `kind` and `items` are required; a delivery to a job also needs the onsite
 * contact, their phone and `siteReady` (the API says so in a 400).
 */
export interface ShippingCreateBody {
  kind: ShippingKind;
  projectId?: number;
  materialId?: number;
  items: string;
  fromLocation?: string;
  toLocation?: string;
  neededByDate?: string;
  expedited?: boolean;
  dimensions?: string;
  weight?: string;
  truckTypeId?: number;
  siteReady?: boolean;
  contactName?: string;
  contactPhone?: string;
  notes?: string;
}

/**
 * PATCH /shipping/:id. Partial: an ABSENT key leaves the stored value alone and an
 * explicit `null` clears it (items, expedited and statusId cannot be cleared).
 * What a request is FOR (kind, job, material) cannot be edited.
 */
export interface ShippingUpdateBody {
  items?: string;
  fromLocation?: string | null;
  toLocation?: string | null;
  neededByDate?: string | null;
  expedited?: boolean;
  dimensions?: string | null;
  weight?: string | null;
  truckTypeId?: number | null;
  siteReady?: boolean | null;
  contactName?: string | null;
  contactPhone?: string | null;
  notes?: string | null;
  statusId?: number;
}

export const shippingApi = {
  /**
   * With `projectId`: that job's requests, for anyone who can see the job. Without it: every
   * request, for people who hold receiving.edit or view-all (anyone else gets a 403).
   */
  list: (params?: { projectId?: number; search?: string; statusId?: number; kind?: ShippingKind; phase?: 'Active' | 'Closed' }) =>
    api.get('/shipping', { params }).then((r) => r.data as ShippingRequest[]),
  get: (id: number) => api.get(`/shipping/${id}`).then((r) => r.data as ShippingRequest),
  create: (data: ShippingCreateBody) => api.post('/shipping', data).then((r) => r.data as ShippingRequest),
  update: (id: number, data: ShippingUpdateBody) =>
    api.patch(`/shipping/${id}`, data).then((r) => r.data as ShippingRequest),
  delete: (id: number) => api.delete(`/shipping/${id}`),
};

// ─── Materials (CMS expansion — per-project procurement/expediting) ─────────
// Tracker-owned + manual entry today; PO numbers + committed cost will flow
// in from Sage Intacct once that sync is built.

export interface Material {
  id: number;
  projectId: number;
  item: string;
  spec: string | null;
  costCode: string | null;
  costCodeName: string | null;
  vendor: string | null;
  qty: string | null;
  committedCost: number | null;
  sagePoNumber: string | null;
  needByDate: string | null;
  status: string; // requisition | po | fab | pickup | shipped | onsite | installed
  notes: string | null;
  requestorId: string | null;
  createdAt: string;
  updatedAt: string;
  // Concrete roll-up (QA/QC pour cards linked to this commitment).
  pourCount: number;
  /** Rock/aggregate deliveries linked here; counted apart, never in releasedCy. */
  rockCount: number;
  releasedCy: number | null;
  project: { id: number; jobNumber: string; title: string } | null;
  requestor: { id: string; displayName: string | null; email: string | null } | null;
}

export const materialsApi = {
  list: (params: { projectId: number; search?: string; status?: string }) =>
    api.get('/materials', { params }).then((r) => r.data as Material[]),
  get: (id: number) => api.get(`/materials/${id}`).then((r) => r.data as Material),
  create: (data: unknown) => api.post('/materials', data).then((r) => r.data as Material),
  update: (id: number, data: unknown) =>
    api.patch(`/materials/${id}`, data).then((r) => r.data as Material),
  delete: (id: number) => api.delete(`/materials/${id}`),
};

// ─── QA/QC (CMS expansion — Formstack mirror: Pour Cards + Inspections) ─────

export interface PourCardChecklistItem { section: string; label: string; value: string }

export interface PourCard {
  id: number;
  projectId: number | null;
  jobNumberRaw: string | null;
  pourType: string;           // main | mudseal | willcall | rock
  element: string | null;
  location: string | null;
  pourDatetime: string | null;
  mix: string | null;
  qtyText: string | null;
  qtyCy: number | null;
  vendor: string | null;
  costCode: string | null;
  placement: string | null;
  pmEmail: string | null;
  superName: string | null;
  /**
   * The field leader's number, resolved server-side against the CSV-seeded
   * field_directory — the form free-types the name and asks for no phone.
   *
   * ⚠️ Never render this bare when `superPhoneSource` isn't 'exact'. The number
   * may belong to a CLOSE name match or to the job's assigned leader rather than
   * the person typed on the card, and `superPhoneName` says who. Showing it
   * unlabelled is how the office ends up ringing the wrong foreman.
   */
  superPhone: string | null;
  superPhoneSource: 'exact' | 'close-match' | 'job-leader' | null;
  /** Who the number belongs to, when that isn't the name on the card. */
  superPhoneName: string | null;
  /** The order confirmation CALL — office-entered, never from Formstack. The
   *  dispatcher works for the vendor and is per-order (plants rotate by shift). */
  dispatcherName: string | null;
  dispatcherPhone: string | null;
  orderNotes: string | null;
  checklist: PourCardChecklistItem[];
  status: string;             // pending | confirmed | poured
  confirmedAt: string | null;
  confirmedBy: string | null;
  pouredAt: string | null;
  pouredBy: string | null;
  pouredSource: string | null;
  cancelledAt: string | null;
  /** Display name of whoever cancelled — the outcome most likely to need
   *  chasing up, so it's surfaced in the strip AND the activity stream. */
  cancelledBy: string | null;
  /**
   * Verdict on the field-typed Mix ID against the project's approved register.
   * Computed on read, never stored, so approving a mix later clears the flag on
   * pours already in flight.
   *
   * ⚠️ `unchecked` means "nothing to check against" — no register on the job, or
   * no mix on the card. It is NOT a mismatch and must never be badged as one, or
   * every job flags on day one and the badge stops meaning anything.
   */
  mixMatch: 'approved' | 'unapproved' | 'unchecked' | 'ambiguous';
  mixDesignId: number | null;
  /** The register's spelling, when matched — may differ in case/punctuation. */
  approvedMixId: string | null;
  /** When the match was settled onto the card. Once settled it survives the
   *  design being retired — that's what stops retirement rewriting history. */
  mixMatchedAt: string | null;
  /** Settled against a design that has since been retired. Still approved; worth
   *  saying so rather than implying the register still offers that mix. */
  mixRetired: boolean;
  coveredAt: string | null;
  coveredBy: string | null;
  dailyLogId: number | null;
  materialId: number | null;
  materialItem: string | null;
  materialSagePo: string | null;
  actualPourAt: string | null;
  actualQtyCy: number | null;
  actualNotes: string | null;
  createdAt: string;
  /** 'manual' = entered by the office with the Add button (phoned-in order, or
   *  the form app was down) — there is no Formstack submission behind it. */
  source: 'formstack' | 'manual';
  /** Who entered a manual card; always null for Formstack cards. */
  createdBy: string | null;
  project: {
    id: number; jobNumber: string; title: string;
    /** Phone read-back fields (drawer Pour Details). */
    jobAddress?: string | null; pmName?: string | null;
    /** The JOB's concrete supplier & blanket POs (project_concrete_setup),
     *  carried on the card so the schedule grid needs no per-job fetch. */
    concreteSupplier?: string | null;
    concreteBpo?: string | null;
    aggregateBpo?: string | null;
  } | null;
}

/** The job's concrete supplier & blanket POs — estimator raises the PO, supplier varies by job; one record per job. */
export interface ConcreteSetup {
  projectId: number;
  supplier: string | null;
  concreteBpo: string | null;
  aggregateBpo: string | null;
  notes: string | null;
  updatedAt: string;
  updatedBy: string | null;
}

// One audited field correction on a pour card (drawer Activity stream).
export interface PourCardEdit {
  id: number;
  field: string;
  oldValue: string | null;
  newValue: string | null;
  editedAt: string;
  editedBy: string | null;
}

export interface QaInspection {
  id: number;
  projectId: number | null;
  inspectorName: string | null;
  inspectorTitle: string | null;
  inspectDate: string | null;
  jobText: string | null;
  noCount: number;
  items: PourCardChecklistItem[];
  summary: string | null;
  createdAt: string;
  project: { id: number; jobNumber: string; title: string } | null;
}

// ─── Sage Intacct PO sync (read-only FROM Intacct — nothing flows back) ─────
export interface SageSyncStatus {
  configured: boolean;
  running: boolean;
  /** Nightly automated-sync schedule state (enabled via SAGE_PO_SYNC_ENABLED). */
  schedule: { enabled: boolean; cron: string };
  lastRun: {
    startedAt: string; finishedAt: string | null; status: string;
    linesFetched: number | null; linesStored: number | null;
    materialsUpdated: number | null; unmatched: number | null;
    error: string | null; runBy: string | null;
  } | null;
  totals: { lines: number; matched: number; unknownJob: number };
  review: { docNo: string; costCode: string | null; item: string | null; value: number | null; jobNumber: string; title: string }[];
}

// One untracked Intacct PO line on a job (the Materials tab's Track list).
export interface SagePoLine {
  recordNo: number;
  docNo: string;
  /** The PO's human label (E&W types it into Intacct's PO-number field). */
  docLabel: string | null;
  costCode: string | null;
  itemId: string | null;
  /** Free-typed line description when Intacct has one; else the catalog item. */
  item: string | null;
  memo: string | null;
  vendor: string | null;
  /** Intacct cost type: M = material, S = subcontract, EQR = rented equipment… */
  costType: string | null;
  qty: number | null;
  value: number | null;
  whenModified: string | null;
}

// One version row in a document chain (SharePoint keeps the file; this is
// the supersede metadata). isCurrent = highest version in its chain.
export interface DocVersion {
  id: number;
  chainId: number;
  version: number;
  isCurrent: boolean;
  chainSize: number;
  driveItemId: string;
  webUrl: string;
  fileName: string;
  sizeBytes: number | null;
  folderItemId: string | null;
  note: string | null;
  uploadedBy: string | null;
  uploadedAt: string;
}

// Where a change is in its life. PCO = a potential change, COR = sent to the
// client, OCO = the client accepted it, ended = declined or withdrawn.
export type ChangeStage = 'PCO' | 'COR' | 'OCO' | 'ended';

// Why a change is being nudged: it has sat in its stage past the configured threshold.
// send = a PCO not yet sent, chase = a COR with the client, link = accepted but not linked to Sage.
export type ChangeAttentionKind = 'send' | 'chase' | 'link';
export interface ChangeAttention {
  kind: ChangeAttentionKind;
  /** Whole Eastern days in the current stage. */
  days: number;
  /** YYYY-MM-DD the stage began. */
  since: string;
}

// One change order (PCO / COR / OCO). Tracked in the Project Tracker; accepted changes are
// entered in Intacct BY the PM/PE (intacctEnteredAt confirms it — no
// integration writes anything).
export interface ChangeOrder {
  id: number;
  projectId: number;
  changeNo: string;
  /** The prefix as stored. The API moves it PCO → COR → OCO with the stage buttons, but a row from before
   *  them can lag (an accepted change that still says PCO), so read `stage`, not this. */
  type: string;                 // PCO | COR | OCO
  /** Where it is now, derived by the API from `status`. */
  stage: ChangeStage;
  description: string;
  costCode: string | null;
  amount: number | null;        // signed
  scheduleDays: number | null;  // signed
  notes: string | null;
  rfiId: number | null;
  rfiNumber: string | null;
  status: string;               // pending | approved (the client accepted) | rejected
  decidedAt: string | null;
  decidedBy: string | null;
  /** YYYY-MM-DD, a calendar date (use fmtDate). Null for a PCO, or a change opened past that stage. */
  sentToClientOn: string | null;
  sentToClientBy: string | null;
  /** YYYY-MM-DD. The date the client accepted: the date the change counts on. */
  acceptedOn: string | null;
  /** The client's PO, signed change order or email. Free text; hidden from field leaders. */
  clientRef: string | null;
  /** Why it was declined or withdrawn. Free text; hidden from field leaders. */
  decisionNote: string | null;
  intacctEnteredAt: string | null;
  intacctEnteredBy: string | null;
  /** The linked Intacct CO id (PROJECTCHANGEORDERID) once confirmed. */
  intacctRef: string | null;
  /** Verification vs the linked Intacct CO on last sync: null = not entered,
   *  true = link resolves, false = confirmed CO not found (re-verify). */
  intacctCoFound: boolean | null;
  /** The linked Intacct CO's amount (client warns if it differs from `amount`). */
  intacctCoAmount: number | null;
  intacctCoStatus: string | null;
  /** The Intacct total frozen when the change was linked: what the Bid Tracker counts. Hidden from field leaders. */
  countedAmount: number | null;
  countedAmountAt: string | null;
  /** Intacct's total has moved off `countedAmount` since the link. Hidden from field leaders. */
  countedAmountDiffers: boolean | null;
  createdAt: string;
  createdBy: string | null;
  /**
   * Set by the API when the change has sat past the threshold; null when it has not, and for a
   * field leader (who cannot act on it). Absent from a response that predates it and from the
   * result of a stage button, so read it as "no cue", never as an error.
   */
  attention?: ChangeAttention | null;
}

// One synced Intacct change order (the confirmation picker's options).
export interface IntacctCo {
  coId: string;
  description: string | null;
  amount: number | null;
  state: string | null;
  status: string | null;
  linked: boolean;
}

export const changesApi = {
  list: (projectId: number) =>
    api.get('/changes', { params: { projectId } }).then((r) => r.data as ChangeOrder[]),
  create: (dto: Record<string, unknown>) =>
    api.post('/changes', dto).then((r) => r.data as ChangeOrder),
  // PCO → COR. `sentOn` is YYYY-MM-DD; omitted means today.
  sentToClient: (id: number, dto: { sentOn?: string }) =>
    api.post(`/changes/${id}/sent-to-client`, dto).then((r) => r.data as ChangeOrder),
  // COR → OCO. `acceptedOn` is the date the change counts on; omitted means today.
  accept: (id: number, dto: { acceptedOn?: string; clientRef?: string }) =>
    api.post(`/changes/${id}/accept`, dto).then((r) => r.data as ChangeOrder),
  // Declined by the client, or withdrawn.
  decline: (id: number, dto: { reason?: string }) =>
    api.post(`/changes/${id}/decline`, dto).then((r) => r.data as ChangeOrder),
  // Only until the change is linked to Intacct.
  correctAcceptance: (id: number, dto: { acceptedOn?: string; clientRef?: string }) =>
    api.patch(`/changes/${id}/acceptance`, dto).then((r) => r.data as ChangeOrder),
  // Re-freeze the counted amount at what Intacct shows now.
  refreshAmount: (id: number) =>
    api.post(`/changes/${id}/refresh-amount`).then((r) => r.data as ChangeOrder),
  intacctCos: (projectId: number) =>
    api.get('/changes/intacct-cos', { params: { projectId } }).then((r) => r.data as IntacctCo[]),
  // Confirm by linking to a real Intacct CO (picked from the job's synced COs).
  intacctEntered: (id: number, intacctCoId: string) =>
    api.post(`/changes/${id}/intacct-entered`, { intacctCoId }).then((r) => r.data as ChangeOrder),
  rfiNoImpact: (rfiId: number) =>
    api.post(`/changes/rfi/${rfiId}/no-impact`).then((r) => r.data as { ok: boolean }),
};

export const documentsApi = {
  versions: (projectId: number) =>
    api.get('/documents/versions', { params: { projectId } }).then((r) => r.data as DocVersion[]),
  registerVersion: (dto: Record<string, unknown>) =>
    api.post('/documents/versions', dto).then((r) => r.data as { id: number; version: number }),
};

// One drawing SET revision. SharePoint keeps the PDF; this is the chain +
// drawing-specific metadata. isCurrent = highest non-deleted version in its
// chain (a "new revision" supersedes the prior one).
export interface DrawingSet {
  id: number;
  chainId: number;
  version: number;
  isCurrent: boolean;
  chainSize: number;
  setName: string;
  setType: string | null;
  discipline: string | null;
  revLabel: string | null;
  issueDate: string | null;
  driveItemId: string;
  webUrl: string;
  fileName: string;
  sizeBytes: number | null;
  folderItemId: string | null;
  note: string | null;
  uploadedBy: string | null;
  uploadedAt: string;
}

export const drawingsApi = {
  sets: (projectId: number) =>
    api.get('/drawings/sets', { params: { projectId } }).then((r) => r.data as DrawingSet[]),
  registerSet: (dto: Record<string, unknown>) =>
    api.post('/drawings/sets', dto).then((r) => r.data as { id: number; version: number }),
  updateSet: (id: number, dto: Record<string, unknown>) =>
    api.patch(`/drawings/sets/${id}`, dto).then((r) => r.data as { updated: number }),
  deleteSet: (id: number) =>
    api.delete(`/drawings/sets/${id}`).then((r) => r.data as { deleted: number }),
};

// ── Project directory (#598): the address book, and who is on each job ──

export interface DirectoryCompany {
  id: number;
  name: string;
  kind: string;
  phone: string | null;
  address1: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  website: string | null;
  notes: string | null;
  /** CSI MasterFormat divisions, two digits each ("03", "26"). */
  divisions: string[];
}

export interface DirectoryPerson {
  id: number;
  name: string;
  title: string | null;
  email: string | null;
  phone: string | null;
  cell: string | null;
  notes: string | null;
  companyId: number | null;
}

/** Someone from outside on the job, and their role on it. */
export interface ProjectContactEntry {
  id: number;
  role: string;
  note: string | null;
  addedAt: string;
  person: DirectoryPerson;
  company: DirectoryCompany | null;
  /**
   * What the entry, the person and the company said when read, for Edit to send
   * back (entryVersion / personVersion / companyVersion): a save is refused if a
   * part it writes has changed since. Editors only.
   */
  versions?: { entry: string; person: string; company: string | null };
}

/** One of our own people on the job, from its PM / PE / estimator / safety manager slots and field leaders. */
export interface DirectoryTeamMember {
  userId: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  role: string;
}

export interface ProjectDirectory {
  team: DirectoryTeamMember[];
  /**
   * When canEdit is false, the address book's own fields come back blank: the
   * person's notes, and the company's street address, ZIP, website and notes.
   * Only what the list shows is sent to someone who can't edit (Codex on #632).
   */
  contacts: ProjectContactEntry[];
  canEdit: boolean;
  roles: { key: string; label: string }[];
  kinds: { key: string; label: string }[];
  divisions: { code: string; title: string }[];
}

/** A company or person as typed in, for the address book. Empty text clears a field. */
export interface CompanyInput {
  name: string;
  kind: string;
  phone?: string | null;
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  website?: string | null;
  notes?: string | null;
  divisions?: string[];
}
export interface PersonInput {
  name: string;
  title?: string | null;
  email?: string | null;
  phone?: string | null;
  cell?: string | null;
  notes?: string | null;
}

/** Put someone on a job: from the book ({ contactId }), or someone new (at a book company, a new one, or none). */
export type AddProjectContactInput = { role: string; note?: string | null } & (
  | { contactId: number }
  | { person: PersonInput; companyId?: number; company?: CompanyInput }
);

export interface DirectorySearchResult {
  people: (Omit<DirectoryPerson, 'companyId'> & { company: { id: number; name: string } | null })[];
  companies: DirectoryCompany[];
}

export interface DirectorySuggestions {
  /** The job's Intacct subcontractors, each with the book company of that name, if any, and its people. */
  subs: { name: string; company: (DirectoryCompany & { people: { id: number; name: string; title: string | null; email: string | null }[] }) | null }[];
}

export const directoryApi = {
  forProject: (projectId: number) =>
    api.get(`/projects/${projectId}/contacts`).then((r) => r.data as ProjectDirectory),
  suggestions: (projectId: number) =>
    api.get(`/projects/${projectId}/contacts/suggestions`).then((r) => r.data as DirectorySuggestions),
  add: (projectId: number, body: AddProjectContactInput) =>
    api.post(`/projects/${projectId}/contacts`, body).then((r) => r.data as { id: number; team: DirectoryTeamMember[]; contacts: ProjectContactEntry[] }),
  /**
   * Edit someone on the job as one save: role and note, and optionally their
   * address-book entry (which can move them to another company) and their
   * company's, or move them to a new company, added to the book (`newCompany`,
   * sent with `person` and no companyId). All or nothing (Codex on #632).
   */
  update: (projectId: number, entryId: number, body: {
    /** Role and note: left out, kept. */
    role?: string; note?: string | null;
    person?: PersonInput & { companyId?: number | null };
    company?: CompanyInput;
    newCompany?: CompanyInput;
    /** With `company`: the company it's for, as shown. Refused if they've moved since. */
    editingCompanyId?: number;
    /** From the entry's versions: refused if that part has changed since it was read. */
    entryVersion?: string;
    personVersion?: string;
    companyVersion?: string;
  }) =>
    api.patch(`/projects/${projectId}/contacts/${entryId}`, body).then((r) => r.data as { team: DirectoryTeamMember[]; contacts: ProjectContactEntry[] }),
  remove: (projectId: number, entryId: number) =>
    api.delete(`/projects/${projectId}/contacts/${entryId}`).then((r) => r.data as { team: DirectoryTeamMember[]; contacts: ProjectContactEntry[] }),
  search: (q: string) =>
    api.get('/contacts/search', { params: { q } }).then((r) => r.data as DirectorySearchResult),
};

// ─── Meetings (#644) ──────────────────────────────────────────────────────────

/** A meeting as the job's Meetings list shows it. */
export interface MeetingSummary {
  id: number;
  meetingType: string;
  /** Its number among the job's meetings of this type. */
  meetingNumber: number;
  /** The title, or "OAC Meeting #3" when it has none. */
  name: string;
  title: string | null;
  /** An instant (ISO); shown and edited in Eastern. */
  startsAt: string;
  location: string | null;
  status: string;
  itemCount: number;
  openCount: number;
  attendeeCount: number;
}

export interface MeetingAttendee {
  id: number;
  /** Picked from the job's Contacts; null for a typed name. */
  contactId: number | null;
  name: string;
  company: string | null;
  /** 'present' | 'absent', or null when not recorded. */
  attendance: string | null;
}

/** An agenda item at one meeting: its wording, and its minutes, status, owner and due date as of that meeting. */
export interface MeetingItem {
  /** Its entry on this meeting — what a save sends back as entryId. */
  entryId: number;
  itemId: number;
  originMeetingId: number;
  title: string;
  section: string | null;
  discussion: string | null;
  status: string;
  responsible: string | null;
  /** YYYY-MM-DD, or null. */
  dueDate: string | null;
}

export interface MeetingDetail extends MeetingSummary {
  projectId: number;
  /** Send back as expectedVersion: a save is refused if someone else has saved since. */
  version: number;
  createdAt: string;
  updatedAt: string | null;
  attendees: MeetingAttendee[];
  items: MeetingItem[];
  canEdit: boolean;
}

export interface ProjectMeetings {
  meetings: MeetingSummary[];
  canEdit: boolean;
  types: { key: string; label: string }[];
  statuses: { key: string; label: string }[];
  itemStatuses: { key: string; label: string }[];
}

/** A meeting as the editor saves it, whole. Times are Eastern wall-clock, YYYY-MM-DDTHH:mm. */
export interface MeetingInput {
  title: string | null;
  startsAt: string;
  location: string | null;
  status: string;
  attendees: { contactId: number | null; name: string; company: string | null; attendance: string | null }[];
  items: {
    /** Left out for an item added in this save. */
    entryId?: number;
    title: string;
    section: string | null;
    discussion: string | null;
    status: string;
    responsible: string | null;
    dueDate: string | null;
  }[];
}

export const meetingsApi = {
  list: (projectId: number) =>
    api.get(`/projects/${projectId}/meetings`).then((r) => r.data as ProjectMeetings),
  get: (projectId: number, meetingId: number) =>
    api.get(`/projects/${projectId}/meetings/${meetingId}`).then((r) => r.data as MeetingDetail),
  /**
   * A new meeting: numbered by the server within its type. clientRequestId:
   * made once per new meeting and resent on retry, so a retry after a lost
   * response gets the meeting the first POST made instead of a second one.
   */
  create: (projectId: number, body: MeetingInput & { meetingType: string; clientRequestId?: string }) =>
    api.post(`/projects/${projectId}/meetings`, body).then((r) => r.data as MeetingDetail),
  /** Save it whole. 409 if someone else saved it after `expectedVersion` was read. */
  update: (projectId: number, meetingId: number, body: MeetingInput & { expectedVersion: number }) =>
    api.put(`/projects/${projectId}/meetings/${meetingId}`, body).then((r) => r.data as MeetingDetail),
  remove: (projectId: number, meetingId: number) =>
    api.delete(`/projects/${projectId}/meetings/${meetingId}`).then((r) => r.data as { id: number }),
};

/** One GL line the Tracker holds that Intacct no longer has (#613). */
export interface GlStaleLine {
  recordNo: string;
  projectId: number | null;
  projectJob: string | null;
  accountNo: string | null;
  taskId: string | null;
  costType: string | null;
  amount: number;
  entryDate: string | null;
  batchNo: string | null;
  whenModified: string | null;
  fetchedAt: string | null;
  /** 'replaced': a surviving line matches it (the entry was re-dated or re-posted).
   *  'removed': nothing like it is left (deleted, or moved to another job). */
  kind: 'replaced' | 'removed';
  replacedBy: string | null;
}
export interface GlStaleJob {
  projectId: number | null; projectJob: string | null;
  lines: number; amount: number; replaced: number; removed: number;
}
export interface GlCheckResult {
  /** False when the pull from Intacct did not finish; nothing is then called stale. */
  complete: boolean;
  localLines: number;
  intacctLines: number;
  excludedAccounts: string[];
  warnings: string[];
  stale: GlStaleLine[];
  staleAmount: number;
  byJob: GlStaleJob[];
  intacctOnlyLines: number;
  intacctOnlyAmount: number;
  /** True when `stale` was cut for size; the totals still cover every line. */
  staleTruncated: boolean;
  staleLinesTotal: number;
}
export interface GlCheckStatus {
  state: 'idle' | 'running' | 'done' | 'failed';
  configured: boolean;
  startedAt: string | null;
  finishedAt: string | null;
  stage: string | null;
  error: string | null;
  result: GlCheckResult | null;
}

export const sageApi = {
  status: () => api.get('/sage/status').then((r) => r.data as SageSyncStatus),
  /** READ-ONLY check for GL lines the Tracker holds that Intacct no longer has (Close access or admin). */
  glCheck: () => api.get('/sage/gl-reconcile').then((r) => r.data as GlCheckStatus),
  startGlCheck: () => api.post('/sage/gl-reconcile').then((r) => r.data as { started: boolean }),
  // Generous timeout: the first full pull runs ~2 min server-side; without a
  // timeout a request caught by an API restart hangs "Syncing…" forever.
  // Fire-and-forget: the server kicks off the sync in the background and returns
  // immediately; the panel polls /sage/status for progress.
  sync: () => api.post('/sage/sync').then((r) => r.data as { started: boolean }),
  lines: (projectId: number) =>
    api.get('/sage/lines', { params: { projectId } }).then((r) => r.data as SagePoLine[]),
  // The Sage PO lines that make up a tracked material row (expandable detail).
  materialLines: (materialId: number) =>
    api.get('/sage/material-lines', { params: { materialId } }).then((r) => r.data as SagePoLine[]),
  // "Track": create a Materials row FROM the PO line — no retyping.
  track: (recordNo: number) =>
    api.post(`/sage/lines/${recordNo}/track`).then((r) => r.data as { materialId: number }),
};

// ─── Paycor field-hours feed ─────────────────────────────────────────────────
// PMT owns this ingestion from 2026-08-25 (it replaced a weekly Power Automate
// flow that failed unnoticed for nine days). Read-only FROM Paycor.
export interface PaycorSyncRun {
  id: number;
  source: 'daily' | 'weekly' | 'roster' | 'manual' | 'backfill' | null;
  status: 'running' | 'success' | 'error';
  started_at: string;
  finished_at: string | null;
  rows_fetched: number | null;
  rows_stored: number | null;
  dates_covered: number | null;
  date_from: string | null;
  date_to: string | null;
  rows_added: number | null;
  rows_removed: number | null;
  /** Roster only: employees present before and after whose details changed. */
  rows_changed: number | null;
  hours_delta: number | null;
  /**
   * Dates this run recorded a correction for. Counted from
   * paycor_hours_revision, so it catches content-only edits (a cost code fixed,
   * hours moved between two people) that leave every other counter at zero.
   */
  revisions: number | null;
  error: string | null;
  run_by_name: string | null;
}

export interface PaycorSyncStatus {
  configured: boolean;
  /** Names of the settings still missing, so the panel can say which. */
  missingConfig: string[];
  running: boolean;
  /**
   * Nightly schedule state. `enabled: false` means nothing is automated yet —
   * the feed ships dark so it can be reconciled against the old Power Automate
   * flow first.
   */
  schedule: { enabled: boolean; cron: string };
  runs: PaycorSyncRun[];
  feed: {
    rows_total: number;
    min_date: string | null;
    max_date: string | null;
    field_hours: number | null;
    last_synced_at: string | null;
  };
  lastSuccessAt: string | null;
  /**
   * Staleness signal — issue #388 alerts on THIS, never on max work_date.
   * It is the age of the STALEST leg, so a healthy daily pull can't mask a dead
   * weekly one. null = at least one leg has never succeeded.
   */
  hoursSinceLastSuccess: number | null;
  /** Per-leg ages; null means that leg has never succeeded. */
  dailyAgeHours: number | null;
  weeklyAgeHours: number | null;
  rosterAgeHours: number | null;
  /**
   * Days since a successful roster sync last added, removed or changed anyone.
   * null = the roster has never synced. `rosterUnchangedWarn` is true once it
   * is long enough to suspect the report is returning a fixed list.
   */
  rosterUnchangedDays: number | null;
  rosterUnchangedWarn: boolean;
  /**
   * Days in the correction window no successful run has covered — a failed
   * nightly pull leaves one. The daily report's window moves on, so the next
   * night's success would otherwise turn the panel green over the hole.
   */
  missingDates: string[];
  lateEdits: { n: number; max_lag_days: number | null; avg_lag_days: number | null };
}

export const paycorApi = {
  status: () => api.get('/paycor/status').then((r) => r.data as PaycorSyncStatus),
  // Fire-and-forget: reports take 15–80 s to generate server-side, so the API
  // returns immediately and the panel polls /paycor/status.
  sync: () => api.post('/paycor/sync').then((r) => r.data as { started: boolean }),
  // On demand only — it asks Paycor directly, and Paycor rate-limits.
  reportTemplates: () => api.get('/paycor/report-templates').then((r) => r.data as PaycorReportTemplates),
};

export interface PaycorReportTemplates {
  templates: {
    templateId: string;
    parentTemplateId: string | null;
    displayName: string | null;
    /** The PAYCOR_TEMPLATE_* settings that hold this id, if any. */
    inUse: string[];
  }[];
  /** Paycor said there were more than one lookup reads; the list is short. */
  truncated: boolean;
  /** Configured ids Paycor did not list — the next sync will fail on them. */
  configuredNotListed: string[];
  /** Per page, what Paycor sent: content type, keys and counts, never values. */
  shape: string[];
}

// ─── Job Financials / WIP (raw facts; the client computes the WIP math) ──────
export interface WipCostRow {
  costCode: string | null;
  costCodeName: string | null;
  costTypeGroup: string;   // Labor | Material | Subcontract | Equipment | Other
  budgeted: boolean;       // false = actual/PO on a code the estimate never budgeted
  budget: number;
  committed: number;
  actual: number;
  /**
   * The portion of `actual` that is job-ALLOCATED indirect (GL 59998) rather
   * than work booked to this code. Already inside `actual` — this is an
   * explanation, not an adjustment, and must never be subtracted from it.
   *
   * Finance confirmed 2026-07-31 that indirect belongs in code-level actual and
   * that this matches Intacct's own Budget vs Actual by Cost Code report. But
   * the estimate budgets nothing to 59998, so a code carrying indirect reads
   * above budget with no way for the PM to see why — 49 codes company-wide are
   * over budget purely because of it. Hence surfacing the figure.
   */
  indirect: number;
}
export interface BudgetTransferEntry {
  id: number; fromCode: string; toCode: string; amount: number;
  reason: string | null; by: string | null; at: string;
}
/**
 * One line of a cost code's cost-to-complete buildup.
 *
 * One shape for every cost type; the drawer labels the factors per the code's
 * reporting group (Labor = crew × days × hrs × rate, Material = units × unit
 * cost, and so on). An unused factor is null and reads as 1 — never 0.
 *
 * `amount` is computed server-side and never stored, so it cannot drift from
 * its own factors.
 */
export interface CtcLine {
  id: number;
  costCode: string;
  sortOrder: number;
  description: string | null;
  qty1: number | null;
  qty2: number | null;
  qty3: number | null;
  rate: number;
  amount: number;
}
export interface CtcLineInput {
  description?: string | null;
  qty1?: number | null;
  qty2?: number | null;
  qty3?: number | null;
  rate?: number | null;
}

export interface FinancialsWip {
  contract: {
    original: number | null; approvedChange: number | null; pendingChange: number | null;
    revised: number | null; billed: number | null; syncedAt: string | null;
  } | null;
  rows: WipCostRow[];
  indirectActual: number;              // allocated indirect $ (GL 59998) — carved out for Super Margin
  /**
   * Intacct marks this job Completed, so budget left on a code is released: a code
   * with no forecast defaults to $0 cost to complete instead of its remaining
   * budget (`ComputeOpts.releaseRemaining`). Decided server-side so the web app
   * carries no copy of what counts as Completed.
   */
  jobCompleted: boolean;
  /**
   * The EFFECTIVE CTC per cost code — already resolved server-side: a code with
   * buildup lines carries their sum, everything else carries its typed
   * override. Codes absent here fall back to remaining budget (`defaultCtc`) —
   * or to $0 when `jobCompleted`.
   */
  forecast: Record<string, number>;
  /**
   * The workings behind the built-up codes. A code appearing here has a
   * READ-ONLY CTC in the table — its figure comes from these lines, and its
   * typed override (if any) is ignored and not saved.
   */
  ctcLines: CtcLine[];
  budgetAdj: Record<string, number>;   // net budget transfer delta by cost code
  transferLog: BudgetTransferEntry[];
  forecastStamp: { asOf: string; by: string | null } | null;
  provenance: {
    actualEnabled: boolean; budgetEnabled: boolean;
    actualSyncedAt: string | null; budgetSyncedAt: string | null; committedSyncedAt: string | null;
    /** False when this job has NO synced estimate lines at all. Distinguishes a
     *  genuinely zero budget from one that never arrived — without it, estimate
     *  margin and fade render as real numbers against a phantom $0 estimate. */
    budgetSynced: boolean;
  };
  /**
   * Present ONLY on a past month's view (`financialsApi.wipAsOf`): the contract,
   * estimate and POs are what the month-end snapshot froze on `frozenAt`, and cost is
   * counted through `through` from the GL as it stands today, so an entry posted later
   * but dated inside the month is included. Absent on the live tab.
   *
   * `closed` is set once accounting has closed the month: the figures are then the
   * final ones stored at that moment, no longer re-read from the GL. Null while open.
   */
  asOf?: {
    period: string; through: string; frozenAt: string;
    closed?: { at: string; by: string | null } | null;
  } | null;
}
export interface PortfolioWipJob {
  projectId: number; jobNumber: string; title: string; division: string;
  revised: number; billed: number; budget: number; actual: number; eac: number;
  pctComplete: number; earned: number;
  projectedProfit: number; projectedMarginPct: number;
  /** Job-allocated indirect actuals (GL 59998). Shown as its own column so the
   *  indirect burden per job is visible, not just its effect on super margin. */
  indirectActual: number;
  superProfit: number; superMarginPct: number;   // margin before allocated indirects (GL 59998)
  estimateProfit: number; estimateMarginPct: number;
  fadeDollars: number; fadePts: number;
  overUnderBilled: number; leftToBill: number;
  health: 'green' | 'orange' | 'red';
  /** Month view only: the job had no synced estimate then, so its estimate profit, fade and
   *  health are not known. Its fade and estimate figures here are neutral (no fade), and the
   *  page and export show them as missing instead of as numbers. Absent on the live roll-up. */
  noEstimate?: boolean;
  /** Month view only: the project has been deleted since. It is still counted in that month, so
   *  it shows (without a link, which would open a job that no longer exists). */
  deletedSince?: boolean;
}
/** A Sage contract with no matching project in the tracker, so the WIP table
 *  can't show it. Surfaced for reconciliation, not as a WIP row. */
export interface UnreconciledContract {
  jobNumber: string; name: string; customerName: string; status: string;
  revised: number; billed: number; leftToBill: number;
  /** YYYY-MM-DD of the most recent GL cost, or null if it never costed. */
  lastCost: string | null;
  /** Unbilled AND still costing — most likely a job that belongs in the tracker. */
  recentCost: boolean;
}
export interface PortfolioWip {
  jobs: PortfolioWipJob[];
  /** Contracts outside the tracker. `contracts`/`revised` cover ALL of them so
   *  the gap against Sage is explainable; `jobs` lists only those with an
   *  unbilled balance, which are the financially live ones. */
  unreconciled: {
    contracts: number; revised: number; leftToBill: number; openContracts: number;
    recentDays: number; jobs: UnreconciledContract[];
  };
  provenance: { actualEnabled: boolean; budgetEnabled: boolean; syncedAt: string | null };
}
/**
 * One job in a past month's portfolio view. `model` is the RAW as-of model, the same facts the
 * job's own tab gets for that month; the figures are computed from it client-side by
 * `lib/portfolioMonth`, so the portfolio and the job tab cannot disagree.
 */
export interface PortfolioAsOfJob {
  projectId: number; jobNumber: string; title: string; division: string;
  /** The project has been deleted since the month; its frozen figures still count in it. */
  deleted: boolean;
  /** True when these are the figures Close stored; false when re-cut from the freeze and the GL. */
  final: boolean;
  /** When the month-end freeze behind the contract, estimate and POs was taken. */
  frozenAt: string;
  /** False when the job had no synced estimate at month end: its budget is MISSING, not zero, so
   *  anything measured against it (estimate profit, fade, health) is meaningless. */
  budgetSynced: boolean;
  model: Pick<FinancialsWip, 'contract' | 'rows' | 'forecast' | 'budgetAdj' | 'indirectActual' | 'jobCompleted'>;
}
export interface PortfolioAsOf {
  period: string;
  /** Last day cost is counted through, 'YYYY-MM-DD'. */
  through: string;
  /** Set once accounting has closed the month. */
  closed: { at: string; by: string | null } | null;
  jobs: PortfolioAsOfJob[];
  /** Jobs that have a freeze for the month which could not be read at cost-code level. */
  skipped: Array<{ projectId: number; jobNumber: string; reason: string }>;
  provenance: { actualEnabled: boolean; budgetEnabled: boolean };
}
/** One job in a roll-up family. */
export interface FamilyMember { projectId: number; jobNumber: string; title: string }
/** Where a job sits in a roll-up family: a parent with children, a child with its parent, or neither. */
export interface ProjectFamily {
  role: 'parent' | 'child' | null;
  self: FamilyMember | null;
  parent: FamilyMember | null;
  children: FamilyMember[];
}
/** One job in a parent's roll-up: its RAW model, live or as of a month. See `lib/rollUp`. */
export interface RollUpMember {
  projectId: number; jobNumber: string; title: string; role: 'parent' | 'child';
  /** A past month only: the job was deleted after that month, and the month still counts it. */
  deleted?: boolean;
  /** A past month only: true when these are the figures Close stored, false when re-cut. Null live. */
  final: boolean | null;
  /** A past month only: when the month-end freeze behind the contract, estimate and POs was taken. */
  frozenAt: string | null;
  /** False when the job had no synced estimate: its budget is missing, not zero. */
  budgetSynced: boolean;
  model: PortfolioAsOfJob['model'];
}
export interface RollUp {
  /** Present for a past month (`?asOf=`), null live. */
  asOf: { period: string; through: string; closed: { at: string; by: string | null } | null } | null;
  /** The parent first, then its children by job number. */
  members: RollUpMember[];
  /** Children that are not in this view, with why (a past month only: no freeze, or one that cannot be read). */
  missing: Array<{ projectId: number; jobNumber: string; title: string; role: 'parent' | 'child'; reason: string }>;
  /** Children the caller cannot see: counted, never named. */
  hidden: number;
  provenance: {
    actualEnabled: boolean; budgetEnabled: boolean;
    actualSyncedAt: string | null; budgetSyncedAt: string | null; committedSyncedAt: string | null;
  };
}
/** One frozen snapshot as stored. `payload` is the raw stored JSON in one of two
 *  shapes — the raw WIP model (drillable to cost code) or an already-derived
 *  roll-up row — normalised by `lib/wipHistory.ts`. */
export interface WipSnapshot {
  id: number;
  asOf: string;
  /** 'YYYY-MM' — the accounting month the snapshot DESCRIBES, not necessarily
   *  the month it was written in (the month-end freeze runs on the 1st). */
  period: string;
  source: 'forecast' | 'portfolio' | 'month-end' | 'month-close' | 'manual' | null;
  by: string | null;
  /** How many snapshots exist in this period; only this one is returned. */
  inPeriod: number;
  payload: unknown;
}
export interface WipHistoryResponse { snapshots: WipSnapshot[]; }

/** A month with a month-end freeze, and whether accounting has closed it. */
export interface FinancialsPeriod {
  period: string;
  /** Jobs that have a month-end freeze for it. */
  jobs: number;
  closed: { at: string; by: string | null; jobsFrozen: number | null } | null;
}
export interface FinancialsPeriodsResponse {
  /** False until the Close migration has been applied; Close is then unavailable. */
  closeReady: boolean;
  periods: FinancialsPeriod[];
}
export interface CloseMonthResult { period: string; jobs: number; frozen: number; unreadable: number; closedAt: string }

/** One GL cost entry — the grain behind Actual cost. `costType` is the raw
 *  Intacct COSTTYPEID on the entry itself, which is NOT necessarily the cost
 *  type group the tab files the code under (the group comes from the estimate's
 *  intent for that cost code). Seeing both is the point of the drill-down. */
export interface GlCostLine {
  recordNo: string;
  costCode: string | null;
  costCodeName: string | null;
  costType: string | null;
  /** The cost group the Tracker files this entry under (Labor, Material, Subcontract, Equipment, Other), from the
   *  server's own mapping. Absent from an API that predates the Actual-by-cost-type view: see `lib/glByGroup`. */
  group?: string;
  accountNo: string | null;
  accountTitle: string | null;
  amount: number;
  entryDate: string | null;
  document: string | null;
  batchNo: string | null;
}
export interface GlDetailResponse { lines: GlCostLine[]; syncedAt: string | null; }

export const financialsApi = {
  wip: (projectId: number) =>
    api.get(`/projects/${projectId}/financials`).then((r) => r.data as FinancialsWip),
  /** The same shape for a PAST month, 'YYYY-MM' (read-only). See `FinancialsWip.asOf`. */
  wipAsOf: (projectId: number, period: string) =>
    api.get(`/projects/${projectId}/financials`, { params: { asOf: period } }).then((r) => r.data as FinancialsWip),
  glDetail: (projectId: number) =>
    api.get(`/projects/${projectId}/financials/gl-detail`).then((r) => r.data as GlDetailResponse),
  /** Where this job sits in a roll-up family. */
  family: (projectId: number) =>
    api.get(`/projects/${projectId}/financials/family`).then((r) => r.data as ProjectFamily),
  /** A parent job with the jobs that roll up to it: each member's raw model, live or as of `period` ('YYYY-MM'). */
  rollUp: (projectId: number, period?: string) =>
    api.get(`/projects/${projectId}/financials/roll-up`, { params: period ? { asOf: period } : undefined }).then((r) => r.data as RollUp),
  history: (projectId: number) =>
    api.get(`/projects/${projectId}/financials/history`).then((r) => r.data as WipHistoryResponse),
  saveForecast: (projectId: number, ctc: Record<string, number>) =>
    api.post(`/projects/${projectId}/financials/forecast`, { ctc }).then((r) => r.data as FinancialsWip),
  /** Replace one cost code's buildup. An empty `lines` array clears it, handing
   *  the code back to its typed override / the default (remaining budget, or $0
   *  on a Completed job). */
  saveCtcLines: (projectId: number, costCode: string, lines: CtcLineInput[]) =>
    api.post(`/projects/${projectId}/financials/ctc-lines`, { costCode, lines }).then((r) => r.data as CtcLine[]),
  transfer: (projectId: number, dto: { fromCode: string; toCode: string; amount: number; reason?: string }) =>
    api.post(`/projects/${projectId}/financials/transfer`, dto).then((r) => r.data as FinancialsWip),
  /** Months with a month-end freeze and which of them accounting has closed. */
  periods: () => api.get('/financials/periods').then((r) => r.data as FinancialsPeriodsResponse),
  /** Accounting closes a month: every job's final figures are stored, then it is marked closed. */
  closeMonth: (period: string) =>
    api.post(`/financials/periods/${period}/close`).then((r) => r.data as CloseMonthResult),
  reopenMonth: (period: string) =>
    api.post(`/financials/periods/${period}/reopen`).then((r) => r.data as { period: string; reopenedAt: string }),
  portfolio: () => api.get('/financials/portfolio').then((r) => r.data as PortfolioWip),
  /** Every job as of the end of a PAST month, 'YYYY-MM' (read-only). See `PortfolioAsOfJob`. */
  portfolioAsOf: (period: string) =>
    api.get(`/financials/portfolio/as-of/${period}`).then((r) => r.data as PortfolioAsOf),
  /** Every job's saved accrual lines for a month, 'YYYY-MM': the Accruals sheet of the month export. */
  portfolioAccruals: (period: string) =>
    api.get(`/financials/portfolio/accruals/${period}`).then((r) => r.data as PortfolioAccruals),
  snapshotPortfolio: () => api.post('/financials/portfolio/snapshot').then((r) => r.data as { count: number }),
};

// ─── Commitments (budget / committed / spent + buyout planning; replaces Cost) ─
export interface CommitmentRow {
  costCode: string | null;
  costCodeName: string | null;
  costTypeGroup: string;
  budgeted: boolean;
  budget: number;
  committed: number;
  spent: number;
  planned: number;
  plannedVendor: string | null;
  plannedNote: string | null;
  planStale: boolean;   // a commitment landed since the plan was saved → reduce it
}
export interface CommitmentLine {
  recordNo: number;
  docNo: string | null;
  docType: string;            // 'PO' | 'Subcontract'
  vendor: string | null;
  costCode: string | null;
  amount: number;
  descr: string | null;
  docLabel: string | null;
}
export interface Commitments {
  rows: CommitmentRow[];
  lines: CommitmentLine[];
  provenance: { actualEnabled: boolean; committedSyncedAt: string | null };
}
export interface BuyoutPlanEntry { costCode: string; plannedAmount: number; vendor?: string | null; note?: string | null; }
export const commitmentsApi = {
  get: (projectId: number) =>
    api.get(`/projects/${projectId}/commitments`).then((r) => r.data as Commitments),
  savePlan: (projectId: number, plans: BuyoutPlanEntry[]) =>
    api.post(`/projects/${projectId}/commitments/plan`, { plans }).then((r) => r.data as Commitments),
};

// ─── Month-end accruals (Commitments ▸ Accruals) ────────────────────────────
// The PM's schedule from the project review sheet. A schedule only: none of it
// reaches Actual, cost-to-complete or EAC. Mirrors apps/api financials/lib/accruals.
export type AccrualKind = 'material' | 'equipment' | 'subcontract';
export type AccrualUnit = 'day' | 'week' | 'month';
/** What the client posts for one line. No id and no total: a save replaces the
 *  whole month and the total is the server's to compute. Exactly these keys —
 *  the API refuses any it does not know. */
export interface AccrualLineInput {
  kind: AccrualKind;
  vendor: string | null;
  costCode: string | null;
  description: string | null;
  carryOver: number | null;
  newAccrual: number | null;
  unit: AccrualUnit | null;
  unitCost: number | null;
  units: number | null;
  contractValue: number | null;
  /** Percent points: 65.5 = 65.5%. */
  pctComplete: number | null;
  pctBilled: number | null;
}
export interface AccrualLine extends AccrualLineInput {
  id: number;
  /** Derived on every read, never stored. */
  total: number;
  /** Last month's total for a matching Material line; null otherwise. */
  prevTotal: number | null;
}
export interface AccrualTotals { material: number; equipment: number; subcontract: number; total: number; }
export interface AccrualPeriodSummary extends AccrualTotals { period: string; lines: number; }
export interface Accruals {
  /** 'YYYY-MM', the accounting month these lines are FOR. */
  period: string;
  lines: AccrualLine[];
  totals: AccrualTotals;
  /** Fingerprint of this month's saved lines. Send it back as `expectedVersion`
   *  when saving: the API refuses (409) if the month has changed since, so a
   *  stale screen cannot replace someone else's newer save. */
  version: string;
  /** The calendar month before `period`; null when nothing was saved for it. */
  previous: AccrualPeriodSummary | null;
  /** Every month with saved lines, newest first. */
  periods: AccrualPeriodSummary[];
  savedAt: string | null;
  savedBy: string | null;
}
/** One saved accrual line with the job it belongs to, for the all-jobs month export (Accruals sheet). */
export interface PortfolioAccrualLine extends AccrualLineInput {
  projectId: number;
  /** Null only for a line whose project row no longer exists. */
  jobNumber: string | null;
  title: string | null;
  /** The job is no longer in the Tracker (deleted since, or its row is gone): the line is kept out of `totals`. */
  deleted: boolean;
  /** Derived on every read, never stored. */
  total: number;
  savedAt: string;
  savedBy: string | null;
}
export interface PortfolioAccruals {
  period: string;
  /** False on a database without the accrual table: there is nothing to list. */
  ready: boolean;
  /** How many jobs still in the Tracker have saved lines for the month. */
  jobs: number;
  /** Every saved line, deleted jobs' included (marked `deleted`). */
  lines: PortfolioAccrualLine[];
  /** Lines of jobs still in the Tracker only. */
  totals: AccrualTotals;
  /** The same two figures for jobs no longer in the Tracker, kept apart from `totals`. */
  deletedJobs: number;
  deletedTotals: AccrualTotals;
}
export const accrualsApi = {
  get: (projectId: number, period: string) =>
    api.get(`/projects/${projectId}/accruals`, { params: { period } }).then((r) => r.data as Accruals),
  /** Replaces the whole month. An empty `lines` array clears it. `expectedVersion`
   *  is the `version` the month was loaded with; a 409 means it has changed since. */
  save: (projectId: number, period: string, expectedVersion: string, lines: AccrualLineInput[]) =>
    api.post(`/projects/${projectId}/accruals`, { period, expectedVersion, lines }).then((r) => r.data as Accruals),
};

export const qaqcApi = {
  // projectId omitted = company-wide (the top-level QA/QC roll-up page).
  listPourCards: (projectId?: number) =>
    api.get('/qaqc/pour-cards', { params: projectId !== undefined ? { projectId } : {} }).then((r) => r.data as PourCard[]),
  listInspections: (projectId?: number) =>
    api.get('/qaqc/inspections', { params: projectId !== undefined ? { projectId } : {} }).then((r) => r.data as QaInspection[]),
  confirm: (id: number) => api.post(`/qaqc/pour-cards/${id}/confirm`).then((r) => r.data as PourCard),
  markPoured: (id: number) => api.post(`/qaqc/pour-cards/${id}/poured`).then((r) => r.data as PourCard),
  cancel: (id: number) => api.post(`/qaqc/pour-cards/${id}/cancel`).then((r) => r.data as PourCard),
  markCovered: (id: number) => api.post(`/qaqc/pour-cards/${id}/covered`).then((r) => r.data as PourCard),
  linkMaterial: (id: number, materialId: number | null) =>
    api.patch(`/qaqc/pour-cards/${id}/link`, { materialId }).then((r) => r.data as PourCard),
  // Office-entered order that no Pre-Pour Checklist will ever arrive for.
  createPourCard: (dto: { projectId: number; pourType: string; alreadyConfirmed: boolean } & Record<string, string | number | boolean | null>) =>
    api.post('/qaqc/pour-cards', dto).then((r) => r.data as PourCard),
  // Office typo-fix for card fields; every change lands in the audit trail.
  updatePourCard:(id: number, dto: Record<string, string | null>) =>
    api.patch(`/qaqc/pour-cards/${id}`, dto).then((r) => r.data as PourCard),
  pourCardEdits: (id: number) =>
    api.get(`/qaqc/pour-cards/${id}/edits`).then((r) => r.data as PourCardEdit[]),
  // Office fix for typo'd job numbers on the form (unmatched/mis-matched cards).
  assignProject: (id: number, projectId: number) =>
    api.patch(`/qaqc/pour-cards/${id}/assign-project`, { projectId }).then((r) => r.data as PourCard),
  assignInspectionProject: (id: number, projectId: number) =>
    api.patch(`/qaqc/inspections/${id}/assign-project`, { projectId }).then((r) => r.data as QaInspection),
  // Unscoped project list for the Assign-to-job picker.
  projectOptions: () =>
    api.get('/qaqc/project-options').then((r) => r.data as { id: number; jobNumber: string; title: string }[]),

  // ── Approved mix designs (PM-owned register; writes need projects.edit) ──
  listMixDesigns: (projectId?: number, includeRetired = false) =>
    api.get('/qaqc/mix-designs', {
      params: { ...(projectId !== undefined ? { projectId } : {}), ...(includeRetired ? { includeRetired: 'true' } : {}) },
    }).then((r) => r.data as MixDesign[]),
  createMixDesign: (dto: MixDesignInput) =>
    api.post('/qaqc/mix-designs', dto).then((r) => r.data as MixDesign),
  updateMixDesign: (id: number, dto: Partial<Omit<MixDesignInput, 'projectId'>>) =>
    api.patch(`/qaqc/mix-designs/${id}`, dto).then((r) => r.data as MixDesign),
  retireMixDesign: (id: number) =>
    api.post(`/qaqc/mix-designs/${id}/retire`).then((r) => r.data as MixDesign),
  reinstateMixDesign: (id: number) =>
    api.post(`/qaqc/mix-designs/${id}/reinstate`).then((r) => r.data as MixDesign),

  // ── The job's concrete supplier & blanket POs (estimator raises the PO, supplier varies by job; writes need projects.edit) ──
  // Null when nothing has been recorded — the card distinguishes that from blank.
  getConcreteSetup: (projectId: number) =>
    api.get('/qaqc/concrete-setup', { params: { projectId } }).then((r) => r.data as ConcreteSetup | null),
  saveConcreteSetup: (projectId: number, dto: Partial<Omit<ConcreteSetup, 'projectId' | 'updatedAt' | 'updatedBy'>>) =>
    api.put('/qaqc/concrete-setup', dto, { params: { projectId } }).then((r) => r.data as ConcreteSetup),
};

export interface MixDesign {
  id: number;
  projectId: number;
  /** As the PM typed it. */
  mixId: string;
  /** Normalised match key (A-Z0-9, upper-case) — server-computed. */
  mixKey: string;
  description: string | null;
  strengthPsi: number | null;
  supplier: string | null;
  submittalId: number | null;
  submittalNumber: string | null;
  submittalTitle: string | null;
  approvedOn: string | null;
  /** Retired mixes stay for history so old pours don't lose their match. */
  isActive: boolean;
  retiredAt: string | null;
  notes: string | null;
  createdAt: string;
  createdBy: string | null;
  updatedAt: string;
  updatedBy: string | null;
}

export interface MixDesignInput {
  projectId: number;
  mixId: string;
  description?: string | null;
  strengthPsi?: number | null;
  supplier?: string | null;
  submittalId?: number | null;
  approvedOn?: string | null;
  notes?: string | null;
}

// ─── RFIs (UI overhaul Phase 3 — per-project) ───────────────────────────────

/** Who an RFI or submittal send went to, as confirmed (#598 step 2). */
export interface EmailRecipient {
  kind: 'to' | 'cc';
  email: string;
  name: string | null;
  /** The job contact they were picked as, if any. */
  contactId: number | null;
}

/** An RFI's assignee or a submittal's reviewer picked from the job's contacts, as their book entry reads now. */
export interface PickedContact {
  id: number;
  name: string | null;
  email: string | null;
  companyName: string | null;
  /** Still in the address book and on this job. Someone taken off is still who it was assigned to, but no new draft starts addressed to them. */
  onJob: boolean;
}

export interface Rfi {
  id: number;
  projectId: number;
  rfiNumber: string;
  subject: string;
  specSection: string | null;
  question: string | null;
  ballInCourt: string | null;
  status: string; // open | answered | closed
  dueDate: string | null;
  response: string | null;
  respondedAt: string | null;
  respondedBy: string | null;
  assignee: string | null;
  /** The job contact it's assigned to, or null when "Assigned to" is a role label (#598 step 2). */
  assigneeContact?: PickedContact | null;
  priority: string | null;
  notifiedAt: string | null; // when the question was sent to the assignee, as confirmed (#608)
  /** Who the send went to, as confirmed; [] when not sent or not recorded (#598 step 2). */
  notifiedTo?: EmailRecipient[];
  createdAt: string;
  updatedAt: string;
}

export const rfisApi = {
  list: (params: { projectId: number }) =>
    api.get('/rfis', { params }).then((r) => r.data as Rfi[]),
  get: (id: number) => api.get(`/rfis/${id}`).then((r) => r.data as Rfi),
  create: (data: unknown) => api.post('/rfis', data).then((r) => r.data as Rfi),
  update: (id: number, data: unknown) =>
    api.patch(`/rfis/${id}`, data).then((r) => r.data as Rfi),
  respond: (id: number, body: { response: string }) =>
    api.post(`/rfis/${id}/respond`, body).then((r) => r.data as Rfi),
  // Mark as sent → notified_at = sentAt (status stays 'open'). Called AFTER the
  // Outlook draft, on confirmation — never when it opens (#608). `sentAt` is
  // Eastern wall-clock 'YYYY-MM-DDTHH:mm' (see modules/projects/lib/sentAt).
  // `recipients` is who the draft was addressed to (#598 step 2); left out when the
  // send is marked without a draft from the app.
  sendToAssignee: (id: number, sentAt?: string, recipients?: EmailRecipient[]) =>
    api.post(`/rfis/${id}/send`, { sentAt, ...(recipients?.length ? { recipients } : {}) }).then((r) => r.data as Rfi),
  // Correct the recorded sent time; null = it was never sent. Logged old → new.
  // `expectedNotifiedAt` is the notifiedAt the dialog was OPENED with: if the RFI
  // no longer holds it (someone else changed it meanwhile) the API answers 409
  // instead of overwriting their save.
  correctSentAt: (id: number, sentAt: string | null, expectedNotifiedAt?: string | null) =>
    api.patch(`/rfis/${id}/sent-at`, { sentAt, expectedNotifiedAt }).then((r) => r.data as Rfi),
  // Reopen (from answered/closed) → status='open', responded_at cleared (response kept).
  reopen: (id: number) =>
    api.post(`/rfis/${id}/reopen`).then((r) => r.data as Rfi),
  // Activity timeline (who-did-what-when), oldest first.
  activity: (id: number) =>
    api.get(`/rfis/${id}/activity`).then((r) => r.data as ActivityEvent[]),
  /**
   * The RFI on the company's Excel form (#645), made on the server from the template so the form keeps its styling.
   * `fileName` is the house name the API gives it ("<job #> - RFI <##> - <Subject> <yyyy.mm.dd>.xlsx"). A refusal's
   * message is read out of the blob (`blobRefusal`).
   */
  exportForm: async (id: number): Promise<{ blob: Blob; fileName: string | null }> => {
    const r = await api.get(`/rfis/${id}/export`, { responseType: 'blob', validateStatus: () => true });
    if (r.status >= 200 && r.status < 300) {
      return { blob: r.data as Blob, fileName: fileNameFromDisposition(r.headers?.['content-disposition'] as string | undefined) };
    }
    throw await blobRefusal(r, `The RFI form could not be made (HTTP ${r.status}).`);
  },
};

// ─── Activity log (generic who-did-what-when; shared across entities) ────────
// One event in an entity's timeline. Keyed server-side by (entityType, entityId)
// so submittals, RFIs, and future domains all share this shape + the
// <ActivityTimeline> component.
export interface ActivityEvent {
  id: number;
  action: string;
  detail: string | null;
  createdAt: string;
  actor: { id: string; displayName: string | null; email: string | null } | null;
}

/**
 * One event in a PROJECT's feed. Extends the per-entity shape with which record
 * it happened to, so the card can show an "RFI-004" chip and link to the tab —
 * a job's feed spans every domain, unlike an entity's own timeline.
 *
 * (Not to be confused with `Activity`, further down, which is an MS Project
 * schedule task.)
 */
export interface ProjectActivityEvent extends ActivityEvent {
  entityType: string;
  entityId: number;
  /** Human handle for the record ("RFI-004"), or null when it has none. */
  entityLabel: string | null;
}

/** One page of a project's activity feed. `hasMore` drives "Show more". */
export interface ProjectActivityPage {
  events: ProjectActivityEvent[];
  hasMore: boolean;
}

// ─── Attachments (generic file links; files live in SharePoint) ─────────────
// One attachment row. The file itself lives in the project's Job Folder on
// SharePoint; the tracker stores only the link + metadata. Keyed server-side by
// (entityType, entityId) so RFIs, submittals, and future domains all share this
// shape + the <FileAttachments> component.
// What a submittal's file is on its review round: 'submitted' is the package that went
// out for review (Rev 0: the original from the subcontractor), 'returned' is the
// reviewer's marked-up copy.
export type SubmittalFileKind = 'submitted' | 'returned';

export interface Attachment {
  id: number;
  fileName: string;
  webUrl: string;
  sizeBytes: number | null;
  /** Submittal files only: the review round the file is on. null for everything else. */
  roundId: number | null;
  kind: SubmittalFileKind | null;
  uploadedAt: string;
  uploadedBy: { id: string; displayName: string | null; email: string | null } | null;
}

export const attachmentsApi = {
  list: (entityType: 'rfi' | 'submittal' | 'daily-log', entityId: number) =>
    api.get('/attachments', { params: { entityType, entityId } }).then((r) => r.data as Attachment[]),
  create: (body: {
    entityType: 'rfi' | 'submittal' | 'daily-log'; entityId: number; fileName: string; webUrl: string; sizeBytes?: number;
    // Submittal files: both or neither.
    roundId?: number; kind?: SubmittalFileKind;
  }) =>
    api.post('/attachments', body).then((r) => r.data as Attachment),
  // Soft-delete (un-link). Does NOT remove the file from SharePoint.
  //
  // ⚠️ A 404 RESOLVES rather than rejecting, so the delete is idempotent to
  // callers. The API 404s both when the link is already gone (someone else
  // removed it after this list loaded) and when it belongs to a job the caller
  // cannot see — deliberately indistinguishable, to keep DELETE from being an
  // existence oracle for attachment ids. In BOTH cases the right UI outcome is
  // "it isn't there", not an error banner, and the callers' refetch reconciles
  // the list. Normalized HERE rather than in each component so a third caller
  // can't reintroduce the spurious error (review, #301).
  remove: (id: number) =>
    api.delete(`/attachments/${id}`)
      .then((r) => r.data)
      .catch((err) => {
        if (isNotFoundError(err)) return { id, removed: false };
        throw err;
      }),
};

// ─── Submittals (UI overhaul Phase 3 — per-project) ─────────────────────────

export interface Submittal {
  id: number;
  projectId: number;
  submittalNumber: string | null;
  title: string;
  specSection: string | null;
  reviewer: string | null;
  /** The job contact reviewing it, or null when "Reviewer" is a role label (#598 step 2). */
  reviewerContact?: PickedContact | null;
  ballInCourt: string | null;
  status: string; // queued | in-review | resubmit | approved | closed
  reviewDue: string | null;
  daysInReview: number | null; // COMPUTED: null until notified, then live, frozen at decidedAt
  reviewWindowDays: number; // EFFECTIVE "of N" window: COALESCE(manual override, auto-derived span, 14)
  reviewWindowManual: number | null; // raw override; null = auto-derived from the dates
  decidedAt: string | null;
  submittalType: string | null;
  receivedAt: string | null;
  notifiedAt: string | null;
  decision: string | null;
  createdAt: string;
  updatedAt: string;
}

// A review decision outcome (POST /submittals/:id/decide).
export type SubmittalDecideOutcome = 'approved' | 'approved-as-noted' | 'revise-resubmit' | 'rejected';

// One review round of a submittal. Rev 0 is the original package; each Revise &
// Resubmit or Rejected that comes back leads to the next on the same submittal.
export interface SubmittalRound {
  id: number;
  submittalId: number;
  revision: number; // 0 = the original package
  receivedAt: string | null; // a calendar date: the day the package came in from the sub
  sentAt: string | null; // a moment: sent to the reviewer
  returnedAt: string | null; // a moment: came back from the reviewer
  outcome: SubmittalDecideOutcome | null;
  note: string | null;
  /** Who this round's send went to, as confirmed; [] when not sent or not recorded (#598 step 2). */
  sentTo?: EmailRecipient[];
  createdAt: string;
  updatedAt: string;
}

export const submittalsApi = {
  list: (params: { projectId: number }) =>
    api.get('/submittals', { params }).then((r) => r.data as Submittal[]),
  get: (id: number) => api.get(`/submittals/${id}`).then((r) => r.data as Submittal),
  create: (data: unknown) => api.post('/submittals', data).then((r) => r.data as Submittal),
  update: (id: number, data: unknown) =>
    api.patch(`/submittals/${id}`, data).then((r) => r.data as Submittal),
  // Mark as sent → status=in-review, ball=Reviewer, notified_at = sentAt. Called AFTER
  // the Outlook draft, on confirmation — never when it opens (#626). `sentAt` is Eastern
  // wall-clock 'YYYY-MM-DDTHH:mm' (see modules/projects/lib/sentAt); omitted means now.
  // 409 when it is already marked sent or its round has come back.
  sendToReviewer: (id: number, sentAt?: string, recipients?: EmailRecipient[]) =>
    api.post(`/submittals/${id}/send-to-reviewer`, { sentAt, ...(recipients?.length ? { recipients } : {}) })
      .then((r) => r.data as Submittal),
  // Send Revision (from 'resubmit') → opens the next review round and re-opens review:
  // status=in-review, ball=Reviewer, notified_at = sentAt, decided_at cleared,
  // review_due = 14 days after the Eastern day it was sent. Called on confirmation, like
  // sendToReviewer. `receivedAt` is the day the corrected package came back from the sub
  // (YYYY-MM-DD); the server defaults to the day it was sent. `reviewDue` is the due date
  // the Outlook draft quoted: the server keeps it when it is on or after the day it was
  // sent, so the tracker holds the date the reviewer was told. Resolves with the submittal
  // plus the revision just opened. 409 from any other status.
  sendRevision: (id: number, body: { receivedAt?: string; sentAt?: string; reviewDue?: string; recipients?: EmailRecipient[] } = {}) =>
    api.post(`/submittals/${id}/send-revision`, body).then((r) => r.data as Submittal & { revision: number }),
  // Correct the recorded sent time, or clear it (null: never sent — first review, no
  // decision only). `expectedNotifiedAt` is the notifiedAt the dialog was OPENED with: if
  // the submittal no longer holds it, someone else changed it and this is a 409.
  correctSentAt: (id: number, sentAt: string | null, expectedNotifiedAt?: string | null) =>
    api.patch(`/submittals/${id}/sent-at`, { sentAt, expectedNotifiedAt }).then((r) => r.data as Submittal),
  // Every review round, Rev 0 first.
  rounds: (id: number) =>
    api.get(`/submittals/${id}/rounds`).then((r) => r.data as SubmittalRound[]),
  // Log a review decision → status/ball/due/decision + decided_at stamped.
  decide: (id: number, body: { outcome: SubmittalDecideOutcome; note?: string }) =>
    api.post(`/submittals/${id}/decide`, body).then((r) => r.data as Submittal),
  // Set/reset the "of N" review window. days=number → manual override; null → auto-derive.
  setReviewWindow: (id: number, days: number | null) =>
    api.patch(`/submittals/${id}/review-window`, { days }).then((r) => r.data as Submittal),
  // Activity timeline (who-did-what-when), oldest first.
  activity: (id: number) =>
    api.get(`/submittals/${id}/activity`).then((r) => r.data as ActivityEvent[]),
  /**
   * The submittal on the company's Excel transmittal (#646), made on the server from the template so the form keeps
   * its styling and checkboxes. `fileName` is the house name the API gives it ("<job #> - Submittal <###> - <Title>
   * <yyyy.mm.dd>.xlsx"). A refusal's message is read out of the blob (`blobRefusal`).
   */
  exportForm: async (id: number): Promise<{ blob: Blob; fileName: string | null }> => {
    const r = await api.get(`/submittals/${id}/export`, { responseType: 'blob', validateStatus: () => true });
    if (r.status >= 200 && r.status < 300) {
      return { blob: r.data as Blob, fileName: fileNameFromDisposition(r.headers?.['content-disposition'] as string | undefined) };
    }
    throw await blobRefusal(r, `The submittal form could not be made (HTTP ${r.status}).`);
  },
};

// ─── Daily Logs (UI overhaul Phase 4 — per-project) ─────────────────────────

export interface DailyLogCrew {
  id?: number;
  company: string | null;
  trade: string | null;
  workers: number | null;
  hours: number | null;
  manhours?: number; // computed server-side (workers × hours) on detail rows
  sortOrder?: number;
}

export interface DailyLogWork {
  id?: number;
  area: string | null;
  narrative: string | null;
  sortOrder?: number;
}

export interface DailyLogQuantity {
  id?: number;
  item: string | null;
  qty: number | null;
  unit: string | null;
  sortOrder?: number;
}

export interface DailyLog {
  id: number;
  projectId: number;
  date: string;             // log_date (calendar date)
  status: string;          // draft | submitted
  author: string | null;   // author_name
  authorId: string | null;
  submittedAt: string | null;
  weatherCond: string | null;
  weatherHi: number | null;
  weatherLo: number | null;
  weatherPrecip: string | null;
  weatherWind: string | null;
  workSummary: string | null;
  delays: string | null;
  safetyNotes: string | null;
  createdAt: string;
  updatedAt: string;
  // List-only roll-ups
  totalWorkers?: number;
  totalManhours?: number;
  // Detail-only collections
  crews?: DailyLogCrew[];
  work?: DailyLogWork[];
  quantities?: DailyLogQuantity[];
}

// Header + collections sent on Save (draft). Collections fully replace existing
// child rows; omit a collection to leave it untouched.
export interface UpdateDailyLogInput {
  weatherCond?: string;
  weatherHi?: number;
  weatherLo?: number;
  weatherPrecip?: string;
  weatherWind?: string;
  workSummary?: string;
  delays?: string;
  safetyNotes?: string;
  crews?: Array<{ company?: string; trade?: string; workers?: number; hours?: number }>;
  work?: Array<{ area?: string; narrative?: string }>;
  quantities?: Array<{ item?: string; qty?: number; unit?: string }>;
  /** Confirmed QA/QC pour cards placed on this log date — saving flips each
   *  to Poured; actualQtyCy tracks yield against the pre-pour estimate. */
  pouredPourCards?: Array<{ id: number; actualQtyCy?: number }>;
}

// One row of the company-wide Daily Logs rollup (#617). The job is named by its
// job number, which is how everyone here refers to one.
export interface DailyLogRollupRow {
  id: number;
  projectId: number;
  jobNumber: string;
  projectTitle: string | null;
  date: string;
  status: string;          // draft | submitted
  author: string | null;
  weatherCond: string | null;
  weatherHi: number | null;
  weatherLo: number | null;
  workSummary: string | null; // first 300 characters only
  totalWorkers: number;
  totalManhours: number;
}

export interface DailyLogRollup {
  from: string;            // YYYY-MM-DD, the window actually applied
  to: string;
  truncated: boolean;      // more rows matched than `cap`; narrow the dates
  cap: number;
  rows: DailyLogRollupRow[];
}

export interface DailyLogRollupParams {
  from?: string;
  to?: string;
  projectId?: number;
  status?: 'draft' | 'submitted';
  q?: string;
}

export const dailyLogsApi = {
  list: (params: { projectId: number }) =>
    api.get('/daily-logs', { params }).then((r) => r.data as DailyLog[]),
  // Every job the caller can see. Omitted filters are left off the query string
  // so the server's defaults (last 30 days) apply.
  rollup: (params: DailyLogRollupParams = {}) =>
    api.get('/daily-logs/rollup', { params }).then((r) => r.data as DailyLogRollup),
  get: (id: number) => api.get(`/daily-logs/${id}`).then((r) => r.data as DailyLog),
  // Start Today's Log / resume → returns the existing draft or a new one.
  startToday: (body: { projectId: number; date?: string }) =>
    api.post('/daily-logs', body).then((r) => r.data as DailyLog),
  update: (id: number, data: UpdateDailyLogInput) =>
    api.patch(`/daily-logs/${id}`, data).then((r) => r.data as DailyLog),
  // Submit → locks the record + stamps author/timestamp.
  submit: (id: number) =>
    api.post(`/daily-logs/${id}/submit`).then((r) => r.data as DailyLog),
};

// ─── Site photos (the job's photo feed: the Photos tab and Daily Logs ▸ Photos) ─
// Files live in the Job Folder (▸ Daily Log Photos); the tracker stores links
// plus capture metadata. A post belongs to a site day (photoDate), which is how
// it lines up with that day's daily log.

export interface SitePhotoPerson { id: string; displayName: string | null; email: string | null }

export interface SitePhoto {
  id: number;
  fileName: string;
  webUrl: string;
  sizeBytes: number | null;
  width: number | null;
  height: number | null;
  /** When the photo was taken, read from the image. Null for older uploads. */
  takenAt: string | null;
  latitude: number | null;
  longitude: number | null;
  /** 'photo' = GPS in the image; 'device' = phone location at upload. */
  locationSource: 'photo' | 'device' | null;
  locationAccuracyM: number | null;
  uploadedAt: string;
}

export interface SitePhotoComment {
  id: number;
  postId: number;
  body: string;
  createdAt: string;
  author: SitePhotoPerson | null;
}

export interface SitePhotoPost {
  id: number;
  projectId: number;
  photoDate: string; // YYYY-MM-DD
  caption: string | null;
  createdAt: string;
  updatedAt: string | null;
  author: SitePhotoPerson | null;
  dailyLog: { id: number; status: string } | null;
  photos: SitePhoto[];
  comments: SitePhotoComment[];
}

export interface SitePhotoPage {
  posts: SitePhotoPost[];
  hasMore: boolean;
  authors: Array<{ id: string; displayName: string }>;
  /** Months (`YYYY-MM`, by site day) that have photos, newest first, with how
   *  many. Unfiltered, like `authors` (#665). */
  months: Array<{ month: string; photos: number }>;
  /** Whether the API would accept a post from this user on this job — the
   *  server's own rule, so the screen never offers an upload it will refuse. */
  canPost: boolean;
  /** Whether this user may edit and remove their OWN posts here. The same rule
   *  without the activity lock, which blocks only new posts. */
  canManageOwn: boolean;
}

export interface SitePhotoInput {
  fileName: string;
  webUrl: string;
  sizeBytes?: number;
  width?: number;
  height?: number;
  takenAt?: string;
  latitude?: number;
  longitude?: number;
  locationSource?: 'photo' | 'device';
  locationAccuracyM?: number;
}

export const sitePhotosApi = {
  // q searches captions, comments and the poster's name (#665).
  list: (params: { projectId: number; authorId?: string; from?: string; to?: string; q?: string; offset?: number; limit?: number }) =>
    api.get('/site-photos', { params }).then((r) => r.data as SitePhotoPage),
  // clientRequestId: made once per batch and resent on retry, so a retry after
  // a lost response returns the first post instead of making a second.
  create: (body: { projectId: number; photoDate: string; caption?: string; clientRequestId?: string; photos: SitePhotoInput[] }) =>
    api.post('/site-photos/posts', body).then((r) => r.data as SitePhotoPost),
  update: (id: number, body: { caption?: string; photoDate?: string }) =>
    api.patch(`/site-photos/posts/${id}`, body).then((r) => r.data as SitePhotoPost),
  // Removing un-links; the files stay in SharePoint. A 404 means it's already
  // gone (or not visible) — same "it isn't there" outcome as attachmentsApi.remove.
  removePost: (id: number) =>
    api.delete(`/site-photos/posts/${id}`).then((r) => r.data).catch((err) => {
      if (isNotFoundError(err)) return { id, removed: false };
      throw err;
    }),
  removePhoto: (id: number) =>
    api.delete(`/site-photos/photos/${id}`).then((r) => r.data).catch((err) => {
      if (isNotFoundError(err)) return { id, removed: false };
      throw err;
    }),
  addComment: (postId: number, body: string) =>
    api.post(`/site-photos/posts/${postId}/comments`, { body }).then((r) => r.data as SitePhotoComment),
  removeComment: (id: number) =>
    api.delete(`/site-photos/comments/${id}`).then((r) => r.data).catch((err) => {
      if (isNotFoundError(err)) return { id, removed: false };
      throw err;
    }),
};

// ─── Weekly project report (#576 internal, #579 client) ─────────────────────
// Mirrors apps/api/src/weekly-report/lib/weekly-report.types.ts. Two report
// types on purpose: the client .docx builder takes only ClientWeeklyReport,
// which only the client endpoint returns, already cut to its allowlist.

export interface WeeklyReportWindow {
  weekStart: string;
  weekEnd: string;
  lookback: { from: string; to: string; weekStarts: [string] };
  lookahead: [string, string, string];
}

/** `fromJob`: the job fills this role from its own assignments, held or not; false for the template's blank roles. */
export interface WeeklyReportPerson { role: string; name: string | null; email: string | null; phone: string | null; fromJob: boolean }

export interface WeeklyReportPhoto {
  webUrl: string;
  fileName: string | null;
  width: number | null;
  height: number | null;
  takenAt: string | null;
}

export interface WeeklyReportPhotoPost { id: number; caption: string | null; author: string | null; photos: WeeklyReportPhoto[] }

export interface WeeklyReportLog {
  id: number;
  status: 'draft' | 'submitted';
  author: string | null;
  weather: { cond: string | null; hi: number | null; lo: number | null; precip: string | null; wind: string | null };
  workSummary: string | null;
  delays: string | null;
  safetyNotes: string | null;
  crews: { company: string | null; trade: string | null; workers: number | null; hours: number | null }[];
  work: { area: string | null; narrative: string | null }[];
  quantities: { item: string | null; qty: number | null; unit: string | null }[];
}

export interface WeeklyReportDay { date: string; log: WeeklyReportLog | null; photoPosts: WeeklyReportPhotoPost[] }

export interface WeeklyReportCarryPerson { role: string; name: string | null; phone: string | null; email: string | null }

/** The template's sections, as the export writes them and the reader finds them. */
export type WeeklyReportSectionKey = 'details' | 'customerTeam' | 'ewTeam' | 'progress' | 'lookahead'
  | 'materials' | 'manpower' | 'survey' | 'safety' | 'equipment' | 'logistics';

/** The narrative sections whose text carries over whole. */
export type WeeklyReportNarrative = 'materials' | 'survey' | 'safety' | 'logistics';

/** One block of a finalized report as filed: text, a table, or where its photos were (the Tracker's own photos show there). */
export type WeeklyReportFiledBlock =
  | { type: 'text'; text: string; style: 'heading' | 'bullet' | 'line'; date: string | null }
  | { type: 'table'; rows: string[][]; header: boolean }
  | { type: 'photos'; date: string | null };

export interface WeeklyReportFiledSection {
  /** As written in the file; null for anything above the first heading. */
  title: string | null;
  /** Which template section it is; null for one the PM added. */
  key: WeeklyReportSectionKey | null;
  blocks: WeeklyReportFiledBlock[];
}

/** A finalized report's whole text, section by section, as read from its Word file. */
export interface WeeklyReportFiled {
  sections: WeeklyReportFiledSection[];
  /** The file was longer than the Tracker keeps, so its end was cut. */
  truncated: boolean;
}

/** A section the PM added, carried whole after the template section it followed. */
export interface WeeklyReportExtraSection {
  title: string;
  after: WeeklyReportSectionKey | null;
  blocks: Extract<WeeklyReportFiledBlock, { type: 'text' | 'table' }>[];
}

/**
 * What the PM typed into the previous finalized report of the same copy. The
 * Tracker's own data wins wherever it has a value; this fills only what it
 * leaves blank, and brings rows, notes and sections the PM added.
 * Mirrors WeeklyReportCarryForward in apps/api/src/weekly-report/lib/weekly-report.types.ts.
 */
export interface WeeklyReportCarryForward {
  fromWeek: string;
  customerTeam: WeeklyReportCarryPerson[];
  /** Team rows for roles the job doesn't fill. */
  team: WeeklyReportCarryPerson[];
  /** Team rows for roles the job fills, where a phone or email was typed for its person. */
  jobTeam: WeeklyReportCarryPerson[];
  /** Project Details values typed differently from the Tracker, and rows the PM added (`added`: a further row with a template label). */
  details: { label: string; value: string; added: boolean }[];
  lookahead: { weekStart: string; lines: string[] }[];
  materials: string[];
  survey: string[];
  safety: string[];
  logistics: string[];
  tables: { section: WeeklyReportNarrative; rows: string[][]; header: boolean }[];
  /** Manpower rows with something typed: a note, or a count the Tracker didn't write. Internal only. */
  manpower: { weekStart: string | null; label: string; headcount: string | null; notes: string | null; added: boolean }[];
  /** Equipment lines the PM typed, not the Tracker's own. Internal only. */
  equipment: string[];
  extraSections: WeeklyReportExtraSection[];
}

export interface WeeklyReportBase {
  generatedAt: string;
  window: WeeklyReportWindow;
  project: {
    id: number;
    jobNumber: string | null;
    title: string | null;
    client: string | null;
    address: string | null;
    firstMobilization: string | null;
  };
  team: WeeklyReportPerson[];
  days: WeeklyReportDay[];
  carryForward: WeeklyReportCarryForward | null;
}

export interface InternalWeeklyReport extends WeeklyReportBase {
  version: 'internal';
  manpower: {
    payrollAvailable: boolean;
    payrollThrough: string | null;
    /** Whether last week's payroll has been pulled in full; the actual row is final only when complete. */
    lastWeek: { status: 'complete' | 'partial' | 'none'; coveredThrough: string | null };
    rows: { weekStart: string; basis: 'actual' | 'scheduled' | 'forecast'; headcount: number | null }[];
  };
  equipment: WeeklyReportEquipment[];
  /** This week's own finalized internal report, as filed (the tab's "After upload" view); null until one is uploaded. */
  filed: WeeklyReportFiledView | null;
}

export interface WeeklyReportEquipment { id: number; name: string; quantity: number | null; status: string | null; statusCode: string | null; onSiteFrom: string | null; endDate: string | null }

/** The report week's newest internal upload, and what was read from it. */
export interface WeeklyReportFiledView {
  uploadId: number;
  webUrl: string;
  fileName: string | null;
  uploadedAt: string;
  uploadedBy: string | null;
  /** Null for a report uploaded before the whole file was read; an editor can read it again. */
  content: WeeklyReportFiled | null;
  /** The last time an editor read the file again, so a later read isn't credited to the uploader. */
  readAgain: { by: string | null; at: string } | null;
}

export interface ClientWeeklyReport extends WeeklyReportBase {
  version: 'client';
}

export type WeeklyUploadKind = 'internal' | 'client';

export interface WeeklyUploadFile {
  id: number;
  webUrl: string;
  fileName: string | null;
  uploadedBy: string | null;
  uploadedAt: string;
  versions: number;
}

/** One week's finalized reports. Mirrors WeeklyUploadWeek in apps/api/src/weekly-report/lib/weekly-report.types.ts. */
export interface WeeklyUploadWeek {
  weekStart: string;
  done: boolean;
  flagged: boolean;
  open: boolean;
  closedReason: 'future' | 'previous-missing' | 'before-start' | null;
  blockedBy: string | null;
  internal: WeeklyUploadFile | null;
  client: WeeklyUploadFile | null;
  noReport: { reason: string; by: string | null; at: string } | null;
}

export interface WeeklyUploadsView {
  available: boolean;
  thisWeek: string;
  chainStart: string | null;
  weeks: WeeklyUploadWeek[];
}

/** What the browser read from an uploaded file; the server cleans it again before storing. */
export type WeeklyCarryInput = Omit<WeeklyReportCarryForward, 'fromWeek'>;

export const weeklyReportApi = {
  /** Each week's finalized uploads, flags and upload gate, for the strip's span. */
  uploads: (projectId: number, from: string, to: string) =>
    api.get(`/projects/${projectId}/weekly-report/uploads`, { params: { from, to } })
      .then((r) => r.data as WeeklyUploadsView),
  /** Record a finalized report already uploaded to the Job Folder. Needs projects.edit; refused out of order. */
  recordUpload: (projectId: number, body: { week: string; kind: WeeklyUploadKind; webUrl: string; fileName: string; carryForward?: WeeklyCarryInput; filed?: WeeklyReportFiled }) =>
    api.post(`/projects/${projectId}/weekly-report/uploads`, body).then((r) => r.data as { id: number; weekStart: string; kind: WeeklyUploadKind }),
  /** Replace what was read from an upload's file with a fresh read of it (an older upload, or a file edited in place). Needs projects.edit. */
  rereadUpload: (projectId: number, uploadId: number, body: { carryForward?: WeeklyCarryInput; filed: WeeklyReportFiled }) =>
    api.post(`/projects/${projectId}/weekly-report/uploads/${uploadId}/content`, body).then((r) => r.data as { id: number; weekStart: string }),
  /** Mark a week "No report this week" (demobilized, shutdown). Needs projects.edit; refused out of order. */
  markNoReport: (projectId: number, week: string, reason: string) =>
    api.post(`/projects/${projectId}/weekly-report/no-report`, { week, reason }).then((r) => r.data as { id: number; weekStart: string }),
  /** The internal report: the Weekly Report tab and the internal export. */
  internal: (projectId: number, week?: string) =>
    api.get(`/projects/${projectId}/weekly-report`, { params: week ? { week } : {} })
      .then((r) => r.data as InternalWeeklyReport),
  /** The client report, with only the ticked lookback days' daily logs. Needs projects.edit. */
  client: (projectId: number, week: string, days: string[]) =>
    api.get(`/projects/${projectId}/weekly-report/client`, { params: { week, ...(days.length ? { days: days.join(',') } : {}) } })
      .then((r) => r.data as ClientWeeklyReport),
};

// ─── Punch list (#518, #524, #526) ───────────────────────────────────────────
// One list per job: its stage and settings, items whose STATUS and BALL IN
// COURT change only through actions, and each item's history. Shapes mirror
// apps/api/src/punch/punch.service.ts.

export type PunchStage = 'internal' | 'published' | 'closed_out';
export type PunchStatus = 'new' | 'open' | 'rejected' | 'ready_for_review' | 'verified' | 'closed';
/** Whose move it is. 'assignee' = the item's assigned party (a sub, or the E&W crew). */
export type PunchBicType = 'ew' | 'assignee' | 'ao' | 'none';
export type PunchAction = 'send' | 'mark_fixed' | 'accept' | 'reject' | 'record_approval' | 'reopen' | 'pass_to';
export type PunchAssigneeInput = { type: 'sub'; name: string } | { type: 'ew_crew' };
export type PunchAssignee = PunchAssigneeInput | null;
export type PunchWall = 'north' | 'south' | 'east' | 'west';
export type PunchPriority = 'low' | 'normal' | 'high';

/** A photo the client has ALREADY uploaded to the Job Folder. Same shape as a site photo. */
export type PunchPhotoInput = SitePhotoInput;

export interface PunchMedia {
  id: number;
  kind: 'before' | 'after' | 'video';
  fileName: string;
  webUrl: string;
  sizeBytes: number | null;
  width: number | null;
  height: number | null;
  takenAt: string | null;
  latitude: number | null;
  longitude: number | null;
  locationSource: 'photo' | 'device' | null;
  locationAccuracyM: number | null;
  uploadedBy: string | null;
  /** A mark-fixed step relied on this photo; it can't be removed. */
  fixEvidence: boolean;
  uploadedAt: string;
}

/**
 * An item's pin on a drawing (#518 phase 3): one page of one revision of a
 * drawing set (drawing_sets.id), and where on it as fractions of the page
 * (0–1 from its top-left, as shown) — so it lands in the same place at any zoom.
 */
export interface PunchPin { drawingSetId: number; page: number; x: number; y: number }

export interface PunchItem {
  id: number;
  itemNo: number;
  label: string;                 // "#021"
  description: string;
  trade: string | null;
  assignee: PunchAssignee;
  assigneeLabel: string | null;
  status: PunchStatus;
  bic: { type: PunchBicType; userId: string | null; userName: string | null; label: string };
  room: string | null;
  wall: PunchWall | null;
  dueDate: string | null;        // YYYY-MM-DD
  overdue: boolean;
  priority: PunchPriority;
  origin: 'internal' | 'published';
  rejectReason: string | null;
  createdAt: string;
  createdBy: string | null;
  updatedAt: string | null;
  /** When the item last changed (its newest history step), and who changed it. */
  lastUpdatedAt: string | null;
  lastUpdatedBy: string | null;
  notes: string | null;
  /** The field leader in charge of the item, as named when picked. */
  fieldLeader: { id: string | null; name: string } | null;
  /** Where the item is on a drawing (#518 phase 3), or null. */
  pin: PunchPin | null;
  media: PunchMedia[];
}

export interface PunchEvent {
  id: number;
  action: string;                // 'created' | 'sent' | 'marked_fixed' | 'passed' | …
  statusFrom: PunchStatus | null;
  statusTo: PunchStatus | null;
  bicTo: { type: PunchBicType; userName: string | null; name: string | null; label: string } | null;
  reason: string | null;
  note: string | null;
  actor: string | null;
  createdAt: string;
}

export interface PunchList {
  id: number;
  projectId: number;
  stage: PunchStage;
  requiresAoApproval: boolean;
  architectName: string | null;
  ownerRep: string | null;
  dueMode: 'offset' | 'fixed';
  dueDays: number;
  dueDate: string | null;
  defaultPriority: PunchPriority;
  publishedAt: string | null;
  publishedBy: string | null;
  walkDate: string | null;
  walkAttendees: string | null;
  closedOutAt: string | null;
  closedOutBy: string | null;
  tradeSubs: { trade: string; subName: string }[];
}

/**
 * A trade as the job offers it: the standard ones (a hidden one is left out of
 * the pickers) and the job's own, whose key is its name.
 */
export interface PunchTrade { key: string; label: string; custom?: boolean; hidden?: boolean }

export interface PunchListResponse {
  access: { canWork: boolean; canManage: boolean };
  trades: PunchTrade[];
  rejectReasons: { key: string; label: string }[];
  ewCrewLabel: string;
  photoSubfolder: string;
  list: PunchList | null;
  items: PunchItem[];
}

/** Who did a sign-off step, and when. */
export interface PunchSignoff { by: string | null; at: string }

/**
 * An item's key sign-offs, from its last repair cycle (a reopen starts it
 * over; a rejection turns down the fix before it). Rejections count across
 * the whole history.
 */
export interface PunchSignoffs {
  fixed: PunchSignoff | null;
  verified: PunchSignoff | null;
  approved: PunchSignoff | null;
  /** notCarriedOver: closed at publish, not carried over to the published list. */
  closed: (PunchSignoff & { notCarriedOver: boolean }) | null;
  rejections: number;
  /** Per rejection, whose fix it turned down (the assignee then), by name. */
  rejectedFor: (string | null)[];
}

/** The close-out report: the closed-out list and its items by number. */
export interface PunchReportResponse {
  list: PunchList;
  items: (PunchItem & { signoffs: PunchSignoffs })[];
}

export interface PunchSub {
  name: string;
  source: 'commitment' | 'manual' | 'in_use';
  /** A live hand-added row exists (Settings can remove it), whatever the source. */
  handAdded?: boolean;
}

/** Who can take an item on this job, and the job's field leaders (for an item's Field Leader). */
export interface PunchSubsResponse {
  subs: PunchSub[];
  ewCrewLabel: string;
  fieldLeaders?: { id: string; name: string }[];
}

export interface PunchItemPatch {
  description?: string;
  trade?: string;
  assignee?: PunchAssigneeInput | null;
  room?: string | null;
  wall?: PunchWall | null;
  notes?: string | null;
  fieldLeaderId?: string | null;
  dueDate?: string | null;
  priority?: PunchPriority;
}

export type PunchSettingsPatch = Partial<Pick<PunchList,
  'requiresAoApproval' | 'architectName' | 'ownerRep' | 'dueMode' | 'dueDays' | 'dueDate' | 'defaultPriority'>>;

export const punchApi = {
  get: (projectId: number) =>
    api.get(`/projects/${projectId}/punch`).then((r) => r.data as PunchListResponse),
  item: (projectId: number, itemId: number) =>
    api.get(`/projects/${projectId}/punch/items/${itemId}`).then((r) => r.data as PunchItem & { events: PunchEvent[] }),
  subs: (projectId: number) =>
    api.get(`/projects/${projectId}/punch/subs`).then((r) => r.data as PunchSubsResponse),

  start: (projectId: number) =>
    api.post(`/projects/${projectId}/punch`).then((r) => r.data as PunchList),
  updateSettings: (projectId: number, body: PunchSettingsPatch) =>
    api.patch(`/projects/${projectId}/punch/settings`, body).then((r) => r.data as PunchList),
  /** Change only the listed trades' default sub (null clears one); the server merges under its lock. */
  setTradeSubs: (projectId: number, entries: { trade: string; subName: string | null }[]) =>
    api.patch(`/projects/${projectId}/punch/trade-subs`, { entries }).then((r) => r.data as PunchList),
  addSub: (projectId: number, name: string) =>
    api.post(`/projects/${projectId}/punch/subs`, { name }).then((r) => r.data as PunchSubsResponse),
  removeSub: (projectId: number, name: string) =>
    api.post(`/projects/${projectId}/punch/subs/remove`, { name }).then((r) => r.data as PunchSubsResponse),
  addTrade: (projectId: number, name: string) =>
    api.post(`/projects/${projectId}/punch/trades`, { name }).then((r) => r.data as { trades: PunchTrade[] }),
  removeTrade: (projectId: number, trade: string) =>
    api.post(`/projects/${projectId}/punch/trades/remove`, { trade }).then((r) => r.data as { trades: PunchTrade[] }),
  setTradeHidden: (projectId: number, trade: string, hidden: boolean) =>
    api.post(`/projects/${projectId}/punch/trades/hidden`, { trade, hidden }).then((r) => r.data as { trades: PunchTrade[] }),
  publish: (projectId: number, body: {
    /** Assignees whose open items close as "not carried over"; everything else carries. */
    closeGroups: (PunchAssigneeInput | { type: 'unassigned' })[];
    walkDate?: string;
    walkAttendees?: string;
  }) => api.post(`/projects/${projectId}/punch/publish`, body)
    .then((r) => r.data as { list: PunchList; carriedOver: number; closed: number }),
  closeOut: (projectId: number) =>
    api.post(`/projects/${projectId}/punch/close-out`).then((r) => r.data as PunchList),
  /** The close-out report's data; refused until the list is closed out. */
  // A timeout, generous for a long list: Print waits on this read, and one
  // caught by an API restart would otherwise spin forever (review of #583).
  report: (projectId: number) =>
    api.get(`/projects/${projectId}/punch/report`, { timeout: 60_000 }).then((r) => r.data as PunchReportResponse),
  /** itemIds: the items on the printed sheet; only those are sent (still New, still this assignee's). */
  sendSheet: (projectId: number, assignee: PunchAssigneeInput, itemIds?: number[]) =>
    api.post(`/projects/${projectId}/punch/send-sheet`, { assignee, ...(itemIds ? { itemIds } : {}) })
      .then((r) => r.data as { sent: number }),

  createItem: (projectId: number, body: {
    clientRequestId?: string;
    description: string;
    trade: string;
    assignee?: PunchAssigneeInput | null;
    room?: string;
    notes?: string;
    /** Omitted: the job's only field leader, or the caller if they lead the job. */
    fieldLeaderId?: string;
    dueDate?: string;
    priority?: PunchPriority;
    beforePhoto: PunchPhotoInput;
    /** Dropped on a drawing (Drop Pin to Add Item). */
    pin?: PunchPin;
  }) => api.post(`/projects/${projectId}/punch/items`, body).then((r) => r.data as PunchItem),
  updateItem: (projectId: number, itemId: number, body: PunchItemPatch) =>
    api.patch(`/projects/${projectId}/punch/items/${itemId}`, body).then((r) => r.data as PunchItem),
  /** One change to many items; the ones that can't take it come back ok: false with the reason. */
  bulk: (projectId: number, body: {
    itemIds: number[];
    op: 'reassign' | 'due' | 'pass_to';
    assignee?: PunchAssigneeInput | null;
    dueDate?: string | null;
    bicTo?: 'ew' | 'assignee' | 'ao';
    bicUserId?: string;
  }) => api.post(`/projects/${projectId}/punch/items/bulk`, body)
    .then((r) => r.data as {
      changed: number;
      results: { itemId: number; ok: boolean; unchanged?: boolean; error?: string }[];
      /** A real failure ended this request early: don't send the rest. */
      stopped?: boolean;
    }),
  act: (projectId: number, itemId: number, body: {
    action: PunchAction;
    reason?: string;
    note?: string;
    bicTo?: 'ew' | 'assignee' | 'ao';
    bicUserId?: string;
    afterPhoto?: PunchPhotoInput;
  }) => api.post(`/projects/${projectId}/punch/items/${itemId}/actions`, body).then((r) => r.data as PunchItem),
  /** kind 'video': a clip, extra evidence only (`photo` then carries the clip's file). */
  addMedia: (projectId: number, itemId: number, kind: 'before' | 'after' | 'video', photo: PunchPhotoInput) =>
    api.post(`/projects/${projectId}/punch/items/${itemId}/media`, { kind, photo }).then((r) => r.data as PunchItem),
  /** Place or move an item's pin; refused on a closed item or a closed-out list. */
  setPin: (projectId: number, itemId: number, pin: PunchPin) =>
    api.put(`/projects/${projectId}/punch/items/${itemId}/pin`, pin).then((r) => r.data as PunchItem),
  removePin: (projectId: number, itemId: number) =>
    api.delete(`/projects/${projectId}/punch/items/${itemId}/pin`).then((r) => r.data as PunchItem),
  removeMedia: (projectId: number, itemId: number, mediaId: number) =>
    api.delete(`/projects/${projectId}/punch/items/${itemId}/media/${mediaId}`).then((r) => r.data as PunchItem),
  removeItem: (projectId: number, itemId: number) =>
    api.delete(`/projects/${projectId}/punch/items/${itemId}`).then((r) => r.data as { id: number; removed: boolean }),
};

// ─── Contracts (#501 — subcontract paperwork status, owner contract review) ───
// Replaces the weekly Subcontract Status Report that was kept by hand in Excel.

export type ContractSubStage = 'preparing' | 'internal_review' | 'waiting_on_sub' | 'signed' | 'on_hold';
export type ContractWaitCategory = 'industrial_commercial' | 'gc_only' | 'fab';
export type ContractAgreementType = 'subcontract' | 'psa' | 'msa';
/** Contract executed, new vendor form, W9, certificate of insurance. */
export type ContractFormKey = 'cc' | 'nv' | 'w9' | 'coi';
/** The stored stages, plus the three the server derives from the forms received. */
export type ContractSubBucket = ContractSubStage | 'missing_items' | 'cleared' | 'voided';
export type ContractOwnerStep = 'new' | 'received' | 'redline_sent' | 'approved' | 'completed';

export interface ContractSub {
  id: number;
  /** null (with jobNumber and projectTitle) for an MSA that is not tied to a job (#631). */
  projectId: number | null;
  jobNumber: string | null;
  projectTitle: string | null;
  vendorName: string;
  agreementType: ContractAgreementType;
  /** The Intacct subcontract number (S00731), once there is one. */
  sageDocNo: string | null;
  stage: ContractSubStage;
  waitCategory: ContractWaitCategory | null;
  newVendor: boolean;
  originallySentOn: string | null;
  holdOn: string | null;
  ccOn: string | null;
  nvOn: string | null;
  w9On: string | null;
  coiOn: string | null;
  notes: string | null;
  voidedAt: string | null;
  /** The Eastern calendar day it was voided. */
  voidedOn: string | null;
  /** The Eastern day the row was last changed (or made). The API's 30-day window counts from the later of this and the event's own date. */
  touchedOn: string | null;
  createdAt: string;
  updatedAt: string | null;
  // ── Derived by the API. Never recomputed here: the server holds the one
  // definition of "cleared to report to site", and the screen reads it back.
  bucket: ContractSubBucket;
  /** The forms this subcontract needs (the new vendor form only for a new vendor). */
  required: ContractFormKey[];
  /** The required forms not yet received. */
  missing: ContractFormKey[];
  /** The day the last required form arrived; null while any is missing. */
  clearedOn: string | null;
}

export interface ContractOwner {
  id: number;
  projectId: number | null;
  jobNumber: string | null;
  projectTitle: string | null;
  name: string;
  sentBy: string | null;
  receivedOn: string | null;
  redlineSentOn: string | null;
  approvedOn: string | null;
  completedOn: string | null;
  notes: string | null;
  step: ContractOwnerStep;
  /** The Eastern day the row was last changed (or made); see ContractSub. */
  touchedOn: string | null;
  createdAt: string;
  updatedAt: string | null;
}

export interface ContractsDashboard {
  access: { canEdit: boolean };
  /** 'yours' = only the jobs the viewer is on; 'company' = every job. */
  scope: 'company' | 'yours';
  asOf: string;
  windowDays: number;
  history: boolean;
  subcontracts: ContractSub[];
  ownerContracts: ContractOwner[];
}

export interface ContractsForProject {
  /** `canRequest`: may start a subcontract request on this job (subcontracts.request). */
  access: { canEdit: boolean; canRequest: boolean; placeholderNumber: boolean; archived: boolean };
  asOf: string;
  subcontracts: ContractSub[];
  ownerContracts: ContractOwner[];
}

export interface ContractProjectOption {
  id: number;
  jobNumber: string;
  title: string | null;
  /** The number is a placeholder (SYSTAC-, ClosedWon-, IMP-, MNT-) or blank: fine for an owner contract, refused for a subcontract. */
  placeholderNumber: boolean;
}

/** On a PATCH only the changed keys are sent; `null` clears a clearable field. */
export interface ContractSubInput {
  projectId?: number;
  vendorName?: string;
  agreementType?: ContractAgreementType;
  sageDocNo?: string | null;
  stage?: ContractSubStage;
  waitCategory?: ContractWaitCategory | null;
  newVendor?: boolean;
  originallySentOn?: string | null;
  holdOn?: string | null;
  ccOn?: string | null;
  nvOn?: string | null;
  w9On?: string | null;
  coiOn?: string | null;
  notes?: string | null;
  /** true voids it (kept as a record for 30 days); false restores it. */
  voided?: boolean;
}

export interface ContractOwnerInput {
  name?: string;
  projectId?: number | null;
  sentBy?: string | null;
  receivedOn?: string | null;
  redlineSentOn?: string | null;
  approvedOn?: string | null;
  completedOn?: string | null;
  notes?: string | null;
}

export const contractsApi = {
  /** `history` also returns cleared, voided and completed rows older than 30 days. */
  dashboard: (history = false) =>
    api.get('/contracts', { params: history ? { history: 1 } : {} }).then((r) => r.data as ContractsDashboard),
  forProject: (projectId: number) =>
    api.get(`/projects/${projectId}/contracts`).then((r) => r.data as ContractsForProject),
  projectOptions: () =>
    api.get('/contracts/project-options').then((r) => r.data as ContractProjectOption[]),

  /** `projectId` may be left out only for an MSA, which is not tied to a job; the API answers 400 for any other type. */
  createSub: (body: ContractSubInput & { vendorName: string }) =>
    api.post('/contracts/subcontracts', body).then((r) => r.data as ContractSub),
  updateSub: (id: number, body: Omit<ContractSubInput, 'projectId'>) =>
    api.patch(`/contracts/subcontracts/${id}`, body).then((r) => r.data as ContractSub),
  removeSub: (id: number) =>
    api.delete(`/contracts/subcontracts/${id}`).then((r) => r.data as { id: number; removed: boolean }),

  createOwner: (body: ContractOwnerInput & { name: string }) =>
    api.post('/contracts/owner', body).then((r) => r.data as ContractOwner),
  updateOwner: (id: number, body: ContractOwnerInput) =>
    api.patch(`/contracts/owner/${id}`, body).then((r) => r.data as ContractOwner),
  removeOwner: (id: number) =>
    api.delete(`/contracts/owner/${id}`).then((r) => r.data as { id: number; removed: boolean }),
};

// ─── Subcontract requests (#627) ─────────────────────────────────────────────
// A PM fills one in; the coordinator creates the subcontract in Intacct, types its number and downloads the Word
// file. The server decides every right (`access`) and what is still missing (`missing`); the screens only show them.

export type SubcontractRequestStatus = 'draft' | 'submitted' | 'withdrawn';
export type LdFrequency = 'month' | 'week' | 'day';
/** How the price is set (#694). A request with none is a lump sum. */
export type ContractBasis = 'lump-sum' | 'time-and-material';
export type NteRateUnit = 'hour' | 'day' | 'week';

/** The document fields as the API stores them. Money is a canonical string ("4680.00"), a percent too ("10"). */
export interface SubcontractRequestPayload {
  vendorName?: string; vendorStreet?: string; vendorCity?: string; vendorState?: string; vendorZip?: string;
  contactName?: string; contactEmail?: string; contactPhone?: string;
  newVendor?: boolean;
  jobName?: string; jobAddress?: string; jobCity?: string; jobState?: string; jobZip?: string;
  owner?: string; architect?: string; costCode?: string;
  agreementDate?: string; amount?: string; retainagePercent?: string; billingDay?: number;
  /** Time and material only: the rate and what it is per, printed together ("$85.00 per hour"). Optional there, both or neither. */
  contractBasis?: ContractBasis; nteRate?: string; nteRateUnit?: NteRateUnit;
  scope?: string; exclusions?: string;
  prevailingWage?: boolean;
  performanceBond?: boolean; performanceBondAmount?: string;
  paymentBond?: boolean; paymentBondAmount?: string;
  startDate?: string; startAsDirected?: boolean;
  completionDate?: string; completionAsDirected?: boolean;
  liquidatedDamages?: boolean; liquidatedDamagesAmount?: string; liquidatedDamagesFrequency?: LdFrequency;
  /** The fifteen Yes/No questions, by key (`SubcontractRequestPrefill.requirements` lists them in order). */
  requirements?: Record<string, boolean>;
  descriptions?: { tests?: string; certifications?: string; inspections?: string };
}

/** What a PATCH may send: any field, and `null` to clear it. */
export type SubcontractRequestPatch = {
  [K in Exclude<keyof SubcontractRequestPayload, 'requirements' | 'descriptions'>]?: SubcontractRequestPayload[K] | null;
} & {
  requirements?: Record<string, boolean | null> | null;
  descriptions?: { tests?: string | null; certifications?: string | null; inspections?: string | null } | null;
};

/** One request as a list shows it. No amount and no payload. */
export interface SubcontractRequestListItem {
  id: number;
  status: SubcontractRequestStatus;
  projectId: number;
  jobNumber: string;
  projectTitle: string | null;
  templateKey: string;
  templateLabel: string;
  vendorName: string | null;
  requestedBy: { id: string; name: string | null };
  submittedAt: string | null;
  createdAt: string;
  updatedAt: string | null;
  /** The Intacct number, typed by the coordinator. Null until then. */
  subcontractNumber: string | null;
  numbered: boolean;
  paperworkVoided: boolean;
  paperworkRemoved: boolean;
}

export interface SubcontractRequestRights {
  canEdit: boolean;
  canSubmit: boolean;
  canWithdraw: boolean;
  canDiscard: boolean;
  canSetNumber: boolean;
}

export interface SubcontractRequest extends SubcontractRequestListItem {
  payload: SubcontractRequestPayload;
  templateVersion: string | null;
  /** What is still missing for a submit, named as the form names it. Empty when complete. */
  missing: string[];
  /** The Yes/No questions with their labels, in the contract's order: what reading the payload back needs. */
  requirements: { key: string; label: string }[];
  access: SubcontractRequestRights;
}

export interface SubcontractRequestPrefill {
  project: { id: number; jobNumber: string; title: string | null };
  access: { canRequest: boolean; placeholderNumber: boolean; archived: boolean };
  job: {
    name: string | null; address: string | null; city: string | null; state: string | null; zip: string | null;
    owner: string | null; addressVerified: boolean;
  };
  suggestedTemplate: string | null;
  templates: { key: string; label: string }[];
  /** The Yes/No questions with their labels, in the contract's order. */
  requirements: { key: string; label: string }[];
  /** Null for someone who may not see the Contracts page. */
  ownerContract: { tracked: boolean; complete: boolean } | null;
  defaults: { agreementDate: string };
}

export type SubcontractRequestFilter = 'waiting' | 'numbered' | 'all';

/** The Word file, and the name the API gave it (null when the browser was not allowed to read it). */
export interface SubcontractDocument { blob: Blob; fileName: string | null }

/**
 * A vendor from Intacct, as the request form's Subcontractor box offers it (#627). The API has already cleaned every value so that
 * none is one a request would refuse on save: `''` means Intacct has nothing usable for that field (never a guess).
 */
export interface IntacctVendor {
  /** Intacct's vendor id, e.g. "V-102458". Tells two vendors with the same name apart. */
  id: string;
  name: string;
  street: string; city: string; state: string; zip: string;
  contactName: string; contactEmail: string; contactPhone: string;
  country: string;
  /** Outside the United States: `state` and `zip` are blank because the form holds a US state and zip only. */
  abroad: boolean;
}

/** `syncedAt` is null, and `vendors` empty, until the nightly vendor sync has run (it ships off). */
export interface IntacctVendorList { syncedAt: string | null; vendors: IntacctVendor[] }

/** One document attached to a request (#656), without its bytes. */
export interface SubcontractRequestFile {
  id: number;
  fileName: string;
  contentType: string | null;
  sizeBytes: number | null;
  uploadedAt: string;
  uploadedBy: { id: string; displayName: string | null; email: string | null } | null;
}

const SUB_REQUESTS = '/contracts/subcontract-requests';

export const subcontractRequestsApi = {
  /** Active Intacct vendors for the Subcontractor type-ahead: about 1,200, the whole list, filtered in the browser. */
  vendors: () => api.get('/contracts/vendors').then((r) => r.data as IntacctVendorList),
  /** The job's facts, the suggested template, the question labels. */
  prefill: (projectId: number) =>
    api.get(`/projects/${projectId}/subcontract-requests/prefill`).then((r) => r.data as SubcontractRequestPrefill),
  /** The caller's own requests, drafts included, newest first. */
  mine: () => api.get(`${SUB_REQUESTS}/mine`).then((r) => r.data as SubcontractRequestListItem[]),
  /** The coordinator's queue, oldest first, a page at a time (the API's default page is 200 and its largest 500). */
  queue: (filter: SubcontractRequestFilter = 'waiting', page: { limit?: number; offset?: number } = {}) =>
    api.get(SUB_REQUESTS, { params: { filter, ...page } }).then((r) => r.data as SubcontractRequestListItem[]),
  get: (id: number) => api.get(`${SUB_REQUESTS}/${id}`).then((r) => r.data as SubcontractRequest),
  create: (body: { projectId: number; templateKey: string; payload: SubcontractRequestPatch }) =>
    api.post(SUB_REQUESTS, body).then((r) => r.data as SubcontractRequest),
  update: (id: number, body: { templateKey?: string; payload?: SubcontractRequestPatch }) =>
    api.patch(`${SUB_REQUESTS}/${id}`, body).then((r) => r.data as SubcontractRequest),
  submit: (id: number) => api.post(`${SUB_REQUESTS}/${id}/submit`).then((r) => r.data as SubcontractRequest),
  withdraw: (id: number) => api.post(`${SUB_REQUESTS}/${id}/withdraw`).then((r) => r.data as SubcontractRequest),
  discard: (id: number) => api.delete(`${SUB_REQUESTS}/${id}`).then((r) => r.data as { id: number; discarded: true }),
  /** `null` clears the number. */
  setNumber: (id: number, sageDocNo: string | null) =>
    api.put(`${SUB_REQUESTS}/${id}/subcontract-number`, { sageDocNo }).then((r) => r.data as SubcontractRequest),
  /**
   * The finished Word document, made on the server from the saved entries.
   *
   * ⚠️ THE ERROR IS READ OUT OF THE BLOB. With `responseType: 'blob'` a refusal's JSON body arrives as a Blob too, and
   * the interceptor then finds no `message` and says "Request failed with status code 409", losing the one sentence
   * that says WHY (type the number first; the request is incomplete; the paperwork was voided).
   */
  document: async (id: number): Promise<SubcontractDocument> => {
    const r = await api.get(`${SUB_REQUESTS}/${id}/document`, { responseType: 'blob', validateStatus: () => true });
    if (r.status >= 200 && r.status < 300) {
      return { blob: r.data as Blob, fileName: fileNameFromDisposition(r.headers?.['content-disposition'] as string | undefined) };
    }
    throw await blobRefusal(r, `The document could not be made (HTTP ${r.status}).`);
  },

  // Documents to include with a request (#656): optional extras for the coordinator to download. Kept in the tracker's own
  // database, so only the request's requester and contracts.edit can open them. Whoever may EDIT the request may add or
  // remove; whoever may read it may download.
  files: (id: number) =>
    api.get(`${SUB_REQUESTS}/${id}/files`).then((r) => r.data as SubcontractRequestFile[]),
  uploadFile: (id: number, file: File) => {
    const fd = new FormData();
    fd.append('file', file, file.name);
    // Content-Type undefined → the browser sets multipart/form-data with the boundary.
    return api.post(`${SUB_REQUESTS}/${id}/files`, fd, { headers: { 'Content-Type': undefined } as any })
      .then((r) => r.data as SubcontractRequestFile);
  },
  /** ⚠️ Like `document`, the refusal is read out of the blob: a 404 or 503 says why, and the interceptor would lose it. */
  downloadFile: async (id: number, fileId: number): Promise<Blob> => {
    const r = await api.get(`${SUB_REQUESTS}/${id}/files/${fileId}`, { responseType: 'blob', validateStatus: () => true });
    if (r.status >= 200 && r.status < 300) return r.data as Blob;
    throw await blobRefusal(r, `The document could not be downloaded (HTTP ${r.status}).`);
  },
  removeFile: (id: number, fileId: number) =>
    api.delete(`${SUB_REQUESTS}/${id}/files/${fileId}`).then((r) => r.data as { id: number; removed: true }),
};

// ─── Schedule (UI overhaul Phase 5 — per-project, MS Project import) ────────

// One schedule activity (task) imported from MS Project. The schedule is
// read-only here — MS Project stays the system of record.
export interface Activity {
  id: number;
  projectId: number;
  uid: number;                    // MS Project Task UID
  name: string;
  start: string | null;          // date-only (YYYY-MM-DD…)
  finish: string | null;
  pctComplete: number;            // 0–100
  isCritical: boolean;
  isSummary: boolean;
  outlineLevel: number | null;
  baselineStart: string | null;
  baselineFinish: string | null;
  sortOrder: number;
}

export interface ImportRun {
  id: number;
  projectId: number;
  source: string;                 // 'mspdi-xml' | 'project-online'
  fileName: string | null;
  activityCount: number;
  importedAt: string;
  importedBy: string | null;
}

export interface ScheduleResponse {
  activities: Activity[];
  lastImport: ImportRun | null;
}

export interface ImportScheduleResult {
  activityCount: number;
  lastImport: ImportRun | null;
}

// ─── Schedule rollup (company-wide, top-nav "Schedule Rollup") ──────────────

export type ScheduleHealth = 'on-track' | 'at-risk' | 'off-track';

export interface ScheduleRollupRow {
  projectId: number;
  jobNumber: string;
  title: string;
  pctComplete: number;            // rounded avg of non-summary activities (0–100)
  activityCount: number;
  nextMilestone: { name: string; finish: string | null } | null;
  health: ScheduleHealth;
}

export const scheduleApi = {
  get: (projectId: number) =>
    api.get(`/projects/${projectId}/schedule`).then((r) => r.data as ScheduleResponse),
  // Company-wide rollup — one row per project that has any schedule activities.
  rollup: () =>
    api.get('/schedule/rollup').then((r) => r.data as ScheduleRollupRow[]),
  // Multipart upload of an MS Project MSPDI XML export. Send FormData and let
  // the browser set the multipart Content-Type (with boundary) — overriding the
  // axios default 'application/json' here is required or the body won't parse.
  import: (projectId: number, file: File) => {
    const body = new FormData();
    body.append('file', file);
    return api
      .post(`/projects/${projectId}/schedule/import`, body, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data as ImportScheduleResult);
  },
};

// ─── Safety rollup (company-wide, read-only mirror of E&W Safety) ───────────

export interface SafetyRollupRow {
  jobNumber: string;
  title: string;
  goodCatch: number;
  siteInspection: number;
  equipmentInspection: number;
}

export interface SafetyRollup {
  available: boolean; // false when safety-site is unreachable
  totals: { goodCatch: number; siteInspection: number; equipmentInspection: number };
  byJob: SafetyRollupRow[]; // sorted desc by total; zero-rows omitted
}

export const safetyApi = {
  rollup: () => api.get('/safety/rollup').then((r) => r.data as SafetyRollup),
};

export const mobilizationsApi = {
  list: (projectId: number) =>
    api.get(`/projects/${projectId}/mobilizations`).then((r) => r.data),
  create: (projectId: number, data: unknown) =>
    api.post(`/projects/${projectId}/mobilizations`, data).then((r) => r.data),
  update: (projectId: number, id: number, data: unknown) =>
    api.patch(`/projects/${projectId}/mobilizations/${id}`, data).then((r) => r.data),
  delete: (projectId: number, id: number) =>
    api.delete(`/projects/${projectId}/mobilizations/${id}`),
};

export const reportsApi = {
  kpi: (scope?: 'mine' | 'all') =>
    api.get('/reports/kpi', { params: scope ? { scope } : {} }).then((r) => r.data),
  exportCsv: () =>
    api.get('/reports/export/projects.csv', { responseType: 'blob' }).then((r) => r.data),
  jobCost: (projectId: number) =>
    api.get(`/projects/${projectId}/reports/job-cost`).then((r) => r.data as JobCostReport),
  manHours: (projectId: number) =>
    api.get(`/projects/${projectId}/reports/man-hours`).then((r) => r.data as ManHoursReport),
};

// ─── Job Cost Management Report (per project) ────────────────────────────────

/** The five reporting groups, in the report's column order. Mirrors
 *  `GROUP_ORDER` in the API's reports/lib/jobCostReport.ts. */
export type JobCostGroup = 'Labor' | 'Material' | 'Equipment' | 'Subcontract' | 'Other';

export interface JobCostCell {
  estimated: number;
  actual: number;
  /** estimated − actual. Negative = over the estimate. Deliberately does NOT
   *  account for committed cost — see `committed`. */
  balance: number;
  /** OPEN commitment: PO/subcontract value not yet invoiced, clamped per line. */
  committed: number;
  /** estimated − actual − committed. ⚠️ Exposure, NOT a forecast — it counts
   *  nothing still to be bought, so it reads optimistically mid-buyout. The
   *  projected final cost lives on the Financials tab. */
  exposure: number;
}

export interface JobCostRow {
  costCode: string | null;
  description: string | null;
  byGroup: Record<JobCostGroup, JobCostCell>;
  total: JobCostCell;
  isChangeOrder: boolean;
}

export interface JobCostSection {
  rows: JobCostRow[];
  /** The section's TOTALS line — `description` carries its label. */
  totals: JobCostRow;
}

export interface JobCostReport {
  header: {
    projectId: number;
    jobNumber: string;
    jobName: string;
    customer: string | null;
    projectManager: string | null;
    estimator: string | null;
    /** Always null — PMT has no billing-person field. Rendered only when set. */
    billingPerson: string | null;
  };
  groups: JobCostGroup[];
  report: {
    costCodes: JobCostSection;
    changeOrders: JobCostSection;
    grandTotal: JobCostRow;
  };
  provenance: {
    estimateEnabled: boolean;
    actualEnabled: boolean;
    /** SAGE_PO_SYNC_ENABLED — the nightly PO/subcontract pull. */
    committedEnabled: boolean;
    estimateSyncedAt: string | null;
    actualSyncedAt: string | null;
    committedSyncedAt: string | null;
    /** PO/sub lines missing any of invoiced-to-date, document state, quantity
     *  remaining, or revised value. The first three OVERSTATE Committed — the
     *  line contributes its full order value, or a Draft/Closed PO still counts,
     *  or already-received price variance still counts. ⚠️ The fourth
     *  UNDERSTATES (#405): posted change orders that raised a PO are missing
     *  until the line syncs. So the UI must say the figure is unreliable, not
     *  that it is high. Clears as the sync backfills. */
    committedLinesNotFullySynced: number;
    /** False = this job has no estimate lines at all, as distinct from a job
     *  genuinely budgeted at zero. */
    estimateSynced: boolean;
    pendingChange: { rows: number; amount: number };
    generatedAt: string;
  };
}

// ─── Man Hours Report (per project) ──────────────────────────────────────────

export interface ManHoursCell {
  estimated: number;
  actual: number;
  /** estimated − actual. Negative = more hours burned than estimated. */
  balance: number;
}

export interface ManHoursRow {
  /** Null for the no-cost-code bucket and for unmatched Paycor names. */
  costCode: string | null;
  description: string | null;
  hours: ManHoursCell;
  isChangeOrder: boolean;
  /** A Paycor cost-code NAME that resolved to no unique cost code — real hours,
   *  uncertain placement. `description` carries the raw name. */
  unmatchedName: boolean;
}

export interface ManHoursSection {
  rows: ManHoursRow[];
  totals: ManHoursRow;
}

export interface ManHoursReport {
  header: {
    projectId: number;
    jobNumber: string;
    jobName: string;
    customer: string | null;
    projectManager: string | null;
    estimator: string | null;
  };
  report: {
    costCodes: ManHoursSection;
    changeOrders: ManHoursSection;
    grandTotal: ManHoursRow;
    unmatchedActualHours: number;
  };
  provenance: {
    /** The $/MH divisor this response was derived at. */
    rate: number;
    /** 'default' = the mp_config row was missing/invalid — tell an admin. */
    rateSource: 'config' | 'default';
    /** The estimator's typed job-level hours, as a cross-check. */
    typedJobHours: number | null;
    estimateEnabled: boolean;
    estimateSyncedAt: string | null;
    estimateSynced: boolean;
    /** False = safety-site unreachable; Actual is missing, not zero. */
    actualsAvailable: boolean;
    /** First work_date in the Paycor feed — hours before this were never synced. */
    actualsFrom: string | null;
    actualsThrough: string | null;
    /** Job start (current, falling back to original) for the incomplete-actuals check. */
    jobStartDate: string | null;
    unmatchedActualHours: number;
    /** Estimated MH derived from pending (not-yet-approved) change rows — included, disclosed. */
    pendingChangeHours: number;
    generatedAt: string;
  };
}

export interface BulkImportEntry {
  jobNumber?: string;        // omit when the Excel row has no job # — name lookup will be used
  projectName?: string;      // raw name from column G; used for name-match + stub creation
  divisionLabel?: string;    // raw value from column A ("IND", "GC", "Comm", …)
  workDate: string;          // YYYY-MM-DD
  crewCount: number;
}

export interface BulkImportResult {
  imported: number;
  skipped: number;
  matchedByName: number;
  autoCreated: number;
  warnings: string[];
}

export interface MpLeader {
  id: number;
  spId: number | null;
  name: string;
  divisionId: number | null;
  class: string | null;
  currentAssignmentId: number | null;
  /** Shift for the current assignment. Null = day shift. */
  assignmentShift: string | null;
  currentFinish: string | null;     // ISO date
  nextAvailable: string | null;     // ISO date
  upcomingJobId: number | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  division: { id: number; label: string } | null;
  currentAssignment: { id: number; jobNumber: string; title: string } | null;
  upcomingJob: { id: number; jobNumber: string; title: string } | null;
  /**
   * Paycor employee id — the join key to the travel roster (#396).
   *
   * ⚠️ Leaders join to travel by THIS, never by name. A leader's go-by name is
   * often not their Paycor name — a nickname, or a short form of the payroll
   * one — so a name join would attach the wrong person's travel preference.
   */
  employeeId: string | null;
  /**
   * Read-only passenger from mp_travel_roster. The Leaders tab shows it and
   * never writes it — the roster owns it.
   *
   * ⚠️ null means one of TWO different things, and the UI has to tell them
   * apart: no employeeId on the leader (never matched), or an employeeId with
   * no roster row (matched, but not on the travel list). Both render blank
   * otherwise, and blank also looks like "no travel preference recorded".
   */
  travel: {
    travelPref: TravelPref;
    availableFrom: string | null;
    twic: boolean;
    weekendOk: boolean;
  } | null;
}

export interface MpLeaderInput {
  name: string;
  divisionId?: number | null;
  class?: string | null;
  currentAssignmentId?: number | null;
  assignmentShift?: string | null;
  currentFinish?: string | null;
  nextAvailable?: string | null;
  upcomingJobId?: number | null;
  /** Paycor employee id. '' clears it; the API rejects one already in use. */
  employeeId?: string | null;
}

// ─── Travel roster (#396 slice 1) ────────────────────────────────────────────

/**
 * ⚠️ The source vocabulary, verbatim — including the slash. It comes from the
 * roster export the manpower lead maintains, not from us, and renaming it here would
 * mean translating in both directions on every read and write.
 */
export const TRAVEL_PREFS = ['Traveler', '50/50', 'Local'] as const;
export type TravelPref = (typeof TRAVEL_PREFS)[number];

export interface TravelRosterRow {
  id: number;
  employeeId: string | null;
  firstName: string | null;
  lastName: string | null;
  name: string | null;
  /** The roster's own job title, deliberately not reconciled with Paycor's. */
  position: string | null;
  divisionId: number | null;
  divisionLabel: string | null;
  employmentType: string | null;
  travelPref: TravelPref;
  /** Holds a TWIC card — gates port and refinery work. */
  twic: boolean;
  weekendOk: boolean;
  /** Raw: 'KY', 'Ky', 'Indiana' and one postcode are all really in this column. */
  homeState: string | null;
  /** ISO date. ⚠️ null means available NOW, not unknown. */
  availableFrom: string | null;
  isActive: boolean;
  /**
   * ⚠️ null means NOBODY HAS SAID, not "no vehicle". Only set when a scheduler
   * has explicitly overridden the job-title default.
   */
  hasCompanyVehicleOverride: boolean | null;
  /**
   * What to actually use — the override if a scheduler set one, otherwise the
   * job title's default.
   *
   * ⚠️ IT NO LONGER AFFECTS PAY (2026-09-22). This used to read "true means NO
   * fuel payment is owed", because the 46-75 $20 was withheld from anyone with
   * a company truck. The approved acknowledgment wording pays it regardless,
   * so this is now a scheduling fact and nothing more.
   */
  hasCompanyVehicle: boolean;
  companyVehicleSource:
    | 'scheduler-override'
    | 'job-title'
    | 'no-title'
    | 'unknown-title'
    /** Paycor holds more than one different title for this employee id. */
    | 'ambiguous-title';
  /** The answer is a fallback, not a ruling — show it to somebody. */
  companyVehicleNeedsReview: boolean;
  companyVehicleReason: string;
  source: 'import' | 'manual';
  notes: string | null;
  importedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TravelRosterInput {
  employeeId?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  position?: string | null;
  divisionId?: number | null;
  employmentType?: string | null;
  travelPref: TravelPref;
  twic?: boolean;
  weekendOk?: boolean;
  homeState?: string | null;
  availableFrom?: string | null;
  isActive?: boolean;
  /**
   * ⚠️ TRI-STATE. Omit to leave it alone, `null` to CLEAR the override and go
   * back to the job-title default, true/false to set it explicitly. `null` is
   * not `false` — sending false pins the person to "no vehicle" forever.
   */
  hasCompanyVehicle?: boolean | null;
  notes?: string | null;
}

export interface TravelRosterList {
  rows: TravelRosterRow[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * One Travel Acknowledgment issued on a job.
 *
 * ⚠️ MORE THAN ONE ROW PER PERSON IS NORMAL. The record is deliberately not
 * unique on person + job: if the travel band changes, the signed row still
 * says what was actually agreed to and a fresh acknowledgment is issued
 * against the new figures. Anything rendering these must not collapse them by
 * person — the point of the list is that a second link is visible.
 *
 * ⚠️ `token` IS A CREDENTIAL. It is the whole of what the public signing page
 * checks, which is why the route behind this is gated on `manpower.edit`
 * rather than a read permission. Do not put it anywhere it can be logged,
 * exported or pasted into a shared document.
 */
export interface TravelAcknowledgmentRow {
  id: number;
  token: string;
  rosterId: number;
  name: string | null;
  position: string | null;
  travelBand: string | null;
  /** The typed name that constitutes the signature. Null until they sign. */
  signedName: string | null;
  signedAt: string | null;
  createdAt: string;
  /**
   * Unsigned, and a newer link was issued to the same person on this job — so
   * this one no longer works. Only the newest link can be signed.
   */
  replaced: boolean;
  /**
   * The engine's warnings about this person's pay when the link was issued,
   * from the frozen record — e.g. that exempt status is recorded nowhere.
   * Shown to the scheduler with the link; the signer never sees them.
   */
  caveats: string[];
  /** The SharePoint link of the filed PDF, once somebody has filed it. */
  filedUrl: string | null;
  filedAt: string | null;
  filedByName: string | null;
  /**
   * The name the signed PDF is filed under. Null until it is signed — there is
   * nothing to file before that. Deterministic, so a retried filing finds the
   * file it already uploaded.
   */
  pdfFileName: string | null;
}

/**
 * The gap view's supply line (slice 3).
 *
 * ⚠️ `derived` is false and always will be while availableFrom is typed rather
 * than computed. Any surface rendering these counts must say so — a supply
 * number that looks derived is trusted more than it has earned.
 *
 * ⚠️ `byPref` keeps the tiers SEPARATE and is never pre-summed. The manpower lead
 * leaves occasional travelers off the availability figures on purpose, so combining
 * them is a decision a caller has to make visibly.
 */
export interface TravelRosterSupply {
  derived: false;
  basis: string;
  totals: Record<string, number>;
  weeks: {
    weekStart: string;
    byPref: Record<string, number>;
    /** Traveler + Full Time — the leading hypothesis for the flat 14 (#396). */
    fullTimeTravelers: number;
  }[];
}

// Unscoped project list for manpower UIs (import matching, forecast merge,
// leader pickers). The regular projectsApi.list is visibility-scoped to the
// caller's assignments, which empties cross-project matching — see API notes.
export interface MpProjectOption {
  id: number;
  jobNumber: string;
  title: string;
  isArchived: boolean;
  currentStartDate: string | null;
  /**
   * First day this job has PUBLISHED crew booked (#385) — the Scheduler's answer
   * to the same question `currentStartDate` answers for the PM. Null when no crew
   * is booked at all. Server-derived and window-independent, so the mismatch flag
   * is a property of the job rather than of whichever weeks are on screen.
   */
  firstCrewDate?: string | null;
  isMaintenance?: boolean;
  isLoanedSpToGc?: boolean;
  loanFromDivision?: { id: number; label: string } | null;
  projectStatus: { id: number; label: string } | null;
  division: { id: number; label: string } | null;
  /**
   * Bid forecast for SYSTAC pursuits (#330) — anticipated peak crew and
   * duration in calendar days, imported from Bid Tracker when the job was
   * moved to SYSTAC. Null on jobs whose bid did not carry them.
   */
  bidPeakCrew?: number | null;
  bidDurationDays?: number | null;
  /** Fallback for the forecast start when currentStartDate has been cleared. */
  originalStartDate?: string | null;
  /**
   * Travel job (#396) — the same `projects.travel_job` GET /manpower/schedule
   * carries. Needed on BOTH because the crew grid synthesizes a row from this
   * endpoint for any job with no crew in the visible window, and the
   * Travel/Local toggle reads whichever row it got.
   */
  travelJob?: boolean;
}

// One row per project that has manpower schedule entries — used by the
// Manpower ⇄ Projects sync tool to flag jobs that are finished on the
// Projects tab but still have crew booked going forward.
export interface MpSyncRow {
  projectId: number;
  jobNumber: string;
  title: string;
  isArchived: boolean;
  statusLabel: string | null;
  divisionLabel: string | null;
  futureEntries: number;
  futureCrew: number;
  lastCrewDate: string | null;
}

/** Traveling leaders needed on one job (#396 slice 2). `value` is what to
 *  show; `default` is the manpower lead's rule alone (1 under 7 peak crew, 2 at 7+); an
 *  override, when set, always wins. */
export interface LeadersNeeded {
  value: number;
  default: number;
  override: number | null;
  isOverride: boolean;
  /** Whether an override can be saved at all. False until the
   *  `projects.leaders_needed` migration has been applied to this database —
   *  the figures above are still correct (they are the computed default), but
   *  the write would fail, so the control must not be offered. Computed
   *  server-side so the button and the PATCH cannot disagree. */
  canOverride: boolean;
  /** Whether this is a travel job. Only the WORDING depends on it — the same
   *  figure is "traveling leaders needed" on a travel job and plain "leaders
   *  needed" on a local one. The rule and the override are identical. */
  travelJob: boolean;
}

/** One job's PUBLISHED scheduled crew, summarised for the project Manpower tab.
 *  `scheduled: false` means the job has no crew on the manpower grid at all —
 *  render nothing rather than a row of zeros. */
export type ProjectScheduleSummary =
  | {
      scheduled: false;
      /**
       * Still present on this branch (#385). `scheduled` means "has crew in the
       * displayable window", which a job whose only crew sits outside it fails
       * while still having a first booked day — and the grid would flag that job
       * from its own unbounded read. The two surfaces have to agree.
       */
      firstCrewDate: string | null;
      /** The PM's side of the same comparison — see the `scheduled: true` branch. */
      plannedStart: string | null;
      /** The job's status label, from the same read — see the `scheduled: true` branch. */
      statusLabel?: string | null;
    }
  | {
      scheduled: true;
      isFab: boolean;
      /** Server's local calendar day, so the client doesn't re-derive it. */
      today: string;
      /** Spans today → the job's last scheduled day, NOT the visible weeks. */
      ahead: {
        crewDays: number; peakCrew: number; days: number;
        firstDay: string | null; lastDay: string | null;
      };
      /** Week buckets from the current week's Sunday. `peak` is the week's
       *  peak DAY (crew doesn't add across days) — the rollup's definition. */
      weeks: { weekStart: string; peak: number; fabPeak: number; instPeak: number; crewDays: number }[];
      /** Present on EVERY scheduled job. It was travel-only while the gap view
       *  was; a local job's figure is now read on the Gap Analysis tab, so it
       *  has to be correctable from the job's own tab. */
      leadersNeeded: LeadersNeeded;
      /**
       * The job's FIRST booked crew day (#385) — the Scheduler's answer to the
       * question `projects.current_start_date` answers for the PM. Unlike
       * `ahead.firstDay` this looks backward as well as forward, because a job
       * whose crew started before the planned start is exactly the disagreement
       * worth surfacing. Only on the `scheduled: true` branch: a job with no crew
       * booked has nothing to disagree with.
       */
      firstCrewDate: string | null;
      /**
       * `projects.current_start_date` — the PM's side of the start-mismatch
       * comparison (#385), returned HERE rather than read off the project query.
       *
       * ⚠️ Both inputs come from one read so they are always from the same
       * moment. Taken from two independently-cached queries they can be from
       * different ones, and once this query began polling that became the normal
       * case — a fresh crew date against a stale plan can assert a disagreement
       * that was never true at any single point in time.
       */
      plannedStart: string | null;
      /**
       * The job's `projectStatus.label`, from the SAME read as the two dates,
       * so the Overview can skip a finished job the way the crew grid does
       * (#385, follow-up to #540) without mixing in the unpolled project cache.
       *
       * ⚠️ Optional because an API deployed before this field existed does not
       * send it; `overviewStartMismatch` falls back to the project's own label
       * then. `null` is a real answer — the job has no status — and is kept.
       */
      statusLabel?: string | null;
    };

/**
 * One week of the gap.
 *
 * ⚠️ Every `...Available` and `...Gap` is NULLABLE, and null is not zero. It
 * means the manpower lead has never filled that box in, so there is no supply figure to
 * subtract and therefore no shortfall to state. Rendering null as 0 would
 * report the entire demand as a gap — a number nobody claimed, in the
 * direction that triggers hiring.
 */
export interface GapAnalysisWeek {
  weekStart: string;
  totalNeeded: number;
  totalAvailable: number | null;
  totalGap: number | null;
  /**
   * What the week would need if every SYSTAC pursuit in it were won and staffed
   * to its bid — booked crew plus the bid forecast.
   *
   * ⚠️ POTENTIAL, NOT DEMAND, and no gap is computed from it. A shortfall is
   * something somebody acts on, and a shortfall against work nobody has been
   * awarded is how a hire gets started for a job that never lands.
   *
   * ⚠️ A COMBINED PEAK, not the sum of two peaks. Booked crew and the forecast
   * can peak on different days of the week, so this is the busiest single day
   * once both are counted — always >= the measured figure, and equal to it when
   * the week holds no pursuits, so the difference is safe to take as the
   * stacked increment.
   */
  totalNeededWithSystac: number;
  travelersNeeded: number;
  travelersAvailable: number | null;
  travelerGap: number | null;
  travelersNeededWithSystac: number;
  travelingLeadersNeeded: number;
  travelingLeadersAvailable: number | null;
  travelingLeaderGap: number | null;
  activeTravelJobs: number;
  /** ⚠️ Its own peak day, so this will not always equal totalNeeded −
   *  travelersNeeded: the busiest day for the company need not be the busiest
   *  day for travelers. It is summed in SQL rather than inferred here. */
  localCrewNeeded: number;
  localCrewAvailable: number | null;
  localCrewGap: number | null;
  /** See totalNeededWithSystac — same rule, local jobs only. */
  localCrewNeededWithSystac: number;
  /** Same rule as the traveling figure — 1 leader under 7 peak crew, 2 at 7+,
   *  summed across the jobs live that week and never peaked by day. */
  localLeadersNeeded: number;
  localLeadersAvailable: number | null;
  localLeaderGap: number | null;
  activeLocalJobs: number;
  /** 0–1. 0 when totalNeeded is 0, never NaN. */
  travelerShareOfDemand: number;
}

/** One of the manpower lead's seven boxes. `value` is null until first filled in. */
export interface GapAnalysisMetric {
  key: string;
  label: string;
  group: 'total' | 'traveler' | 'half' | 'local';
  role: 'laborer' | 'leader' | null;
  value: number | null;
  /** The five that drive a gap line — total, both FT traveler boxes, both
   *  Local boxes. Only the 50/50 pair is shown beside them counting toward
   *  nothing, which is the manpower lead's rule about not over-committing them. */
  countsTowardGap: boolean;
}

export interface GapAnalysisJob {
  projectId: number;
  jobNumber: string | null;
  title: string | null;
  /** Best effort, parsed from the free-text job address — there is no
   *  structured state field on the project. Null when nothing recognisable
   *  was found. */
  state: string | null;
  /** The job's division. Sent whole rather than as a computed `isFab` so the
   *  client keeps one definition of Fabrication (`isFabDivision`). */
  division: { id: number; label: string | null } | null;
  leadersNeeded: number;
  isLeadersOverride: boolean;
  /** Leaders linked to the job through mp_leaders — `upcoming` marks one lined
   *  up for it rather than on it today. Both count as staffed; an empty list is
   *  what the view calls unstaffed. */
  leaders: { name: string; upcoming: boolean }[];
  weeklyCrew: { weekStart: string; peak: number }[];
}

/**
 * One SYSTAC pursuit — work we might win — as a row in the job tables.
 *
 * ⚠️ `weeklyCrew` HERE IS NOT THE CHART'S YELLOW BAND. The band is how much
 * higher a WEEK's peak would go, a difference of two peaks that can fall on
 * different days; this is what this bid asks for, on its own, which is the
 * question the band cannot answer. They will often differ, and both are right.
 *
 * ⚠️ NO `leadersNeeded`, deliberately. A bid states crew and duration and says
 * nothing whatever about leaders — the same reason the leadership charts carry
 * no band and the weekly leader columns do not move with the toggle.
 */
export interface GapAnalysisPursuit {
  projectId: number;
  jobNumber: string | null;
  title: string | null;
  /** Best effort, parsed from the free-text job address — as for a real job. */
  state: string | null;
  division: { id: number; label: string | null } | null;
  /** The estimator's anticipated peak crew, spread flat across the run. */
  bidPeakCrew: number;
  startDate: string;
  /** Calendar days, which is what the bid records — weekends are not worked. */
  durationDays: number;
  /** The bid peak in each week the run works, 0 in the others. Flat, never a
   *  week's man-days: the bid gives a peak, not a curve. */
  weeklyCrew: { weekStart: string; peak: number }[];
}

export interface GapAnalysis {
  weeks: GapAnalysisWeek[];
  /**
   * The same weeks with the Fabrication division dropped from every demand
   * figure — the charts, the weekly table and the workbook all read this one
   * while "Hide Fabrication jobs" is on, which is the default.
   *
   * ⚠️ IT CANNOT BE DERIVED FROM `weeks` HERE, which is why the API sends it.
   * Every figure is a peak of daily sums, and taking Fabrication out of a
   * week's peak by subtracting its own weekly peak reports a day nobody works
   * whenever the two fall on different days. Only the server has the day-level
   * series to re-peak.
   *
   * Supply is identical in both: the manpower lead types one field headcount and there is no
   * Fabrication box to subtract from it.
   */
  weeksExcludingFab: GapAnalysisWeek[];
  /**
   * ⚠️ Split on `projects.travel_job` and on nothing else — the flag the crew
   * grid's Type toggle and the project Overview both write. Every active job
   * is in exactly one of these two lists.
   */
  travelJobs: GapAnalysisJob[];
  localJobs: GapAnalysisJob[];
  /**
   * The SYSTAC pursuits, split by the same travel flag.
   *
   * ⚠️ SEPARATE FROM the two lists above, which mean "jobs with crew on the
   * schedule" — every consumer of them is counting booked work. The tab shows
   * these only when the pursuits are switched on; the payload does not change
   * shape with a checkbox.
   */
  systacTravelJobs: GapAnalysisPursuit[];
  systacLocalJobs: GapAnalysisPursuit[];
  /**
   * The supply side — the manpower lead's seven boxes, typed and owned by them.
   *
   * ⚠️ `derived` is false and stays false. Nothing computes these, and
   * `mp_travel_roster` deliberately does not feed them: the roster is a
   * snapshot and Paycor lags, while the manpower lead knows first. `updatedAt` is the
   * newest save across the seven and is what the tab shows so everyone else
   * can see how current the numbers are.
   */
  supply: {
    derived: false;
    basis: string;
    updatedAt: string | null;
    totalAvailable: number | null;
    travelersAvailable: number | null;
    travelingLeadersAvailable: number | null;
    localAvailable: number | null;
    localLeadersAvailable: number | null;
    metrics: GapAnalysisMetric[];
  };
}

/** Weekly planned vs actual crew-days for one job, looking backward.
 *  Unit is crew-days on both sides: planned = scheduled head count summed over
 *  the week; actual = distinct people with field hours, summed the same way. */
export type PlanVsActual =
  | { available: false; jobNumber: string | null }
  | {
      available: true;
      jobNumber: string | null;
      /** Newest date in the Paycor feed. Both sides are truncated here, so the
       *  current week isn't reported as a shortfall for want of payroll data. */
      dataThrough: string | null;
      weeks: {
        weekStart: string; planned: number; actual: number; variance: number;
        /** False when the whole week is past the feed cutoff — unknown, not zero. */
        comparable: boolean;
        /** Week is only partly inside the feed. */
        partial: boolean;
      }[];
      totals: { planned: number; actual: number; variance: number };
    };

/** Unpublished crew-schedule drafts, split by ownership. */
export interface DraftStatus {
  /** Cells YOU staged — exactly what your Publish will fold in. */
  count: number;
  /** When you last staged one, or null. */
  lastUpdatedAt: string | null;
  /** Cells staged by other schedulers. You can't publish or discard these. */
  othersCount: number;
  /** Display names of those other schedulers (empty when othersCount is 0). */
  otherEditors: string[];
}

/** One entry in the company-wide crew-schedule audit stream (#350). */
export interface ScheduleHistoryRow {
  id: number;
  at: string;
  /** Activity slug — 'crew_schedule_published' | 'crew-future-cleared' | 'crew-cell-edited'. */
  action: string;
  /** Human-readable summary written when the change happened, e.g.
   *  "9 crew days updated (2026-08-04 → 2026-08-29)". Null is possible on old
   *  rows, so the view must not assume it. */
  detail: string | null;
  /** Display name of whoever made the change; null if the user was removed. */
  by: string | null;
  projectId: number | null;
  jobNumber: string | null;
  projectTitle: string | null;
}

export interface ScheduleHistory {
  rows: ScheduleHistoryRow[];
  /** True when another page exists past the current limit/offset. */
  hasMore: boolean;
  /** The last row of THIS page. Send it back as cursorAt/cursorId to get the
   *  next one. Null when there is no next page. Keyset rather than an offset:
   *  an event arriving mid-read then lands in its own place instead of shifting
   *  every row and showing one change twice.
   *
   *  ⚠️ `at` is SQL Server's own ISO form (no `Z`), NOT a JS ISO string. It
   *  carries the column's full sub-millisecond precision, which a Date round
   *  trip would truncate — dropping every row tied to the boundary. Pass it
   *  back verbatim; do not parse or reformat it. */
  nextCursor: { at: string; id: number } | null;
  actors: { id: string; name: string }[];
  projects: { id: number; jobNumber: string | null; title: string | null }[];
}

/** Manpower request vocabularies (#303 / #420). Mirrors the API's own
 *  constants in `manpower-requests.service.ts` — keep the two in step. */
export type MpRequestType = 'pm-change' | 'systac-forecast' | 'pco' | 'co';
export type MpRequestStatus = 'received' | 'reviewed' | 'booked' | 'declined' | 'archived';
export type MpRequestSource = 'in-app' | 'formstack';

/** One manpower request — somebody asking for a job's crew to change. */
export interface MpRequest {
  id: number;
  /** Where it came from. 'formstack' means imported history (slice 6). */
  source: MpRequestSource;
  requestType: MpRequestType;
  /** 'initial' | 'change'. Null on imported rows — the old form never asked. */
  stage: string | null;

  projectId: number | null;
  /** ⚠️ The job number AS SUBMITTED, which is not always the matched job's own
   *  — a real submission typed 10854 for four months while meaning 10923. Show
   *  this next to `pmtJobNumber` rather than instead of it. */
  jobNumber: string | null;
  jobName: string | null;
  pmtJobNumber: string | null;
  projectTitle: string | null;

  changeOrderId: number | null;
  /** The change's number and kind (PCO-003 / PCO), for pco and co requests. */
  changeNo: string | null;
  changeType: string | null;

  /** ⚠️ FREE TEXT, never a number. '1-2', 'Whole crew' and a 71-character
   *  breakdown are all real values. Render it as given; never parse it. */
  crewSize: string | null;
  /** 'fabrication' | 'installation' | null — matches the crew grid's phases. */
  phase: string | null;
  startDate: string | null;
  endDate: string | null;
  reason: string | null;
  staffingNotes: string | null;

  status: MpRequestStatus;
  schedulerNote: string | null;
  decidedAt: string | null;
  decidedBy: string | null;

  submittedBy: string | null;
  pm: string | null;

  requestedOn: string | null;
  submittedAt: string | null;
  createdAt: string;
}

export interface MpRequestList {
  rows: MpRequest[];
  hasMore: boolean;
  /** Keyset cursor — same contract as ScheduleHistory's above. `at` is SQL
   *  Server's own ISO form; pass it back verbatim, never through a Date. */
  nextCursor: { at: string; id: number } | null;
  /** Counts over the WHOLE table, not narrowed by the active filters — so
   *  picking one status never makes the others look empty. */
  statusCounts: Record<MpRequestStatus, number>;
  /** received + reviewed: what still needs a scheduler. */
  openCount: number;
}

/**
 * Pre-fill for the Manpower Request form (#303 slice 2). The whole reason the
 * form lives in PMT: it opens knowing the job instead of asking for a number.
 */
export interface MpRequestContext {
  projectId: number;
  jobNumber: string;
  title: string | null;
  pm: { id: string; name: string | null } | null;
  /** Fabrication-division job — the form offers a shop/field phase. */
  isFab: boolean;
  /** Published crew already exists on the grid. */
  hasPublishedCrew: boolean;
  /** 'change' when crew is booked, else 'initial'. The field Formstack never had. */
  suggestedStage: 'initial' | 'change';
  /** 'systac' = bid, refused for everything but a forecast; 'closed-won' = won,
   *  no Sage # yet, ALLOWED (decision 2); 'real'. */
  lifecycle: 'systac' | 'closed-won' | 'real';
  /** ONE rule, computed server-side: primary PM (or, on a SYSTAC bid, the
   *  estimator — #386), manpower.request_change, or Admin. */
  canRequest: boolean;
  /** The bid's estimator, when one is recorded. The owner of a SYSTAC forecast update. */
  estimator: { id: string; name: string | null } | null;
  /** The ONE type this job takes: 'systac-forecast' on a bid, else 'pm-change'.
   *  The form sends it; it never offers a choice. */
  requestType: MpRequestType;
  /** On a SYSTAC bid, the forecast the Tracker already holds (Bid Tracker
   *  import) — the estimator's pre-fill. Null off a bid, or before the import
   *  has landed anything. */
  forecast: { peakCrew: number | null; durationDays: number | null; startDate: string | null } | null;
  /** The job's change orders a crew request may be raised against (not
   *  rejected). `executed` = linked to an Intacct CO, which makes the request
   *  `co` rather than `pco`. Empty on a bid. Never carries amounts. */
  changes: MpRequestChangeRef[];
  creatableTypes: MpRequestType[];
}

export interface MpRequestChangeRef {
  id: number;
  changeNo: string;
  type: string;            // PCO | COR | OCO
  status: string;          // pending | approved
  description: string | null;
  executed: boolean;
  /** Crew has been requested for THIS change before. The change's own stage
   *  default — the job-wide "has published crew" says nothing about a change. */
  hasRequest: boolean;
  suggestedStage: 'initial' | 'change';
}

export interface MpRequestCreate {
  projectId: number;
  requestType: MpRequestType;
  /** The change order the crew is for — required for pco / co, absent otherwise. */
  changeOrderId?: number | null;
  stage: 'initial' | 'change';
  /** ⚠️ FREE TEXT — "2-3" is a valid answer. Never coerce. */
  crewSize: string;
  phase?: 'fabrication' | 'installation' | null;
  startDate?: string | null;
  endDate?: string | null;
  reason: string;
  staffingNotes?: string | null;
}

/** A scheduler's triage (#303 slice 3). The legal moves live in
 *  manpower/lib/requestLifecycle.ts, mirroring the API's table. 'booked'
 *  RECORDS that the crew was entered on the grid by hand — it never enters it. */
export interface MpRequestDecision {
  status: 'reviewed' | 'booked' | 'declined';
  /** Required when declining or reopening; replaces the existing note when given. */
  schedulerNote?: string | null;
}

export const manpowerApi = {
  schedule: (from?: string, to?: string) =>
    api.get('/manpower/schedule', { params: { from, to } }).then((r) => r.data),
  // Draft layer (scheduler's edit mode): staged edits overlay the published
  // schedule until published. All draft calls require manpower.edit.
  //
  // `count` is the caller's OWN staged cells — what their Publish will fold in.
  // `othersCount` / `otherEditors` describe work staged by other schedulers,
  // which they can neither publish nor discard but must be able to see.
  scheduleDraft: (from?: string, to?: string) =>
    api.get('/manpower/schedule/draft', { params: { from, to } }).then((r) => r.data),
  /** Dates in a range where one project already carries crew. Lets the bulk
   *  crew-setup dialog count what it would overwrite BEYOND the visible grid
   *  window, and the job shift move the whole run rather than just the part
   *  the scheduler happens to be looking at — neither of which the windowed
   *  schedule feed can answer. */
  projectCrewDays: (projectId: number, from: string, to: string, draft = false) =>
    api.get('/manpower/schedule/project-crew-days', { params: { projectId, from, to, draft: draft ? 1 : undefined } })
      .then((r) => r.data as { workDate: string; phase: string | null; crewCount: number; isOverride: boolean }[]),
  /** PUBLISHED scheduled crew for one job, summarised for the project Manpower
   *  tab. Weekly figures are the week's PEAK day, matching the rollup grid.
   *  Never includes drafts. */
  projectScheduleSummary: (projectId: number, weeks = 8) =>
    api.get('/manpower/schedule/project-summary', { params: { projectId, weeks } })
      .then((r) => r.data as ProjectScheduleSummary),
  /** Weekly planned vs actual crew-days, looking backward. Companion to
   *  projectScheduleSummary, which looks forward. */
  projectPlanVsActual: (projectId: number, weeks = 8) =>
    api.get('/manpower/schedule/plan-vs-actual', { params: { projectId, weeks } })
      .then((r) => r.data as PlanVsActual),
  /** Recent crew-schedule publishes for one job — the "schedule updated" notice
   *  on the project Overview. Readable by any PM who can see the project; it
   *  does not require manpower edit rights. */
  recentScheduleChanges: (projectId: number, limit = 5) =>
    api.get('/manpower/schedule/recent-changes', { params: { projectId, limit } })
      .then((r) => r.data as { at: string; by: string | null; detail: string | null }[]),
  /** Company-wide crew-schedule change history (#350) — the Manpower History
   *  view. Same gate as the crew grid: authenticated, no manpower.edit needed.
   *  `actors`/`projects` are the filter options for the current date window and
   *  are NOT narrowed by the other filters, so picking one never empties the
   *  list you would use to pick another. */
  scheduleHistory: (params: {
    projectId?: number; actorId?: string; from?: string; to?: string;
    cursorAt?: string; cursorId?: number; limit?: number;
  } = {}) =>
    api.get('/manpower/schedule/history', { params })
      .then((r) => r.data as ScheduleHistory),
  /** Manpower requests (#303 / #420) — the scheduler inbox. Same gate as the
   *  crew grid: authenticated, no manpower.edit needed, because a PM has to be
   *  able to see the request they raised. Read-only until slice 3. */
  requests: (params: {
    status?: MpRequestStatus; open?: boolean; requestType?: MpRequestType;
    source?: MpRequestSource; projectId?: number;
    cursorAt?: string; cursorId?: number; limit?: number;
  } = {}) =>
    api.get('/manpower/requests', {
      params: { ...params, open: params.open ? 1 : undefined },
    }).then((r) => r.data as MpRequestList),
  request: (id: number) =>
    api.get(`/manpower/requests/${id}`).then((r) => r.data as MpRequest),
  /** Pre-fill for the request form, plus whether the caller may raise one. */
  requestContext: (projectId: number) =>
    api.get('/manpower/requests/new-context', { params: { projectId } })
      .then((r) => r.data as MpRequestContext),
  /** Raise a request. Never writes the crew grid — a scheduler books it. */
  createRequest: (body: MpRequestCreate) =>
    api.post('/manpower/requests', body).then((r) => r.data as MpRequest),
  /** Triage a request — manpower.edit only. A 409 means someone else moved it
   *  first: refetch and show the row again rather than retrying blind. */
  decideRequest: (id: number, body: MpRequestDecision) =>
    api.patch(`/manpower/requests/${id}`, body).then((r) => r.data as MpRequest),
  /** How many requests still need a scheduler — the badge on the view switcher. */
  openRequestCount: () =>
    api.get('/manpower/requests/open-count').then((r) => r.data as { open: number }),
  draftStatus: () =>
    api.get('/manpower/schedule/draft-status').then((r) => r.data as DraftStatus),
  upsertDraftEntry: (data: unknown) =>
    api.post('/manpower/schedule/draft/entries', data).then((r) => r.data as DraftStatus),
  bulkDraftEntries: (entries: unknown[]) =>
    api.post('/manpower/schedule/draft/entries/bulk', { entries }).then((r) => r.data as DraftStatus),
  publishDrafts: () =>
    api.post('/manpower/schedule/draft/publish').then((r) => r.data as { published: number }),
  resetDrafts: () =>
    api.delete('/manpower/schedule/draft').then((r) => r.data as { discarded: number }),
  syncStatus: (from?: string) =>
    api.get('/manpower/sync-status', { params: from ? { from } : {} })
      .then((r) => r.data as MpSyncRow[]),
  clearFutureEntries: (projectId: number, from?: string) =>
    api.delete(`/manpower/schedule/project/${projectId}/future`, { params: from ? { from } : {} })
      .then((r) => r.data as { deleted: number }),
  projectOptions: (includeArchived = false) =>
    api.get('/manpower/project-options', { params: includeArchived ? { includeArchived: 1 } : {} })
      .then((r) => r.data as MpProjectOption[]),
  upsertEntry: (data: unknown) =>
    api.post('/manpower/schedule/entries', data).then((r) => r.data),
  bulkImport: (entries: BulkImportEntry[]) =>
    api.post('/manpower/schedule/import', { entries }).then((r) => r.data as BulkImportResult),
  leaders: (divisionId?: number) =>
    api.get('/manpower/leaders', { params: { divisionId } }).then((r) => r.data as MpLeader[]),
  createLeader: (data: MpLeaderInput) =>
    api.post('/manpower/leaders', data).then((r) => r.data as MpLeader),
  updateLeader: (id: number, data: Partial<MpLeaderInput>) =>
    api.patch(`/manpower/leaders/${id}`, data).then((r) => r.data as MpLeader),
  deactivateLeader: (id: number) =>
    api.delete(`/manpower/leaders/${id}`).then((r) => r.data),
  /** Travel roster (#396 slice 1) — the supply side of the gap analysis. */
  travelRoster: (params: {
    travelPref?: TravelPref;
    divisionId?: number;
    availableOn?: string;
    includeInactive?: boolean;
    limit?: number;
    offset?: number;
  } = {}) =>
    api.get('/manpower/travel-roster', { params }).then((r) => r.data as TravelRosterList),
  travelRosterSupply: (weeks = 8) =>
    api.get('/manpower/travel-roster/supply', { params: { weeks } })
      .then((r) => r.data as TravelRosterSupply),
  createTravelRosterRow: (data: TravelRosterInput) =>
    api.post('/manpower/travel-roster', data).then((r) => r.data as TravelRosterRow),
  updateTravelRosterRow: (id: number, data: Partial<TravelRosterInput>) =>
    api.patch(`/manpower/travel-roster/${id}`, data).then((r) => r.data as TravelRosterRow),
  deactivateTravelRosterRow: (id: number) =>
    api.delete(`/manpower/travel-roster/${id}`).then((r) => r.data),
  /**
   * Travel Acknowledgments. Both routes are gated on `manpower.edit` — the
   * list returns the tokens, and a token is the whole credential on the public
   * signing page, so reading the list is exactly as powerful as issuing one.
   */
  travelAcknowledgments: (projectId: number) =>
    api.get(`/manpower/projects/${projectId}/travel-acknowledgments`)
      .then((r) => r.data as TravelAcknowledgmentRow[]),
  /**
   * A signed acknowledgment as a PDF, built on the server from the frozen record.
   *
   * ⚠️ THE ERROR IS READ OUT OF THE BLOB. With `responseType: 'blob'` a refusal's
   * JSON body arrives as a Blob too, and `toApiError` then finds no `message` and
   * says "Request failed with status code 409" — losing the one sentence that
   * says WHY (not signed; no frozen terms). So a failed response is read as text
   * here and its message kept.
   */
  travelAcknowledgmentPdf: async (id: number) => {
    const r = await api.get(`/manpower/travel-acknowledgments/${id}/pdf`, {
      responseType: 'blob', validateStatus: () => true,
    });
    if (r.status >= 200 && r.status < 300) return r.data as Blob;
    throw await blobRefusal(r, `The PDF could not be made (HTTP ${r.status}).`);
  },
  /** Record where the PDF was filed. The first filing stands; a different second link is a 409. */
  markTravelAcknowledgmentFiled: (id: number, url: string) =>
    api.post(`/manpower/travel-acknowledgments/${id}/filed`, { url })
      .then((r) => r.data as { filed: true; filedUrl: string }),
  /**
   * ⚠️ MINTS A NEW LINK EVERY TIME — it is not idempotent, and must not be.
   * A changed travel band means a new acknowledgment rather than a rewritten
   * one, because the old row records what somebody actually agreed to. Callers
   * show the existing links first so a second click is a decision.
   *
   * Refuses with a 400 when the entitlement cannot be stated; the message
   * carries the blocker reason, which names who can fix it.
   */
  createTravelAcknowledgment: (rosterId: number, projectId: number) =>
    api.post(`/manpower/travel-roster/${rosterId}/acknowledgment/${projectId}`)
      .then((r) => r.data as {
        id: number;
        token: string;
        /**
         * ⚠️ WHAT THE ENGINE COULD NOT SETTLE, AND THE CALLER MUST SHOW.
         * `caveatsFor` (#536) raises these on a STATED entitlement — an
         * unresolved job title that happens not to matter on this band, and
         * the exempt-status warning it raises on every management person,
         * because the handbook scopes travel pay to hourly non-exempt staff
         * and nothing in the Project Tracker records exempt status.
         *
         * The public signing page deliberately withholds them: they are
         * written for somebody checking pay, and on a form being signed they
         * read as the company hedging. This response is the only place they
         * reach a human, so a caller that drops it silently discards the
         * whole mechanism.
         */
        caveats?: string[];
      }),
  config: () =>
    api.get('/manpower/config').then((r) => r.data as Record<string, string | null>),
  updateConfig: (key: string, value: string) =>
    api.put(`/manpower/config/${key}`, { value }).then((r) => r.data),
  /** The typed Temps active box (#584). `count` is null until it is entered.
   *  Written with updateConfig(TEMPS_ACTIVE_KEY, …). */
  tempsActive: () =>
    api.get('/manpower/temps-active').then((r) => r.data as { count: number | null; updatedAt: string | null }),
  /** The typed Subcontractors box (#688), the same shape as tempsActive.
   *  Written with updateConfig(SUBCONTRACTORS_ACTIVE_KEY, …). */
  subcontractorsActive: () =>
    api.get('/manpower/subcontractors-active').then((r) => r.data as { count: number | null; updatedAt: string | null }),
  // Maintenance sub-projects (schedule-only) + row flags.
  createSubProject: (parentId: number, name: string) =>
    api.post(`/manpower/projects/${parentId}/sub-projects`, { name }).then((r) => r.data as { id: number; jobNumber: string; title: string; parentProjectId: number; divisionId: number | null }),
  deleteSubProject: (id: number) =>
    api.delete(`/manpower/sub-projects/${id}`).then((r) => r.data as { deleted: boolean }),
  setMaintenance: (projectId: number, isMaintenance: boolean) =>
    api.patch(`/manpower/projects/${projectId}/maintenance`, { isMaintenance }).then((r) => r.data),
  setGcLoan: (projectId: number, isLoaned: boolean, loanFromDivisionId?: number | null) =>
    api.patch(`/manpower/projects/${projectId}/gc-loan`, { isLoaned, loanFromDivisionId }).then((r) => r.data),
  /** Classify a job Travel or Local (#396). Writes projects.travel_job — the
   *  same field the project Overview edits — so the job reads the same way
   *  everywhere. manpower.edit only; refused on a sub-project, which takes its
   *  classification from its parent. */
  setTravelJob: (projectId: number, travelJob: boolean) =>
    api.patch(`/manpower/projects/${projectId}/travel-job`, { travelJob }).then((r) => r.data as { projectId: number; travelJob: boolean }),
  /** Leaders needed (#396 slice 2), travel and local jobs alike. null clears
   *  the override and falls back to the default. manpower.edit only. */
  setLeadersNeeded: (projectId: number, value: number | null) =>
    api.patch(`/manpower/projects/${projectId}/leaders-needed`, { value }).then((r) => r.data as { projectId: number; leadersNeeded: number | null }),
  /** The gap view (#396 slice 3). Ungated beyond auth, matching the crew grid
   *  and the Leaders tab. */
  gapAnalysis: (weeks = 8) =>
    api.get('/manpower/schedule/gap-analysis', { params: { weeks } }).then((r) => r.data as GapAnalysis),
};

export const usersApi = {
  me: () => api.get('/users/me').then((r) => r.data as { id: string; displayName: string | null; email: string | null; roles: string[]; permissions: string[]; divisions: number[]; canViewAll: boolean; impersonating: boolean; realName: string | null; hasActiveAssignments: boolean }),
  /**
   * Every user, with `deletedAt` set on those who have been removed.
   *
   * ⚠️ Removed people are INCLUDED on purpose — this list also resolves a
   * stored id back to a name, and a picker that hides them must still keep
   * whoever is already selected, or saving silently reassigns the field.
   */
  list: () => api.get('/users').then((r) => r.data as { id: string; displayName: string | null; email: string | null; deletedAt: string | null }[]),
};

// ─── In-app feedback ───────────────────────────────────────────────────────────
export type FeedbackCategory = 'bug' | 'idea' | 'question';
export interface FeedbackAttachmentMeta { id: number; fileName: string; contentType: string | null; sizeBytes: number | null }
/** One reply on a feedback item. The author's id stays on the server: nothing showing a thread needs it. */
export interface FeedbackReply { id: number; authorName: string | null; body: string; createdAt: string }
export type FeedbackCloseReason = 'completed' | 'not_planned' | 'duplicate';
/**
 * Whether other people may read a feedback item: `unreviewed` (the default: admins only, and what everything sent before the board
 * is, and all new feedback not ticked private), `public` (on the board), `private` (the sender asked to keep it private: no admin action changes it), `hidden` (an admin took
 * it off the board).
 */
export type BoardVisibility = 'unreviewed' | 'public' | 'private' | 'hidden';
export interface FeedbackItem {
  id: number; category: FeedbackCategory; message: string; page: string | null;
  submittedBy: string | null; submitterName: string | null; submitterEmail: string | null; submitterRoles: string | null;
  status: 'open' | 'closed'; githubIssueNumber: number | null; githubIssueUrl: string | null;
  createdAt: string; updatedAt: string; attachments?: FeedbackAttachmentMeta[];
  closedAt: string | null; closedReason: 'completed' | 'not_planned' | 'duplicate' | null;
  /** What GitHub said about the issue the last time the sync looked. */
  githubState: 'open' | 'closed' | null; githubSyncedAt: string | null;
  /** A change made here that has not reached GitHub yet. */
  githubPushPending: boolean;
  /** What has been said to the people following the item. Optional so an older response, or a fixture, still fits. */
  replies?: FeedbackReply[];
  /** How many people follow it, counting only accounts that can still be told. `null`, or absent, means the API could not read the conversation (the replies-and-followers migration has not been run there), which is not the same as nobody. */
  followerCount?: number | null;
  /**
   * How many of those are NOT the person who sent it, counting only accounts that can still be told: the people a Hide leaves hearing
   * about it, and that "also stop telling them" removes. `null`, or absent, means it could not be read (as for `followerCount`).
   */
  otherFollowerCount?: number | null;
  /** `null`, or absent, means the API could not say (the board migration has not been run there): UNKNOWN, never "unreviewed". */
  boardVisibility?: BoardVisibility | null;
  /**
   * The id of the item an admin MERGED this one into (it was combined with another request), else `null`. A merged item is closed as a duplicate and
   * hidden, and can be neither merged again nor shown on the board. `null`, or absent, reads as "not merged": it is also what an API that could not
   * read the column sends, and where the column is missing nothing can have been merged.
   */
  duplicateOf?: number | null;
}
/** One item the signed-in person follows, from `GET /feedback/mine`: an explicit field list, so nothing about the submitter or GitHub. */
export interface MyFeedbackItem {
  id: number;
  category: FeedbackCategory;
  message: string;
  status: 'open' | 'closed';
  closedAt: string | null;
  closedReason: FeedbackCloseReason | null;
  createdAt: string;
  /** A reply by somebody else, or the close, newer than the last time this person opened the item. */
  unread: boolean;
  /**
   * Whether this person may stop following it: their follow is a "me too", which is the only kind the API lets them undo. Absent (an API
   * that does not say yet) reads as "no", so nothing is offered that may not work.
   */
  canStopFollowing?: boolean;
  /**
   * I follow it because an admin combined something I follow into it (a request I sent, or said "me too" to, that was the same as this one), not because I
   * chose to: the screen says why. Absent reads as "no".
   */
  addedByMerge?: boolean;
  /**
   * For an item an admin combined into another one: that other item's id, else `null`. A number and nothing more (no text of the other item comes with
   * it). Absent, or `null`, reads as "not combined", which is also what an API that could not read it sends.
   */
  combinedInto?: number | null;
  replies: FeedbackReply[];
}
/**
 * One item on the shared board, from `GET /feedback/board`: an explicit field list that never says who sent it, or their page,
 * attachments or GitHub issue. Replies are on the board too, with their authors' names, and `followerCount` counts the person who sent
 * it and everyone who said "me too" ("N people want this").
 */
export interface BoardItem {
  id: number;
  category: FeedbackCategory;
  message: string;
  status: 'open' | 'closed';
  closedAt: string | null;
  closedReason: FeedbackCloseReason | null;
  createdAt: string;
  followerCount: number;
  /** I follow it: I sent it, or I said "me too". */
  following: boolean;
  /** I am the person who sent it. The only thing the board says about who sent an item, and true only for me. */
  sentByMe: boolean;
  /**
   * I follow it because an admin combined something I follow into it, not because I said "me too": the screen says why, and offers "Stop following"
   * and not "Undo me too". Never true of an item I sent. Absent reads as "no".
   */
  addedByMerge?: boolean;
  replies: FeedbackReply[];
}
/** What following or unfollowing answers: whether I follow it now, and how many people do. */
export interface FollowState {
  following: boolean;
  followerCount: number;
}
/** What stopping an item's followers answers: mirrors `StoppedFollowers` in the API's `feedback-board.service.ts`. */
export interface StoppedFollowers {
  id: number;
  /** How many people (accounts that have not been removed) were removed: the ones who could still have been told. */
  removed: number;
  /** How many follows were deleted in all, which includes accounts that have since been removed. */
  total: number;
}
/**
 * What one id in a "Show on board" / "Hide" request came to: mirrors `VisibilityResult` in the API's `feedback-board.service.ts`, and
 * `feedbackBoardContract.spec.ts` fails if the two stop agreeing. `visibility` is what the item is NOW (`null` = it does not exist, or
 * the write failed and nobody can say).
 */
export interface VisibilityResult {
  id: number;
  visibility: BoardVisibility | null;
  outcome: 'changed' | 'unchanged' | 'refused' | 'not_found' | 'conflict' | 'error';
  /**
   * For `refused`: the sender asked for privacy (`private`), the value was not one the API knows (`unknown`), or the item was merged into another request
   * (`merged`) and is not shown again.
   */
  reason?: 'private' | 'unknown' | 'merged';
  /** For an item just hidden: how many people other than the sender follow it, and so KEEP it. */
  otherFollowers?: number;
  /**
   * For an item just hidden with "also stop telling them": how many people were removed. Then `otherFollowers` is absent. The removal is
   * part of the hide, so an item it could not be done for is NOT hidden: it is an `error`.
   */
  removedFollowers?: number;
  /** For an item just shown: how many replies are now public with it. */
  publishedReplies?: number;
}
/**
 * What `POST /feedback/:id/merge` answers: mirrors `MergeResult` in the API's `feedback-merge.service.ts`, and `feedbackBoardContract.spec.ts` fails if the two stop
 * agreeing. It is only ever the answer to a merge that HAPPENED: every refusal is a 4xx with its reason in words.
 */
export interface MergeResult {
  /** The item that was merged. */
  id: number;
  /** The item it went into: the one chosen, or, if THAT had itself been merged, the one it was merged into. */
  mergedInto: number;
  /** What was chosen. */
  requestedInto: number;
  /** How many people now follow `mergedInto` because of this merge. */
  followersMoved: number;
  /** How many of the people the merged item had (its followers, and its sender) were NOT added: they already follow the other item, or the account has been removed. */
  notMoved: number;
  /** Whether this merge closed the item. False when it had already been closed as a duplicate (on GitHub, say). */
  closed: boolean;
  github: {
    /** The "Duplicate of #N" comment on the merged item's issue. `skipped`: it has no issue, or GitHub is not connected. */
    comment: 'posted' | 'failed' | 'skipped';
    /** Closing the issue: `pushed`, `pending` (saved here, retried by the nightly check), `skipped`, `superseded`, or `not_needed` (no issue, or already closed). */
    close: 'pushed' | 'pending' | 'skipped' | 'superseded' | 'not_needed';
  };
}
/** What `POST /feedback/:id/replies` answers: whether the reply also reached the GitHub issue (`null` = there was nowhere to copy it). */
export interface FeedbackReplyResult { reply: FeedbackReply; githubMirrored: boolean | null }
/** What one run of "Sync with GitHub" did — mirrors ReconcileSummary in the API's feedback-sync.service.ts. */
export interface FeedbackSyncSummary {
  /** False when GitHub is not connected for this environment: nothing was attempted. */
  configured: boolean;
  pushed: number; pushFailed: number; pushErrors: string[]; closed: number; reopened: number; baselined: number;
  conflicts: { id: number; issueNumber: number }[];
  notOnGitHub: number;
  /** Items that have an issue and were checked. */
  checked: number;
  /** False when GitHub's list failed or was cut short. */
  complete: boolean;
  error?: string;
}
export const feedbackApi = {
  /**
   * `keepPrivate` is the "Keep this private" tick: `true` keeps the item off the shared board for good (an admin cannot show it). Without it the
   * item is left for the admins to review, who read it first and share it on the board only if they choose to: NEW FEEDBACK IS NOT PUBLIC BY
   * DEFAULT. ⚠️ THE TYPE IS `true`, NOT `boolean`, ON PURPOSE: `false` is never sent, because an API that has not been updated reads an
   * explicit `false` as "put it on the board", which is the one thing this client must not be able to ask of it.
   */
  submit: (data: { category: FeedbackCategory; message: string; page?: string; files?: File[]; keepPrivate?: true }) => {
    const fd = new FormData();
    fd.append('category', data.category);
    fd.append('message', data.message);
    if (data.page) fd.append('page', data.page);
    if (data.keepPrivate) fd.append('keepPrivate', 'true');
    (data.files ?? []).forEach((f) => fd.append('files', f, f.name));
    // Content-Type undefined → the browser sets multipart/form-data with the boundary.
    return api.post('/feedback', fd, { headers: { 'Content-Type': undefined } as any }).then((r) => r.data as FeedbackItem);
  },
  list: () => api.get('/feedback').then((r) => r.data as FeedbackItem[]),
  /**
   * Open or close an item. `reason` is how it was closed (done, or won't do); `note` is what to tell the people following it, only
   * with a close: it is stored as a reply and is the body of the one email they get.
   */
  updateStatus: (id: number, status: 'open' | 'closed', opts: { reason?: 'completed' | 'not_planned'; note?: string } = {}) =>
    api.patch(`/feedback/${id}/status`, {
      status,
      // An absent reason is dropped by JSON itself; a blank note is dropped here, so closing with an empty box stores no empty reply.
      reason: opts.reason,
      ...(opts.note?.trim() ? { note: opts.note.trim() } : {}),
    }).then((r) => r.data as FeedbackItem),
  /** Say something to the people following an item (Admin / Feedback Admin). */
  reply: (id: number, body: string) =>
    api.post(`/feedback/${id}/replies`, { body }).then((r) => r.data as FeedbackReplyResult),
  /** What everyone may read: the public items, open first, with replies and who wants each one. */
  board: () => api.get('/feedback/board').then((r) => r.data as BoardItem[]),
  /** "Me too": I follow an open public item, so I am told when it is answered. Doing it twice changes nothing. */
  follow: (id: number) => api.post(`/feedback/${id}/follow`).then((r) => r.data as FollowState),
  /** Undo "me too". The person who sent an item cannot undo following it. */
  unfollow: (id: number) => api.delete(`/feedback/${id}/follow`).then((r) => r.data as FollowState),
  /** The items I follow, with replies and what I have not read. */
  mine: () => api.get('/feedback/mine').then((r) => r.data as MyFeedbackItem[]),
  /** I have opened this item: everything on it so far is read. */
  seen: (id: number) => api.post(`/feedback/${id}/seen`).then((r) => r.data as { ok: true }),
  /**
   * Put items on the board ("public") or take them off ("hidden"), many at a time (the API takes up to 100). Answers for EVERY id.
   * `removeFollowers` only goes with "hidden", and is sent only when it is true: it deletes every follow but the sender's on the items
   * this request hides, and cannot be undone.
   */
  setVisibility: (ids: number[], visibility: 'public' | 'hidden', opts: { removeFollowers?: boolean } = {}) =>
    api.post('/feedback/visibility', {
      ids,
      visibility,
      ...(opts.removeFollowers === true ? { removeFollowers: true } : {}),
    }).then((r) => r.data as VisibilityResult[]),
  /**
   * Stop telling the people who follow an item that is ALREADY HIDDEN: everyone but the person who sent it is removed, they are not told, and
   * it cannot be undone (showing the item again does not put them back). An item that is not hidden is refused (409).
   */
  stopTellingFollowers: (id: number) => api.delete(`/feedback/${id}/followers`).then((r) => r.data as StoppedFollowers),
  /**
   * Merge an item into another request an admin found it duplicates (Admin / Feedback Admin): the item closes as a duplicate and leaves the board, its
   * followers are added to the other request, and its sender is emailed once. ⚠️ THERE IS NO UNDO. Every refusal is a 4xx in words for the admin
   * (private, closed as done, the other request not on the board or not open, a loop), made before anything is written.
   */
  merge: (id: number, intoId: number) => api.post(`/feedback/${id}/merge`, { intoId }).then((r) => r.data as MergeResult),
  /** Resend one item's status to its GitHub issue. */
  pushStatus: (id: number) =>
    api.post(`/feedback/${id}/push-status`).then((r) => r.data as FeedbackItem),
  /** Bring every item and its GitHub issue back into step now. */
  sync: () => api.post('/feedback/sync').then((r) => r.data as FeedbackSyncSummary),
  downloadAttachment: (id: number) =>
    api.get(`/feedback/attachments/${id}`, { responseType: 'blob' }).then((r) => r.data as Blob),
};

// ─── PPR (Post Project Review) ─────────────────────────────────────────────────

export const PPR_POSITIONS = [
  'Field Leader',
  'Estimator',
  'Project Manager',
  'Safety Manager',
  'Survey Manager',
  'Scheduler',
  'Operations',
  'Payroll',
  'Business Development/Sales',
] as const;
export type PprPosition = (typeof PPR_POSITIONS)[number];

export type PprReviewType = 'Financial Only' | 'Full Review';
export type PprStatus =
  | 'Initiating'
  | 'Survey Collection'
  | 'Awaiting Financials'
  // Accounting move a job here when the numbers are settled — the coordinator's
  // signal to start the report. Mirrors PPR_STATUSES in the API's
  // update-ppr-job.dto.ts; keep the two in step.
  | 'Financials Completed by Accounting'
  | 'Report Prep'
  | 'Complete'
  // Terminal, and NOT a pipeline stage — a review started by mistake (#340).
  // Reachable only through cancelJob(); the API refuses it on setStatus/updateJob
  // so the reason and the closeout-alert reset can't be skipped.
  | 'Cancelled';

/** The one stage change a ppr.view-only user (Accounting) may make. The API
 *  enforces it; the UI uses this to offer only that option. */
export const PPR_ACCOUNTING_TRANSITION = {
  from: 'Awaiting Financials' as PprStatus,
  to: 'Financials Completed by Accounting' as PprStatus,
};

export interface PprContact {
  id: number;
  pprJobId: number;
  position: PprPosition;
  contactName: string | null;
  contactEmail: string | null;
  userId: string | null;
  notificationSent: boolean;
  notificationDate: string | null;
  responseReceived: boolean;
  responseDate: string | null;
  reminderCount: number;
  lastReminderDate: string | null;
  escalated: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PprJob {
  id: number;
  projectId: number | null;
  jobNumber: string;
  jobName: string;
  jobValue: number;
  reviewType: PprReviewType;
  status: PprStatus;
  closeDate: string | null;
  presidentConsulted: boolean;
  presidentNotes: string | null;
  sharepointFolderUrl: string | null;
  financialsComplete: boolean;
  financialsCompleteDate: string | null;
  /** Field leader's feedback on their crew — a SEPARATE deliverable from the
   *  Field Leader contact's survey response (which is "Field Team Feedback
   *  Needed"). Tracked per review. */
  /** Cancellation state, written ONLY by cancel()/reopen(). Not PATCH-able —
   *  the reason used to live in `notes`, where anyone could edit it away. */
  cancelledReason?: string | null;
  cancelledAt?: string | null;
  cancelledById?: string | null;
  reopenedAt?: string | null;
  reopenedById?: string | null;
  teamFeedbackComplete: boolean;
  teamFeedbackDate: string | null;
  teamFeedbackNotes: string | null;
  /** The LIVE project EFFECTIVE completion — the later of the project's
   *  estimated completion and its latest mobilization end (#414) — not the
   *  closeDate copy taken at PPR start. Present on getJob only (the list
   *  projection doesn't join projects). Used by the cancel dialog: the closeout
   *  alert can only re-fire while this date is still ahead of us, and that
   *  window is measured on the effective date, not the stored column. */
  projectEffectiveCompletion?: string | null;
  /** Why the linked project can never produce a closeout alert, or null when
   *  nothing blocks it. Mirrors the project filters in getCloseoutDue — see
   *  CLOSEOUT_BLOCK_SUBQUERY in the API. getJob only; null when unlinked. */
  projectCloseoutBlock?: 'deleted' | 'archived' | 'sub-project' | 'other-review' | null;
  // Financial fields (nullable until coordinator fills them in)
  contractValue: number | null;
  originalBid: number | null;
  totalCosts: number | null;
  totalCostsWoDirects: number | null;
  originalBidMargin: number | null;
  finalMargin: number | null;
  superMargin: number | null;
  originalBidMarginPct: number | null;
  finalMarginPct: number | null;
  superMarginPct: number | null;
  equipmentBid: number | null;
  equipmentCosts: number | null;
  materialBid: number | null;
  actualMaterialExpense: number | null;
  subBid: number | null;
  subCosts: number | null;
  totalBidLaborHours: number | null;
  actualJobHours: number | null;
  straightTimeHours: number | null;
  otHours: number | null;
  perDiem: number | null;
  broaderMeetingRequired: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  createdById: string | null;
  updatedById: string | null;
  // Aggregates
  contactsTotal: number | null;
  contactsNotified: number | null;
  contactsResponded: number | null;
  contactsOverdue: number | null;
  contactsEscalated: number | null;
  // Only on findOne
  contacts?: PprContact[];
}

export interface PprPrefill {
  projectId: number;
  jobNumber: string;
  jobName: string;
  closeDate: string | null;
  sharepointFolderUrl: string | null;
  contacts: Array<{
    position: PprPosition;
    contactName: string | null;
    contactEmail: string | null;
    userId: string | null;
  }>;
}

export interface CreatePprJobInput {
  projectId?: number;
  jobNumber: string;
  jobName: string;
  jobValue: number;
  reviewType: PprReviewType;
  closeDate?: string;
  presidentConsulted?: boolean;
  presidentNotes?: string;
  sharepointFolderUrl?: string;
  contacts: Array<{
    position: PprPosition;
    contactName?: string;
    contactEmail?: string;
    userId?: string;
  }>;
}

export interface PprGoodCatch {
  jobNumber: string;
  total: number;
  people: Array<{ firstName: string | null; lastName: string | null; count: number }>;
  available: boolean;
}

export const pprApi = {
  listJobs: (filters?: {
    search?: string; status?: PprStatus; reviewType?: PprReviewType;
    includeCompleted?: boolean; includeCancelled?: boolean;
  }) =>
    api.get('/ppr/jobs', { params: filters }).then((r) => r.data as PprJob[]),
  getJob: (id: number) =>
    api.get(`/ppr/jobs/${id}`).then((r) => r.data as PprJob),
  createJob: (data: CreatePprJobInput) =>
    api.post('/ppr/jobs', data).then((r) => r.data as PprJob),
  updateJob: (id: number, data: Partial<Omit<PprJob, 'id' | 'createdAt' | 'updatedAt' | 'contacts'>>) =>
    api.patch(`/ppr/jobs/${id}`, data).then((r) => r.data as PprJob),
  markFinancialsComplete: (id: number) =>
    api.post(`/ppr/jobs/${id}/financials-complete`).then((r) => r.data as PprJob),
  setStatus: (id: number, status: PprStatus) =>
    api.post(`/ppr/jobs/${id}/status`, { status }).then((r) => r.data as PprJob),

  /** Cancel a mis-started review. `closeoutAlertRearmed` reports whether the
   *  project can actually be flagged for closeout again — false once its
   *  estimated completion has passed, since that scan only looks forward. */
  cancelJob: (id: number, reason: string) =>
    api.post(`/ppr/jobs/${id}/cancel`, { reason })
      .then((r) => r.data as PprJob & { closeoutAlertRearmed: boolean }),
  reopenJob: (id: number) =>
    api.post(`/ppr/jobs/${id}/reopen`).then((r) => r.data as PprJob),
  /** Only permitted while Initiating with no contact notified — the API returns
   *  400 with an explanation otherwise, pointing at cancel instead. */
  deleteJob: (id: number) =>
    api.delete(`/ppr/jobs/${id}`).then((r) => r.data as { ok: boolean; deletedId: number }),

  downloadReport: (id: number) =>
    api.get(`/ppr/jobs/${id}/report.docx`, { responseType: 'blob' }).then((r) => r.data as Blob),
  goodCatchByJob: (jobNumber: string) =>
    api.get('/ppr/good-catch', { params: { jobNumber } }).then((r) => r.data as PprGoodCatch),

  searchProjects: (q: string) =>
    api.get('/ppr/project-search', { params: { q } }).then((r) => r.data as Array<{ id: number; jobNumber: string; title: string }>),
  getProjectPrefill: (projectId: number) =>
    api.get(`/ppr/prefill/${projectId}`).then((r) => r.data as PprPrefill),

  updateContact: (jobId: number, contactId: number, data: Partial<PprContact>) =>
    api.patch(`/ppr/jobs/${jobId}/contacts/${contactId}`, data).then((r) => r.data as PprContact),
  markNotified: (jobId: number, contactId: number) =>
    api.post(`/ppr/jobs/${jobId}/contacts/${contactId}/notified`).then((r) => r.data as PprContact),
  markResponded: (jobId: number, contactId: number) =>
    api.post(`/ppr/jobs/${jobId}/contacts/${contactId}/responded`).then((r) => r.data as PprContact),
};

export const surveyApi = {
  listRequests: (filters?: Record<string, string>) =>
    api.get('/survey/requests', { params: filters }).then((r) => r.data),
  getRequest: (id: number) => api.get(`/survey/requests/${id}`).then((r) => r.data),
  createRequest: (data: unknown) => api.post('/survey/requests', data).then((r) => r.data),
  updateRequest: (id: number, data: unknown) =>
    api.patch(`/survey/requests/${id}`, data).then((r) => r.data),

  listLogs: (filters?: Record<string, string>) =>
    api.get('/survey/logs', { params: filters }).then((r) => r.data),
  getLog: (id: number) => api.get(`/survey/logs/${id}`).then((r) => r.data),
  createLog: (data: unknown) => api.post('/survey/logs', data).then((r) => r.data),
  updateLog: (id: number, data: unknown) =>
    api.patch(`/survey/logs/${id}`, data).then((r) => r.data),
  deleteLog: (id: number) => api.delete(`/survey/logs/${id}`).then((r) => r.data),

  listTaskTypes: () =>
    api.get('/survey/task-types').then((r) => r.data as { id: number; code: string; label: string }[]),
};

// ─── Admin ─────────────────────────────────────────────────────────────────────

export interface AdminUser {
  id: string;
  displayName: string | null;
  email: string | null;
  roles: string[];
  divisions: number[];
  /**
   * Set when the user has been removed: sign-in is blocked and they are gone
   * from every person-picker, but their name still stands on everything they
   * ever touched. Null for an active user. Only `GET /admin/users` returns
   * removed people at all — this is the screen you restore them from.
   */
  deletedAt: string | null;
}

/** One live job a user is still attached to, and in what capacity. */
export interface AdminUserAssignment {
  id: number;
  jobNumber: string;
  title: string;
  roles: string[];
}
export interface AdminUserAssignments {
  userId: string;
  displayName: string | null;
  removed: boolean;
  projects: AdminUserAssignment[];
}

export interface AdminRole {
  id: number;
  label: string;
  isBuiltIn: boolean;
  permissions: string[];
}

export interface AdminPermission {
  id: number;
  code: string;
  label: string;
}

export interface AdminDivision {
  id: number;
  label: string;
}

// ─── Lookups (read-only, available to any authenticated user) ────────────────

export interface LookupOption {
  id: number;
  label: string;
}

export interface ReceivingStatusOption extends LookupOption {
  phase: 'Active' | 'Closed';
  sortOrder: number;
}

/** An equipment status. `code` is stable and safe to branch on; `label` is
 *  display text that changes (On Rent → On-site, Returned → Complete). */
export interface EquipmentStatusOption extends LookupOption {
  code: string;
  phase: 'Active' | 'Closed';
  sortOrder: number;
  /** False for retired statuses: renderable on old requests, not offerable. */
  isSelectable: boolean;
}

export const lookupsApi = {
  listDivisions: () =>
    api.get('/lookups/divisions').then((r) => r.data as LookupOption[]),
  /** Mobilization statuses. Read from the API, not a literal, so a value added
   *  in the database (MOBILIZED, 2026-07-28) is immediately selectable. */
  listMobilizationStatuses: () =>
    api.get('/lookups/mobilization-statuses').then((r) => r.data as LookupOption[]),
  listReceivingStatuses: () =>
    api.get('/lookups/receiving-statuses').then((r) => r.data as ReceivingStatusOption[]),
  /** Equipment statuses in lifecycle order. `code` is the stable identifier the
   *  UI gates behaviour on; `label` is display-only and gets reworded (#306).
   *  Retired statuses come back with `isSelectable: false` — still renderable
   *  on an old request, not offered when setting one. */
  listEquipmentStatuses: () =>
    api.get('/lookups/equipment-statuses').then((r) => r.data as EquipmentStatusOption[]),
  listReceivingDeliveryMethods: () =>
    api.get('/lookups/receiving-delivery-methods').then((r) => r.data as LookupOption[]),
  /** Shipping request statuses in order. Branch on `code`; `label` is display text. */
  listShippingStatuses: () =>
    api.get('/lookups/shipping-statuses').then((r) => r.data as ShippingStatusOption[]),
  listShippingTruckTypes: () =>
    api.get('/lookups/shipping-truck-types').then((r) => r.data as ShippingTruckTypeOption[]),
  // Material cost codes; free-entered codes join the list on first use.
  listCostCodes: () =>
    api.get('/lookups/cost-codes').then((r) => r.data as { code: string; name: string | null }[]),
};

export const adminApi = {
  // Users
  listUsers: () =>
    api.get('/admin/users').then((r) => r.data as AdminUser[]),
  listRoles: () =>
    api.get('/admin/roles').then((r) => r.data as AdminRole[]),
  createUser: (data: { entraObjectId: string; displayName: string; email?: string }) =>
    api.post('/admin/users', data).then((r) => r.data as AdminUser),
  setUserRoles: (userId: string, roles: string[]) =>
    api.put(`/admin/users/${userId}/roles`, { roles }).then((r) => r.data),
  setUserDivisions: (userId: string, divisions: number[]) =>
    api.put(`/admin/users/${userId}/divisions`, { divisions }).then((r) => r.data),
  /** Live jobs still pointing at this user — shown in the Remove confirmation. */
  userAssignments: (userId: string) =>
    api.get(`/admin/users/${userId}/assignments`).then((r) => r.data as AdminUserAssignments),
  /** Soft removal: blocks sign-in, keeps history. Reversible via restoreUser. */
  removeUser: (userId: string) =>
    api.delete(`/admin/users/${userId}`),
  restoreUser: (userId: string) =>
    api.post(`/admin/users/${userId}/restore`),

  // Roles & permissions
  listPermissions: () =>
    api.get('/admin/permissions').then((r) => r.data as AdminPermission[]),
  createRole: (data: { label: string; permissions: string[] }) =>
    api.post('/admin/roles', data).then((r) => r.data as AdminRole),
  updateRole: (id: number, data: { label?: string; permissions?: string[] }) =>
    api.patch(`/admin/roles/${id}`, data).then((r) => r.data),
  deleteRole: (id: number) =>
    api.delete(`/admin/roles/${id}`),

  // Divisions
  listDivisions: () =>
    api.get('/admin/divisions').then((r) => r.data as AdminDivision[]),
  createDivision: (label: string) =>
    api.post('/admin/divisions', { label }).then((r) => r.data as AdminDivision),
  updateDivision: (id: number, label: string) =>
    api.patch(`/admin/divisions/${id}`, { label }).then((r) => r.data as AdminDivision),
  deleteDivision: (id: number) =>
    api.delete(`/admin/divisions/${id}`),

  // Owned equipment catalog (dropdown source for owned-equipment assignment)
  listOwnedEquipment: () =>
    api.get('/admin/owned-equipment').then((r) => r.data as AdminOwnedEquipment[]),
  createOwnedEquipment: (data: { unitNumber: string; description?: string; equipmentClass?: string }) =>
    api.post('/admin/owned-equipment', data).then((r) => r.data as AdminOwnedEquipment),
  updateOwnedEquipment: (id: number, data: { unitNumber?: string; description?: string; equipmentClass?: string; isActive?: boolean }) =>
    api.patch(`/admin/owned-equipment/${id}`, data).then((r) => r.data as AdminOwnedEquipment),
  deleteOwnedEquipment: (id: number) =>
    api.delete(`/admin/owned-equipment/${id}`),

  // Job roll-ups (#613): which jobs roll up to which. Jobs are named by job number.
  listRollUps: () =>
    api.get('/admin/roll-ups').then((r) => r.data as AdminRollUps),
  /** All or nothing: a refused job number rejects the whole call, and the message names each one. */
  linkRollUp: (parentJobNumber: string, childJobNumbers: string[]) =>
    api.post('/admin/roll-ups', { parentJobNumber, childJobNumbers }).then((r) => r.data as AdminRollUpLinked),
  unlinkRollUp: (childProjectId: number) =>
    api.delete(`/admin/roll-ups/${childProjectId}`).then((r) => r.data as AdminRollUpUnlinked),
};

/** One job in a roll-up family, as the Admin screen lists it. */
export interface AdminRollUpJob { projectId: number; jobNumber: string; title: string }
export interface AdminRollUpFamily {
  parent: AdminRollUpJob & { deleted: boolean };
  /** The parent itself rolls up to another job: a chain, which the Financials roll-up cannot read. */
  parentIsAlsoChild: boolean;
  children: Array<AdminRollUpJob & { deleted: boolean; linkedAt: string }>;
}
export interface AdminRollUps {
  /** False before the roll-up migration has been applied: there is nothing to list or change. */
  available: boolean;
  families: AdminRollUpFamily[];
  history: Array<{ action: string; detail: string | null; at: string; by: string | null }>;
}
export interface AdminRollUpLinked { parent: AdminRollUpJob; linked: AdminRollUpJob[]; alreadyLinked: AdminRollUpJob[] }
export interface AdminRollUpUnlinked { projectId: number; jobNumber: string; parentJobNumber: string }

export interface AdminOwnedEquipment {
  id: number;
  unitNumber: string;
  description: string | null;
  equipmentClass: string | null;
  isActive: boolean;
}

// ─── Training guide progress (Phase 3) ──────────────────────────────────────
/**
 * One person's state on one topic. A row exists if they have ticked it, written
 * a note, or both — an empty one is deleted server-side.
 *
 * `guideRevision` is the guide revision at the moment of ticking. Compare it to
 * `TRAINING.revision` to tell "read the current version" from "read an older
 * one": the content genuinely changes, and a tick that silently claimed
 * otherwise would be worse than no record.
 */
export interface TrainingProgress {
  topicId: string;
  completedAt: string | null;
  guideRevision: string | null;
  note: string | null;
}
/**
 * One person's training state, for the admin roll-up.
 *
 * ⚠️ Carries their ROLES AND PERMISSIONS deliberately. The guide is
 * access-filtered, so the denominator is different for every person, and only
 * the client knows the content — it has to evaluate each user's access itself.
 * `GET /users` returns id/name/email only, which made everyone look
 * unprivileged (found in review on #283).
 *
 * `topics` includes note-only rows — someone can annotate a topic without
 * ticking it, and the note editor promises a manager can see it.
 */
export interface TrainingProgressForUser {
  userId: string;
  displayName: string | null;
  email: string | null;
  roles: string[];
  permissions: string[];
  canViewAll: boolean;
  topics: { topicId: string; completedAt: string | null; guideRevision: string | null; note: string | null }[];
  lastActivity: string | null;
}
export const trainingApi = {
  myProgress: () => api.get('/training/progress').then((r) => r.data as TrainingProgress[]),
  /** Always writes to the caller's own row — the server takes the user from the token. */
  setProgress: (topicId: string, dto: { completed?: boolean; note?: string | null; guideRevision?: string | null }) =>
    api.post(`/training/progress/${encodeURIComponent(topicId)}`, dto).then((r) => r.data as TrainingProgress[]),
  /** Admin only. */
  everyone: () => api.get('/training/progress/all').then((r) => r.data as TrainingProgressForUser[]),
};

// ─── Today ─────────────────────────────────────────────────────────────────────

/**
 * ⚠️ `notices` and `clocks` are OPTIONAL, and that is the contract,
 * not laziness. Three of the page's card groups have no source in the database
 * yet (see `apps/api/src/today/today.service.ts` for what was measured), so the
 * API omits them rather than sending `[]`. The page must be able to tell
 * "nothing to do today" from "this was never built", and an empty array says
 * the first when it means the second.
 */
export interface TodayApiPayload {
  viewerName: string;
  activeCount: number;
  weeks: {
    weekStart: string;
    projects: {
      projectId: number;
      name: string;
      job: string;
      mobilizationDate: string;
      ready: number;
      /** This project's own denominator. Never assume 14 or 15. */
      scoredTotal: number;
      crit: number;
    }[];
  }[];
  /**
   * Derived absences, not records — a job with no crew booked, or one whose
   * critical readiness criteria are still unset.
   *
   * ⚠️ The design's own definition of this lane ("assigned to me" from RFIs and
   * submittals) could not be built: production holds one RFI and two submittals,
   * and neither table names a person. See `apps/api/src/today/today-items.ts`.
   */
  items: {
    id: string;
    type: string;
    title: string;
    project: string;
    job: string;
    projectId: number | null;
    dueDate: string | null;
    dueLabel: string;
    blocks: string;
    staleAge: number;
    isHardDeadline: boolean;
    affectedProjects: number;
    /** Quietest lane this row may sit in — a floor, not the lane itself. */
    minLane?: 'today' | 'week';
  }[];
  stales: { id: string; projectId: number; name: string; ageDays: number }[];
  /**
   * The stale window the API filtered with (an `mp_config` key). The page uses
   * THIS rather than a constant of its own: the filtering happens in SQL, so a
   * smaller number on the web could never widen the list and would be a setting
   * that silently does nothing.
   */
  staleThresholdDays: number;
  activity: {
    id: string;
    who: string;
    what: string;
    project: string;
    projectId: number | null;
    at: string;
  }[];
  syncedAt: string;
}

export interface LeadershipRow {
  id: string;
  projectId: number;
  /** ⚠️ NOT always five digits — `101TMI` and `5070` are live Construction
   *  jobs. Render what is stored; never pad, format or validate to five. */
  job: string;
  title: string;
  pm: string | null;
  health: 'off-track' | 'at-risk';
  rule: string;
  reason: string;
  daysLate: number | null;
  anchorDate: string | null;
}

export interface LeadershipUnassigned {
  id: string;
  projectId: number;
  job: string;
  title: string;
  status: string | null;
  startDate: string | null;
}

export interface LeadershipReadiness {
  id: string;
  projectId: number;
  job: string;
  title: string;
  pm: string | null;
  startDate: string | null;
  criticalUnset: number;
  /** ⚠️ Per job, never a constant — always rendered as the denominator. */
  criticalTotal: number;
  gateDate: string | null;
  /** ⚠️ True when that cutoff is already in the past — a different fact from a
   *  deadline coming up, and decided server-side because it needs Eastern. */
  gatePassed: boolean;
}

export interface LeadershipCrew {
  id: string;
  projectId: number;
  job: string;
  title: string;
  pm: string | null;
  startDate: string | null;
}

export interface LeadershipQuiet {
  id: string;
  projectId: number;
  job: string;
  title: string;
  pm: string | null;
  /** ⚠️ null means nothing has EVER been logged — a state, never an age. */
  quietDays: number | null;
  lastActivityAt: string | null;
}

export interface LeadershipWonBid {
  id: string;
  projectId: number;
  job: string;
  title: string;
  /** The estimator, not the PM — a job awaiting setup usually has no PM yet. */
  estimator: string | null;
  status: string | null;
  /** ⚠️ A full ISO TIMESTAMP, not a calendar date — render with
   *  `fmtInstantDate`. `bid_closed_won_at` is a datetime2. */
  wonAt: string | null;
}

export interface LeadershipApiPayload {
  viewerName: string;
  scopeLabel: string;
  totalLiveProjects: number;
  needsAttention: LeadershipRow[];
  longStanding: LeadershipRow[];
  unassigned: LeadershipUnassigned[];
  readinessGaps: LeadershipReadiness[];
  noCrew: LeadershipCrew[];
  quiet: LeadershipQuiet[];
  wonBids: LeadershipWonBid[];
  agingThresholdDays: number;
  readinessLeadDays: number;
  crewLeadDays: number;
  quietThresholdDays: number;
  syncedAt: string;
}

export const todayApi = {
  get: () => api.get('/today').then((r) => r.data as TodayApiPayload),
  leadership: () => api.get('/today/leadership').then((r) => r.data as LeadershipApiPayload),
};

// ─── Travel payroll report (#557 follow-up) ─────────────────────────────────
// Every Travel Acknowledgment SIGNED in a date range, with the rates exactly as
// signed. Rates, never amounts: per-diem days are payroll's call. Open to
// manpower.edit and travel.payroll_report (the HR role).
export interface TravelPayrollEntry {
  acknowledgmentId: number;
  signedDate: string;
  signedName: string | null;
  signedLanguage: string | null;
  issuedBy: string | null;
  person: { name: string | null; employeeId: string | null; jobTitle: string | null; regionalOffice: string | null };
  job: {
    jobNumber: string | null;
    title: string | null;
    parentJobNumber: string | null;
    siteAddress: string | null;
    mobilizationDate: string | null;
    travelBand: string | null;
    isPrevailingWage: boolean;
  };
  lines: { label: string; rate: number; unit: 'once' | 'per-day' | 'per-hour' }[];
  caveats: string[];
  /** A later signed acknowledgment for the same person on the same job. */
  supersededOn: string | null;
}

export interface TravelPayrollEmailRun {
  id: number;
  week_from: string;
  week_to: string;
  trigger_kind: 'scheduled' | 'manual';
  status: 'sending' | 'sent' | 'failed';
  /** Still 'sending' half an hour on: its outcome was never recorded either way. */
  unconfirmed: boolean;
  acknowledgments: number | null;
  error: string | null;
  started_at: string;
  finished_at: string | null;
  run_by_name: string | null;
}

export interface TravelPayrollEmailStatus {
  enabled: boolean;
  from: string | null;
  to: string[];
  recent: TravelPayrollEmailRun[];
}

export const travelPayrollApi = {
  entries: (from: string, to: string) =>
    api.get('/manpower/travel-payroll-report', { params: { from, to } }).then((r) => r.data as TravelPayrollEntry[]),
  workbook: (from: string, to: string) =>
    api.get('/manpower/travel-payroll-report/workbook', { params: { from, to }, responseType: 'blob' })
      .then((r) => r.data as Blob),
  emailStatus: () =>
    api.get('/manpower/travel-payroll-report/email').then((r) => r.data as TravelPayrollEmailStatus),
  sendEmail: (from: string, to: string) =>
    api.post('/manpower/travel-payroll-report/email', { from, to })
      .then((r) => r.data as { status: 'sent' | 'skipped' | 'failed' | 'unconfirmed'; acknowledgments?: number }),
};
